import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import './App.css';
import './theme-a.css';
import './theme-b.css';
import './theme-c.css';
import { supabase } from './lib/supabase';
import { addReservierung, getReservierungen, updateReservierung, deleteReservierung, getTischBelegung, addAnfrage, getReservierungenByEmail } from './lib/db';
import { addBestellung, getBestellungen, updateBestellung } from './lib/db';
import { addMitglied, getMitglieder } from './lib/db';
import { getFaqs, saveFaq, deleteFaq } from './lib/db';
import { generateQrToken, redeemToken, getStempel } from './lib/db';
import { QRCodeSVG } from 'qrcode.react';
import { Html5Qrcode } from 'html5-qrcode';

function CupSVG({ className }) {
  return (
    <svg className={className} viewBox="0 0 100 92" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M30 18 C28 13 33 9 31 5" stroke="white" strokeWidth="2" strokeLinecap="round" fill="none" opacity="0.75"/>
      <path d="M41 16 C39 11 44 7 42 3" stroke="white" strokeWidth="2" strokeLinecap="round" fill="none" opacity="0.75"/>
      <path d="M52 18 C50 13 55 9 53 5" stroke="white" strokeWidth="2" strokeLinecap="round" fill="none" opacity="0.75"/>
      <path d="M16 25 Q50 20 84 25 L80 60 Q50 66 20 60 Z" fill="white" opacity="0.95"/>
      <path d="M80 33 Q95 33 95 46 Q95 59 80 57" stroke="white" strokeWidth="2.5" fill="none" strokeLinecap="round"/>
      <ellipse cx="50" cy="65" rx="44" ry="7" fill="white" opacity="0.92"/>
      <path d="M8 70 Q50 79 92 70 L92 72 Q50 81 8 72 Z" fill="white" opacity="0.7"/>
    </svg>
  );
}

function getBlockMin(uhrzeit) {
  if (!uhrzeit) return 120;
  const h = parseInt(uhrzeit.split(':')[0], 10);
  return h >= 17 ? 120 : 90;
}

// Sitzbereiche (seit 11.10.2026): Der Gast wählt den Bereich, die Grenze gilt je Bereich und je Zeitfenster
// (Summe aller Buchungen, die im selben Fenster ANFANGEN). Ersetzt den Sommer/Winter-Modus.
const BEREICHE = [
  { key: 'draussen', label: 'Draußen', max: 40, maxPersonen: 40, hinweis: 'beheizt, Decken vorhanden' },
  { key: 'mercato', label: 'Mercato', max: 25, maxPersonen: 25 },
  { key: 'kleines_savoca', label: 'Kleines Savoca', gesperrtBis: '2026-11-14' /* Ingo, 31.10.2026: wegen Umbau online erst ab 14.11. buchbar – gleiche Regel im DB-Trigger 'bereich_gesperrt' */, max: 5, maxPersonen: 5, ab: { datum: '2026-11-14', max: 15, maxPersonen: 15 } },
];
// Grenzen, die ab einem Datum steigen (Ingo, 28.10.2026: Kleines Savoca ab 14.11. 15 statt 5) – gleiche Regel im DB-Trigger
const bereichAm = (b, datum) => (b.ab && datum && datum >= b.ab.datum) ? { ...b, max: b.ab.max, maxPersonen: b.ab.maxPersonen } : b;
const bereicheAm = (datum) => BEREICHE.map(b => bereichAm(b, datum));
const bereichInfo = (key, datum) => { const b = BEREICHE.find(x => x.key === key); return b ? bereichAm(b, datum) : null; };
// Stichtag (Ingo, 11.10.2026): Die Bereichsregel gilt für Reservierungen mit Datum AB diesem Tag. Davor bleibt alles wie bisher
// (kein Bereich, Winter-Regel: max. 25 Personen pro Zeitfenster, Stand der Datenbank am 11.10.). Ohne Datum (Formular noch leer) gilt die neue Regel.
const BEREICHE_AB = '2026-10-12';
const MAX_PERSONEN_ALT = 25;
const bereichGilt = (datum) => !datum || datum >= BEREICHE_AB;

// Belegung (Ingo, 16.10.2026): Wer um 18:00 bucht, sitzt 2 Stunden (mittags 90 Min.) und blockiert den Platz bis dahin – auch im NÄCHSTEN Zeitfenster.
// Maßgeblich ist deshalb die höchste Zahl GLEICHZEITIG sitzender Gäste im Bereich; sie darf zu keinem Zeitpunkt die Grenze überschreiten.
// (Die Balken im Reservierungsbuch dürfen je Fenster mehr zeigen, weil dieselbe Buchung in zwei Fenstern zählt.)
const dauerVon = (r) => parseInt(r.dauer, 10) || getBlockMin(r.uhrzeit);
function spitzeImBereich(zeilen, bereichKey, vonMin, bisMin, ausschlussId = null) {
  const rs = (zeilen || [])
    .filter(r => r.uhrzeit && r.id !== ausschlussId && r.sitzbereich === bereichKey)
    .map(r => ({ s: minutenVon(r.uhrzeit), e: minutenVon(r.uhrzeit) + dauerVon(r), p: parseInt(r.personen, 10) || 0 }));
  // Die Belegung steigt nur, wenn jemand ankommt – es genügt, an Beginn und an jeder Ankunft im Zeitraum zu zählen
  const punkte = [vonMin, ...rs.map(r => r.s).filter(t => t > vonMin && t < bisMin)];
  return Math.max(0, ...punkte.map(t => rs.filter(r => r.s <= t && t < r.e).reduce((a, r) => a + r.p, 0)));
}
// Belegung je Bereich für eine neue Buchung ab `uhrzeit`: { draussen: 12, mercato: 0, ... }
function belegungJeBereich(zeilen, uhrzeit, ausschlussId = null) {
  const von = minutenVon(uhrzeit), bis = von + getBlockMin(uhrzeit);
  return Object.fromEntries(BEREICHE.map(b => [b.key, spitzeImBereich(zeilen, b.key, von, bis, ausschlussId)]));
}

async function pruefeKapazitaet(datum, uhrzeit, neuePersonen, bereich, ausschlussId = null) {
  // Sicht „belegung“: nur Uhrzeit/Personen/Bereich, keine Namen – damit die Kapazitätsprüfung ohne Login geht (RLS)
  const { data: tagRes } = await supabase.from('belegung').select('id,uhrzeit,dauer,personen,sitzbereich').eq('datum', datum);
  if (!bereichGilt(datum)) {
    // Alte Regel für Tage vor dem Stichtag (Winter-Modus): max. 25 Personen pro Zeitfenster, Summe der Buchungen, die im Fenster ANFANGEN
    const fensterAlt = findeZeitfenster(uhrzeit);
    const bestehendAlt = (tagRes || []).reduce((summe, r) => {
      if (!r.uhrzeit || r.id === ausschlussId) return summe;
      const f = findeZeitfenster(r.uhrzeit);
      return (fensterAlt && f && f.start === fensterAlt.start) ? summe + (parseInt(r.personen, 10) || 0) : summe;
    }, 0);
    const neueSummeAlt = bestehendAlt + (parseInt(neuePersonen, 10) || 0);
    return { erlaubt: neueSummeAlt <= MAX_PERSONEN_ALT, alt: true, max: MAX_PERSONEN_ALT, fenster: fensterAlt };
  }
  const fenster = findeZeitfenster(uhrzeit);
  const belegt = belegungJeBereich(tagRes, uhrzeit, ausschlussId);
  const personen = parseInt(neuePersonen, 10) || 0;
  const info = bereichInfo(bereich, datum);
  if (!info) return { erlaubt: false, kein_bereich: true, fenster };
  const bestehend = belegt[info.key];
  const frei = Object.fromEntries(bereicheAm(datum).map(b => [b.key, Math.max(b.max - belegt[b.key], 0)]));
  const alternativen = bereicheAm(datum).filter(b => b.key !== info.key && frei[b.key] >= personen);
  return { erlaubt: bestehend + personen <= info.max, bereich: info, bestehend, neueSumme: bestehend + personen, max: info.max, fenster, frei, alternativen, uhrzeit, dauerMin: getBlockMin(uhrzeit) };
}
function kapazitaetsMeldung(kap) {
  if (kap.alt) return `Für das Zeitfenster ${kap.fenster.start}–${kap.fenster.ende} Uhr sind wir leider ausgebucht (max. ${kap.max} Personen). Bitte wählen Sie eine andere Uhrzeit.`;
  if (kap.kein_bereich) return 'Bitte wählen Sie aus, wo Sie sitzen möchten.';
  const dauer = kap.dauerMin === 120 ? '2 Stunden' : '1,5 Stunden';
  const fenster = kap.uhrzeit ? ` ab ${kap.uhrzeit} Uhr (${dauer} Sitzzeit)` : '';
  let text = `${kap.bereich.label} ist${fenster} leider ausgebucht.`;
  if (kap.alternativen.length) text += ` Noch frei: ${kap.alternativen.map(b => `${b.label} (${kap.frei[b.key]} Plätze)`).join(', ')}.`;
  return text + ' Oder wählen Sie eine andere Uhrzeit.';
}

function parseTische(tisch_id) {
  return (tisch_id || '').split(',').map(t => t.trim()).filter(Boolean);
}

// Zeitfenster für die Buchungsstopp-Sperren — Länge entspricht der Tisch-Blockierungsdauer
// (90 Min. vormittags, 120 Min. ab 15 Uhr)
const ZEITFENSTER = [
  { start: '09:00', ende: '10:30' },
  { start: '10:30', ende: '12:00' },
  { start: '12:00', ende: '13:30' },
  { start: '13:30', ende: '15:00' },
  { start: '15:00', ende: '17:00' },
  { start: '17:00', ende: '19:00' },
  { start: '19:00', ende: '21:00' },
  { start: '21:00', ende: '23:00' },
];

function minutenVon(uhrzeit) {
  const [h, m] = uhrzeit.split(':').map(Number);
  return h * 60 + m;
}

function findeZeitfenster(uhrzeit) {
  if (!uhrzeit) return null;
  const min = minutenVon(uhrzeit);
  return ZEITFENSTER.find(f => min >= minutenVon(f.start) && min < minutenVon(f.ende)) || ZEITFENSTER[ZEITFENSTER.length - 1];
}

// Auswahl des Sitzbereichs (Pflicht): drei Karten mit Platzzahl, bei Draußen der Heiz-Hinweis
function BereichWahl({ value, onChange, ohneTitel = false, datum }) {
  return (
    <div className="form-group bereich-wahl">
      {!ohneTitel && <label>Wo möchten Sie sitzen? *</label>}
      <div className="bereich-karten">
        {bereicheAm(datum).map(b => (
          <button type="button" key={b.key} className={`bereich-karte${value === b.key ? ' aktiv' : ''}`} onClick={() => onChange(b.key)} aria-pressed={value === b.key}>
            <span className="bereich-name">{b.label}</span>
            <span className="bereich-plaetze">{b.gesperrtBis && !(datum && datum >= b.gesperrtBis) ? `Online buchbar ab ${b.gesperrtBis.slice(8)}.${b.gesperrtBis.slice(5,7)}.` : `${b.max} Plätze`}</span>
            {b.hinweis && <span className="bereich-hinweis">{b.hinweis}</span>}
          </button>
        ))}
      </div>
    </div>
  );
}

function BereichSelect({ value, onChange, leer = '-- Bereich wählen --', datum }) {
  return (
    <select value={value || ''} onChange={e => onChange(e.target.value)}>
      <option value="">{leer}</option>
      {bereicheAm(datum).map(b => <option key={b.key} value={b.key}>{b.label} ({b.max})</option>)}
    </select>
  );
}

// ===== TIMER =====
function TimerWidget() {
  const TIMER_API = process.env.REACT_APP_TIMER_API || '';
  const [current, setCurrent] = useState(null);
  const [startedAt, setStartedAt] = useState(null);
  const [elapsed, setElapsed] = useState(0);

  const loadStatus = useCallback(async () => {
    if (!TIMER_API) return;
    try {
      const r = await fetch(`${TIMER_API}/api/status`);
      const d = await r.json();
      setCurrent(d.current);
      if (d.current && d.current.start_time) {
        setStartedAt(new Date(d.current.start_time));
      } else {
        setStartedAt(null);
        setElapsed(0);
      }
    } catch {}
  }, []);

  useEffect(() => {
    loadStatus();
    const interval = setInterval(loadStatus, 30000);
    return () => clearInterval(interval);
  }, [loadStatus]);

  useEffect(() => {
    if (!startedAt) return;
    const tick = setInterval(() => {
      const secs = Math.floor((Date.now() - startedAt.getTime()) / 1000);
      setElapsed(secs);
    }, 1000);
    return () => clearInterval(tick);
  }, [startedAt]);

  const fmt = (s) => {
    const h = Math.floor(s / 3600).toString().padStart(2, '0');
    const m = Math.floor((s % 3600) / 60).toString().padStart(2, '0');
    const sec = (s % 60).toString().padStart(2, '0');
    return `${h}:${m}:${sec}`;
  };

  const doStart = async () => {
    await fetch(`${TIMER_API}/api/start`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ projekt: 'Savoca App' }),
    });
    await loadStatus();
  };

  const doStop = async () => {
    try { await fetch(`${TIMER_API}/api/stop`, { method: 'POST' }); } catch {}
    setCurrent(null);
    setStartedAt(null);
    setElapsed(0);
    await loadStatus();
  };

  const isRunning = !!current && !current.paused_at;

  return (
    <div className="timer-widget">
      <span className="timer-label">Session</span>
      <span className="timer-time">{fmt(elapsed)}</span>
      {!isRunning ? (
        <button onClick={doStart}>START</button>
      ) : (
        <button onClick={doStop} style={{ background: '#e74c3c', color: '#fff' }}>STOP</button>
      )}
    </div>
  );
}

const BUCHUNGS_OPTIONEN = [
  { key: 'tisch',           icon: '🍽', titel: 'Tisch reservieren',  desc: 'Einen Tisch für deine Gruppe buchen' },
  { key: 'eisgeburtstag',   icon: '🍦', titel: 'Eisgeburtstag',       desc: 'Geburtstag im Eislabor' },
  { key: 'kindergeburtstag',icon: '🎂', titel: 'Kindergeburtstag',    desc: 'Kinderparty auf Anfrage' },
  { key: 'catering',        icon: '🍾', titel: 'Catering',            desc: 'Für Events außer Haus' },
  { key: 'event',           icon: '🎉', titel: 'Event anfragen',      desc: 'Private Veranstaltungen' },
];

// Herbstbrunch (einmalig, 15.11.2026): erscheint direkt hinter „Tisch reservieren", solange er noch bevorsteht
const BRUNCH_OPTION = { key: 'brunch', icon: '🥂', titel: 'Herbstbrunch 15.11.', desc: 'Einmalig – draußen Herbst, drinnen Italien' };
const buchungsOptionen = (termine = []) => [BUCHUNGS_OPTIONEN[0], ...(brunchAktiv() ? [BRUNCH_OPTION] : []), ...termine.map(terminOption), ...BUCHUNGS_OPTIONEN.slice(1)];

// ===== HOME =====
function Home({ setPage, user, onReservieren }) {
  const [adminClicks, setAdminClicks] = useState(0);
  const [modalOffen, setModalOffen] = useState(false);
  const [kaffeOffen, setKaffeOffen] = useState(false);
  const [kaffeSchritt, setKaffeSchritt] = useState('start');
  const reservierbareTermine = useReservierbareTermine();

  const handleMercatoClick = () => {
    const next = adminClicks + 1;
    if (next >= 2) { setAdminClicks(0); setPage('admin'); }
    else setAdminClicks(next);
  };

  const handleTaglineClick = () => {};

  const kaffeNode = KAFFEE_BAUM[kaffeSchritt] || KAFFEE_BAUM.start;

  return (
    <>
      {/* Buchungs-Auswahl Modal */}
      {modalOffen && (
        <div className="buchungs-overlay" onClick={() => setModalOffen(false)}>
          <div className="buchungs-modal" onClick={e => e.stopPropagation()}>
            <button className="buchungs-modal-close" onClick={() => setModalOffen(false)}>✕</button>
            <div className="buchungs-modal-titel">Was möchtest du buchen?</div>
            <div className="buchungs-optionen">
              {buchungsOptionen(reservierbareTermine).map(o => (
                <button key={o.key} className="buchungs-option-btn" onClick={() => { onReservieren(o.key); setModalOffen(false); }}>
                  <span className="buchungs-opt-icon">{o.icon}</span>
                  <span className="buchungs-opt-text">
                    <span className="buchungs-opt-titel">{o.titel}</span>
                    <span className="buchungs-opt-desc">{o.desc}</span>
                  </span>
                  <span className="buchungs-opt-arrow">›</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="hero-mercato">
        <div className="shimmer-swirl-1" />
        <div className="shimmer-swirl-2" />
        <img
          src="/savoca-logo.png"
          alt="Savoca Bar Caffè"
          className="hero-logo-img"
          onClick={handleMercatoClick}
          style={{ cursor: 'default', userSelect: 'none' }}
        />

        <div className="mercato-divider" />

        {user ? (
          <div className="mercato-buttons">
            <button className="btn-mercato" onClick={() => setModalOffen(true)}>Reservierung</button>
            <button className="btn-mercato-outline" onClick={() => setPage('mitglied')}>Mein Konto</button>
          </div>
        ) : (
          <div className="mercato-auth">
            <button className="btn-mercato" onClick={() => setModalOffen(true)}>Reservierung</button>
            <button className="btn-mercato-outline" onClick={() => setPage('mitglied')}>Einloggen</button>
            <button className="btn-mercato-outline" onClick={() => setPage('mitglied-register')}>Registrieren</button>
          </div>
        )}

        {kaffeOffen && (
          <div className="kaffee-home-bubble">
            <button className="kaffee-assist-close" onClick={() => setKaffeOffen(false)}>✕</button>
            <p className="kaffee-assist-text">{kaffeNode.text}</p>
            <div className="kaffee-assist-optionen">
              {kaffeNode.optionen.map((o, i) => (
                <button key={i} className="kaffee-assist-btn" onClick={() => {
                  if (o.aktion) { setPage(o.aktion); setKaffeOffen(false); }
                  else if (o.next) setKaffeSchritt(o.next);
                }}>{o.label}</button>
              ))}
            </div>
          </div>
        )}

        <button
          className="kaffee-home-btn"
          onClick={() => { setKaffeSchritt('start'); setKaffeOffen(v => !v); }}
        >
          ☕ Wie kann ich dir helfen?
        </button>
      </div>
    </>
  );
}

// ===== TISCH GRID =====
function TischGrid({ datum, uhrzeit, gewaehlter, onWaehlen }) {
  const [belegungen, setBelegungen] = useState([]);

  useEffect(() => {
    if (!datum) return;
    getTischBelegung(datum).then(setBelegungen);
  }, [datum, uhrzeit]);

  function istBelegt(tischId) {
    if (!uhrzeit) return false;
    const [rh, rm] = uhrzeit.split(':').map(Number);
    const reqMin = rh * 60 + rm;
    const reqBlock = getBlockMin(uhrzeit);
    return belegungen.some(b => {
      if (!parseTische(b.tisch_id).includes(tischId)) return false;
      const [bh, bm] = b.uhrzeit.split(':').map(Number);
      const bMin = bh * 60 + bm;
      const bBlock = b.dauer || getBlockMin(b.uhrzeit);
      return reqMin < bMin + bBlock && bMin < reqMin + reqBlock;
    });
  }

  function Tisch({ id, nr }) {
    const belegt = istBelegt(id);
    const aktiv = gewaehlter === id;
    return (
      <div
        className={`tisch-cell${belegt ? ' belegt' : aktiv ? ' aktiv' : ' frei'}`}
        onClick={() => !belegt && onWaehlen(aktiv ? null : id)}
        title={`Tisch ${nr}`}
      >
        {nr}
      </div>
    );
  }

  if (!datum || !uhrzeit) {
    return <div className="tisch-hint">Bitte zuerst Datum und Uhrzeit wählen.</div>;
  }

  return (
    <div className="tisch-grid-wrap">
      <div className="tisch-section-label">INNEN <span>10 Tische</span></div>
      <div className="tisch-grid tisch-grid-innen">
        {Array.from({ length: 10 }, (_, i) => <Tisch key={`I${i+1}`} id={`I${i+1}`} nr={i+1} />)}
      </div>
      <div className="tisch-section-label" style={{ marginTop: '1.5rem' }}>AUSSEN <span>10 Tische</span></div>
      <div className="tisch-grid tisch-grid-aussen">
        {Array.from({ length: 10 }, (_, i) => <Tisch key={`A${i+1}`} id={`A${i+1}`} nr={i+1} />)}
      </div>
      <div className="tisch-legende">
        <span className="leg-frei">● verfügbar</span>
        <span className="leg-belegt">● belegt</span>
        <span className="leg-aktiv">● ausgewählt</span>
      </div>
    </div>
  );
}

// ===== ANFRAGE ERFOLG =====
function AnfrageErfolg({ name, datum, uhrzeit, extra, onNeu }) {
  return (
    <div style={{ textAlign: 'center', padding: '2.5rem 1rem' }}>
      <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>✅</div>
      <div style={{ fontFamily: 'var(--font-serif)', fontSize: '1.6rem', color: 'var(--primary)', marginBottom: '0.75rem' }}>Anfrage übermittelt!</div>
      <div style={{ color: 'var(--gray)', fontSize: '0.95rem', marginBottom: '0.5rem' }}>Wir haben Ihre Anfrage erhalten und melden uns per E-Mail.</div>
      <div style={{ margin: '1.5rem auto', maxWidth: '320px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '10px', padding: '1.2rem 1.5rem', textAlign: 'left', fontSize: '0.9rem', lineHeight: '1.8' }}>
        <div><span style={{ color: 'var(--gray)', fontSize: '0.72rem', letterSpacing: '1.5px', textTransform: 'uppercase' }}>Name</span><br /><strong>{name}</strong></div>
        <div style={{ marginTop: '0.6rem' }}><span style={{ color: 'var(--gray)', fontSize: '0.72rem', letterSpacing: '1.5px', textTransform: 'uppercase' }}>Datum & Uhrzeit</span><br /><strong>{datum} · {uhrzeit} Uhr</strong></div>
        {extra && <div style={{ marginTop: '0.6rem' }}>{extra}</div>}
      </div>
      <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap', marginTop: '1.5rem' }}>
        <button className="btn-primary" onClick={onNeu}>+ Neue Anfrage</button>
        <button onClick={() => window.location.hash = ''} style={{ background: 'transparent', border: '2px solid var(--primary)', color: 'var(--primary)', padding: '0.85rem 2rem', borderRadius: '4px', fontSize: '0.7rem', letterSpacing: '3px', textTransform: 'uppercase', fontWeight: '600', cursor: 'pointer' }}>Zur Startseite</button>
      </div>
    </div>
  );
}

// ===== KINDERGEBURTSTAG FORM =====
function KindGeburtstagForm() {
  const [form, setForm] = useState({ name: '', email: '', telefon: '', kind_name: '', kind_alter: '', datum: '', uhrzeit: '', personen_kinder: '', personen_erwachsene: '', wuensche: '' });
  const [status, setStatus] = useState(null);
  const [letzteBuchung, setLetzteBuchung] = useState(null);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.telefon || !form.kind_name || !form.kind_alter || !form.datum || !form.uhrzeit || !form.personen_kinder || !form.personen_erwachsene) {
      setStatus({ type: 'error', msg: 'Bitte alle Pflichtfelder ausfüllen.' });
      return;
    }
    try {
      await addAnfrage({ typ: 'kindergeburtstag', name: form.name, email: form.email, telefon: form.telefon, datum: form.datum, uhrzeit: form.uhrzeit, notiz: form.wuensche, status: 'offen', details: JSON.stringify({ kind_name: form.kind_name, kind_alter: form.kind_alter, personen_kinder: form.personen_kinder, personen_erwachsene: form.personen_erwachsene }) });
      fetch('/api/notify-anfrage', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ typ: 'kindergeburtstag', name: form.name, email: form.email, telefon: form.telefon, datum: form.datum, uhrzeit: form.uhrzeit, details: `Kind: ${form.kind_name}, ${form.kind_alter} J. · ${form.personen_kinder} Kinder, ${form.personen_erwachsene} Erw.` }) }).catch(() => {});
      if (form.email) fetch('/api/confirm-anfrage', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ typ: 'kindergeburtstag', name: form.name, email: form.email, datum: form.datum, uhrzeit: form.uhrzeit, details: `Kind: ${form.kind_name}, ${form.kind_alter} J. · ${form.personen_kinder} Kinder, ${form.personen_erwachsene} Erw.` }) }).catch(() => {});
      setLetzteBuchung({ name: form.name, datum: form.datum, uhrzeit: form.uhrzeit, extra: `Kind: ${form.kind_name} · ${form.personen_kinder} Kinder` });
      setStatus({ type: 'success' });
      setForm({ name: '', email: '', telefon: '', kind_name: '', kind_alter: '', datum: '', uhrzeit: '', personen_kinder: '', personen_erwachsene: '', wuensche: '' });
    } catch { setStatus({ type: 'error', msg: 'Fehler beim Senden. Bitte erneut versuchen.' }); }
  };

  if (status?.type === 'success' && letzteBuchung) {
    return <AnfrageErfolg {...letzteBuchung} onNeu={() => { setStatus(null); setLetzteBuchung(null); }} />;
  }

  return (
    <form onSubmit={handleSubmit}>
      <div className="anfrage-info">Buchungsart: <strong>Auf Anfrage</strong> — wir bestätigen Ihren Termin per E-Mail.</div>
      {status?.type === 'error' && <div className="msg-error">{status.msg}</div>}
      <div className="form-grid">
        <div className="form-group"><label>Name Elternteil *</label><input required value={form.name} onChange={e => set('name', e.target.value)} placeholder="Ihr Name" /></div>
        <div className="form-group"><label>E-Mail *</label><input required type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="email@beispiel.de" /></div>
        <div className="form-group"><label>Telefon *</label><input required value={form.telefon} onChange={e => set('telefon', e.target.value)} placeholder="+49 221 ..." /></div>
        <div className="form-group"><label>Name des Kindes *</label><input required value={form.kind_name} onChange={e => set('kind_name', e.target.value)} placeholder="Vorname" /></div>
        <div className="form-group"><label>Alter des Kindes *</label><input required value={form.kind_alter} onChange={e => set('kind_alter', e.target.value)} placeholder="z.B. 7" /></div>
        <div className="form-group"><label>Datum *</label><input required type="date" value={form.datum} onChange={e => set('datum', e.target.value)} min={new Date().toISOString().split('T')[0]} /></div>
        <div className="form-group"><label>Uhrzeit *</label><select required value={form.uhrzeit} onChange={e => set('uhrzeit', e.target.value)}><option value="">-- Bitte wählen --</option>{ZEITEN.map(z => <option key={z} value={z}>{z} Uhr</option>)}</select></div>
        <div className="form-group"><label>Anzahl Kinder *</label><input required value={form.personen_kinder} onChange={e => set('personen_kinder', e.target.value)} placeholder="z.B. 10" /></div>
        <div className="form-group"><label>Anzahl Erwachsene *</label><input required value={form.personen_erwachsene} onChange={e => set('personen_erwachsene', e.target.value)} placeholder="z.B. 4" /></div>
      </div>
      <div className="form-group" style={{ marginBottom: '2rem' }}><label>Besondere Wünsche</label><textarea value={form.wuensche} onChange={e => set('wuensche', e.target.value)} placeholder="Dekorationen, Allergien, spezielle Anfragen..." /></div>
      <button type="submit" className="btn-primary">Anfrage absenden</button>
    </form>
  );
}

// ===== EISGEBURTSTAG FORM =====
function EisgeburtstagsForm() {
  const minDatum = () => { const d = new Date(); d.setDate(d.getDate() + 14); return d.toISOString().split('T')[0]; };

  const [form, setForm] = useState({ name: '', email: '', telefon: '', kind_name: '', datum: '', uhrzeit: '', paket: '2.5h', kinder_anzahl: '', wuensche: '' });
  const [status, setStatus] = useState(null);
  const [letzteBuchung, setLetzteBuchung] = useState(null);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const getZeitslots = (datumStr) => {
    if (!datumStr) return [];
    const tag = new Date(datumStr).getDay();
    return (tag === 0 || tag === 6) ? ['10:30', '15:30'] : ['15:30'];
  };

  const handleDatumChange = (v) => {
    const slots = getZeitslots(v);
    setForm(f => ({ ...f, datum: v, uhrzeit: slots.length === 1 ? slots[0] : '' }));
  };

  const zeitslots = getZeitslots(form.datum);
  const isWochenende = form.datum && (new Date(form.datum).getDay() === 0 || new Date(form.datum).getDay() === 6);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.telefon || !form.kind_name || !form.datum || !form.uhrzeit || !form.kinder_anzahl) {
      setStatus({ type: 'error', msg: 'Bitte alle Pflichtfelder ausfüllen.' }); return;
    }
    const anzahlKinder = parseInt(form.kinder_anzahl, 10);
    if (!(anzahlKinder >= 1 && anzahlKinder <= 10)) {
      setStatus({ type: 'error', msg: 'Der Eisgeburtstag ist für 1 bis 10 Kinder möglich. Bitte geben Sie eine Zahl zwischen 1 und 10 ein.' }); return;
    }
    try {
      await addAnfrage({
        typ: 'eisgeburtstag', name: form.name, email: form.email, telefon: form.telefon,
        datum: form.datum, uhrzeit: form.uhrzeit, notiz: form.wuensche, status: 'offen',
        details: JSON.stringify({ kind_name: form.kind_name, paket: form.paket, kinder_anzahl: form.kinder_anzahl, preis_pro_kind: null, preis_pauschal: 290 })
      });
      fetch('/api/notify-anfrage', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ typ: 'eisgeburtstag', name: form.name, email: form.email, telefon: form.telefon, datum: form.datum, uhrzeit: form.uhrzeit, details: `Kind: ${form.kind_name} · Paket: ${form.paket} · ${form.kinder_anzahl} Kinder` }) }).catch(() => {});
      if (form.email) fetch('/api/confirm-anfrage', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ typ: 'eisgeburtstag', name: form.name, email: form.email, datum: form.datum, uhrzeit: form.uhrzeit, details: `Kind: ${form.kind_name} · Paket: ${form.paket} · ${form.kinder_anzahl} Kinder` }) }).catch(() => {});
      setLetzteBuchung({ name: form.name, datum: form.datum, uhrzeit: form.uhrzeit, extra: `Kind: ${form.kind_name} · ${form.kinder_anzahl} Kinder · Eisparty 2,5 h` });
      setStatus({ type: 'success' });
      setForm({ name: '', email: '', telefon: '', kind_name: '', datum: '', uhrzeit: '', paket: '2.5h', kinder_anzahl: '', wuensche: '' });
    } catch { setStatus({ type: 'error', msg: 'Fehler beim Senden. Bitte erneut versuchen.' }); }
  };

  if (status?.type === 'success' && letzteBuchung) {
    return <AnfrageErfolg {...letzteBuchung} onNeu={() => { setStatus(null); setLetzteBuchung(null); }} />;
  }

  return (
    <form onSubmit={handleSubmit}>
      <div className="anfrage-info">
        <strong>Eislabor-Geburtstag Köln</strong> — Kids ab 6 Jahren stellen ihr eigenes Eis her!<br />
        <span style={{ fontSize: '0.82rem' }}>Bitte mindestens 2 Wochen im Voraus buchen</span>
      </div>

      {/* Paket-Auswahl */}
      <div className="eis-pakete">
        <div className="eis-paket-card aktiv" style={{ cursor: 'default' }}>
          <div className="eis-paket-titel">🍦 Eisparty 2,5h</div>
          <div className="eis-paket-preis">290 € für 1–10 Kinder</div>
          <div className="eis-paket-zeit">15:30 – 18:00 Uhr</div>
          <ul className="eis-paket-list">
            <li>Echte Speiseeismaschine</li>
            <li>2 Eissorten nach Wahl</li>
            <li>Kuchenstückchen &amp; Snacks</li>
            <li>Waffeltüte oder Becher</li>
            <li>Wasser &amp; Apfelschorle</li>
            <li>Eisdiplom als Andenken</li>
          </ul>
        </div>
      </div>

      {status?.type === 'error' && <div className="msg-error">{status.msg}</div>}

      <div className="form-grid">
        <div className="form-group"><label>Ihr Name *</label><input required value={form.name} onChange={e => set('name', e.target.value)} placeholder="Vor- und Nachname" /></div>
        <div className="form-group"><label>E-Mail *</label><input required type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="email@beispiel.de" /></div>
        <div className="form-group"><label>Telefon *</label><input required value={form.telefon} onChange={e => set('telefon', e.target.value)} placeholder="+49 221 ..." /></div>
        <div className="form-group"><label>Name des Geburtstagskindes *</label><input required value={form.kind_name} onChange={e => set('kind_name', e.target.value)} placeholder="Vorname" /></div>
        <div className="form-group">
          <label>Datum * <span style={{ fontSize: '0.75rem', color: 'var(--gray)' }}>(mind. 2 Wochen Vorlauf)</span></label>
          <input required type="date" value={form.datum} onChange={e => handleDatumChange(e.target.value)} min={minDatum()} />
          {form.datum && <div style={{ fontSize: '0.75rem', color: 'var(--gray)', marginTop: '0.3rem' }}>{isWochenende ? '🗓 Wochenende: 10:30 oder 15:30 Uhr verfügbar' : '🗓 Wochentag: 15:30 Uhr'}</div>}
        </div>
        <div className="form-group">
          <label>Uhrzeit *</label>
          <select required value={form.uhrzeit} onChange={e => set('uhrzeit', e.target.value)} disabled={!form.datum}>
            {!form.datum && <option value="">Zuerst Datum wählen</option>}
            {zeitslots.map(z => <option key={z} value={z}>{z} Uhr</option>)}
          </select>
        </div>
        <div className="form-group">
          <label>Anzahl Kinder * <span style={{ fontSize: '0.75rem', color: 'var(--gray)' }}>(1–10)</span></label>
          <input required type="number" min="1" max="10" value={form.kinder_anzahl} onChange={e => set('kinder_anzahl', e.target.value)} placeholder="z.B. 10" />
        </div>
      </div>

      <div className="form-group" style={{ marginBottom: '0.5rem' }}>
        <label>Eisgeschmäcker &amp; Wünsche <span style={{ fontSize: '0.75rem', color: 'var(--gray)' }}>(Absprache 7 Tage vor dem Termin)</span></label>
        <textarea value={form.wuensche} onChange={e => set('wuensche', e.target.value)} placeholder="Lieblings-Eissorten, Allergien, Sonderwünsche..." />
      </div>
      <div style={{ marginBottom: '1.75rem' }} />
      <button type="submit" className="btn-primary">Buchungsanfrage senden</button>
    </form>
  );
}

// ===== CATERING FORM =====
function CateringForm() {
  const [form, setForm] = useState({ name: '', email: '', telefon: '', veranstaltungsart: '', datum: '', uhrzeit: '', personen: '', ort: '', wuensche: '' });
  const [status, setStatus] = useState(null);
  const [letzteBuchung, setLetzteBuchung] = useState(null);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.telefon || !form.veranstaltungsart || !form.datum || !form.uhrzeit || !form.personen || !form.ort) {
      setStatus({ type: 'error', msg: 'Bitte alle Pflichtfelder ausfüllen.' });
      return;
    }
    try {
      await addAnfrage({ typ: 'catering', name: form.name, email: form.email, telefon: form.telefon, datum: form.datum, uhrzeit: form.uhrzeit, notiz: form.wuensche, status: 'offen', details: JSON.stringify({ veranstaltungsart: form.veranstaltungsart, personen: form.personen, ort: form.ort }) });
      fetch('/api/notify-anfrage', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ typ: 'catering', name: form.name, email: form.email, telefon: form.telefon, datum: form.datum, uhrzeit: form.uhrzeit, details: `Art: ${form.veranstaltungsart} · ${form.personen} Personen · Ort: ${form.ort}` }) }).catch(() => {});
      if (form.email) fetch('/api/confirm-anfrage', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ typ: 'catering', name: form.name, email: form.email, datum: form.datum, uhrzeit: form.uhrzeit, details: `Art: ${form.veranstaltungsart} · ${form.personen} Personen · Ort: ${form.ort}` }) }).catch(() => {});
      setLetzteBuchung({ name: form.name, datum: form.datum, uhrzeit: form.uhrzeit, extra: `${form.veranstaltungsart} · ${form.personen} Personen` });
      setStatus({ type: 'success' });
      setForm({ name: '', email: '', telefon: '', veranstaltungsart: '', datum: '', uhrzeit: '', personen: '', ort: '', wuensche: '' });
    } catch { setStatus({ type: 'error', msg: 'Fehler beim Senden. Bitte erneut versuchen.' }); }
  };

  if (status?.type === 'success' && letzteBuchung) {
    return <AnfrageErfolg {...letzteBuchung} onNeu={() => { setStatus(null); setLetzteBuchung(null); }} />;
  }

  return (
    <form onSubmit={handleSubmit}>
      <div className="anfrage-info">Buchungsart: <strong>Auf Anfrage</strong> — wir bestätigen Ihren Termin per E-Mail.</div>
      {status?.type === 'error' && <div className="msg-error">{status.msg}</div>}
      <div className="form-grid">
        <div className="form-group"><label>Name *</label><input required value={form.name} onChange={e => set('name', e.target.value)} placeholder="Ihr Name" /></div>
        <div className="form-group"><label>E-Mail *</label><input required type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="email@beispiel.de" /></div>
        <div className="form-group"><label>Telefon *</label><input required value={form.telefon} onChange={e => set('telefon', e.target.value)} placeholder="+49 221 ..." /></div>
        <div className="form-group"><label>Veranstaltungsart *</label><input required value={form.veranstaltungsart} onChange={e => set('veranstaltungsart', e.target.value)} placeholder="z.B. Firmenfeier, Hochzeit, Geburtstag" /></div>
        <div className="form-group"><label>Datum *</label><input required type="date" value={form.datum} onChange={e => set('datum', e.target.value)} min={new Date().toISOString().split('T')[0]} /></div>
        <div className="form-group"><label>Uhrzeit *</label><select required value={form.uhrzeit} onChange={e => set('uhrzeit', e.target.value)}><option value="">-- Bitte wählen --</option>{ZEITEN.map(z => <option key={z} value={z}>{z} Uhr</option>)}</select></div>
        <div className="form-group"><label>Personenanzahl *</label><input required value={form.personen} onChange={e => set('personen', e.target.value)} placeholder="z.B. 50" /></div>
        <div className="form-group"><label>Ort der Veranstaltung *</label><input required value={form.ort} onChange={e => set('ort', e.target.value)} placeholder="Adresse oder Beschreibung" /></div>
      </div>
      <div className="form-group" style={{ marginBottom: '2rem' }}><label>Menüwünsche / Besonderheiten</label><textarea value={form.wuensche} onChange={e => set('wuensche', e.target.value)} placeholder="Allergien, Menüvorstellungen, besondere Anfragen..." /></div>
      <button type="submit" className="btn-primary">Anfrage absenden</button>
    </form>
  );
}

// ===== EVENT FORM =====
function EventForm() {
  const [form, setForm] = useState({ name: '', email: '', telefon: '', event_art: '', datum: '', uhrzeit: '', gaeste: '', beschreibung: '' });
  const [status, setStatus] = useState(null);
  const [letzteBuchung, setLetzteBuchung] = useState(null);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.telefon || !form.event_art || !form.datum || !form.uhrzeit || !form.gaeste) {
      setStatus({ type: 'error', msg: 'Bitte alle Pflichtfelder ausfüllen.' });
      return;
    }
    try {
      await addAnfrage({ typ: 'event', name: form.name, email: form.email, telefon: form.telefon, datum: form.datum, uhrzeit: form.uhrzeit, notiz: form.beschreibung, status: 'offen', details: JSON.stringify({ event_art: form.event_art, gaeste: form.gaeste }) });
      fetch('/api/notify-anfrage', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ typ: 'event', name: form.name, email: form.email, telefon: form.telefon, datum: form.datum, uhrzeit: form.uhrzeit, details: `Art: ${form.event_art} · ${form.gaeste} Gäste` }) }).catch(() => {});
      if (form.email) fetch('/api/confirm-anfrage', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ typ: 'event', name: form.name, email: form.email, datum: form.datum, uhrzeit: form.uhrzeit, details: `Art: ${form.event_art} · ${form.gaeste} Gäste` }) }).catch(() => {});
      setLetzteBuchung({ name: form.name, datum: form.datum, uhrzeit: form.uhrzeit, extra: `${form.event_art} · ${form.gaeste} Gäste` });
      setStatus({ type: 'success' });
      setForm({ name: '', email: '', telefon: '', event_art: '', datum: '', uhrzeit: '', gaeste: '', beschreibung: '' });
    } catch { setStatus({ type: 'error', msg: 'Fehler beim Senden. Bitte erneut versuchen.' }); }
  };

  if (status?.type === 'success' && letzteBuchung) {
    return <AnfrageErfolg {...letzteBuchung} onNeu={() => { setStatus(null); setLetzteBuchung(null); }} />;
  }

  return (
    <form onSubmit={handleSubmit}>
      <div className="anfrage-info">Buchungsart: <strong>Auf Anfrage</strong> — wir bestätigen Ihren Termin per E-Mail.</div>
      {status?.type === 'error' && <div className="msg-error">{status.msg}</div>}
      <div className="form-grid">
        <div className="form-group"><label>Name *</label><input required value={form.name} onChange={e => set('name', e.target.value)} placeholder="Ihr Name" /></div>
        <div className="form-group"><label>E-Mail *</label><input required type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="email@beispiel.de" /></div>
        <div className="form-group"><label>Telefon *</label><input required value={form.telefon} onChange={e => set('telefon', e.target.value)} placeholder="+49 221 ..." /></div>
        <div className="form-group"><label>Art des Events *</label><input required value={form.event_art} onChange={e => set('event_art', e.target.value)} placeholder="z.B. Konzert, Lesung, Firmenabend" /></div>
        <div className="form-group"><label>Datum *</label><input required type="date" value={form.datum} onChange={e => set('datum', e.target.value)} min={new Date().toISOString().split('T')[0]} /></div>
        <div className="form-group"><label>Uhrzeit *</label><select required value={form.uhrzeit} onChange={e => set('uhrzeit', e.target.value)}><option value="">-- Bitte wählen --</option>{ZEITEN.map(z => <option key={z} value={z}>{z} Uhr</option>)}</select></div>
        <div className="form-group"><label>Erwartete Gästezahl *</label><input required value={form.gaeste} onChange={e => set('gaeste', e.target.value)} placeholder="z.B. 80" /></div>
      </div>
      <div className="form-group" style={{ marginBottom: '2rem' }}><label>Beschreibung des Events</label><textarea value={form.beschreibung} onChange={e => set('beschreibung', e.target.value)} placeholder="Was planen Sie? Was sind Ihre Vorstellungen?" /></div>
      <button type="submit" className="btn-primary">Anfrage absenden</button>
    </form>
  );
}

// ===== GRISCHI FEST (09.–11.10.2026) – Poster-Popup, verschwindet nach dem letzten Festtag von selbst =====
const GRISCHI = { tage: ['2026-10-09', '2026-10-10', '2026-10-11'], bild: '/grischi-fest.jpg' };
function grischiAktiv() { return heuteLokal() <= GRISCHI.tage[GRISCHI.tage.length - 1]; }
// Herbstbrunch (einmalig, Testlauf – Ingo 16.10.2026): Reiter „Brunch" = normale Tischreservierung mit FESTEM Datum; verschwindet nach dem Veranstaltungstag
const BRUNCH = { datum: '2026-11-15', datumText: 'Sonntag, 15.11.2026' };
const BRUNCH_AUSGEBUCHT = true; // Ingo 28.10.2026: Brunch ausgebucht -> Option, Reiter und Karte sind ausgeblendet (auf false setzen, um sie wieder zu zeigen)
function brunchAktiv() { return !BRUNCH_AUSGEBUCHT && heuteLokal() <= BRUNCH.datum; }
function GrischiPopup({ onClose, knopf = 'Schließen' }) {
  return (
    <div className="grischi-overlay" onClick={onClose}>
      <div className="grischi-box" onClick={e => e.stopPropagation()}>
        <button className="grischi-x" onClick={onClose} aria-label="Schließen">✕</button>
        <img src={GRISCHI.bild} alt="Grischi Fest im Savoca – 9., 10. und 11. Oktober: Weißwurst, Obatzda, Allgäu-Döner, Krautnudeln, Bayrische Pizza, Schweinsbraten" />
        <div className="grischi-ki">Bild mit KI erstellt</div>
        <button className="btn-primary grischi-btn" onClick={onClose}>{knopf}</button>
      </div>
    </div>
  );
}

// ===== ADMIN-LOGIN (Supabase Auth, Rolle admin in app_metadata) =====
function istAdminSession(session) {
  return !!(session && session.user && session.user.app_metadata && session.user.app_metadata.role === 'admin');
}
async function adminHeaders() {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// ===== TAGESSCHLUSS 17:30 =====
// Ab 17:30 Uhr sind für den HEUTIGEN Tag keine Online-Reservierungen mehr möglich (Service sieht späte Buchungen nicht mehr).
// Morgen und später bleiben buchbar. Die Uhrzeit kommt aus dem Browser des Kunden (lokale Zeit).
const TAGESSCHLUSS_MIN = 17 * 60 + 30;
const SAVOCA_TELEFON = '0221 67811545';
function heuteLokal(d = new Date()) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function istNachTagesschluss(jetzt = new Date()) {
  return jetzt.getHours() * 60 + jetzt.getMinutes() >= TAGESSCHLUSS_MIN;
}
function istTagesschluss(datum, jetzt = new Date()) {
  if (!datum || datum !== heuteLokal(jetzt)) return false;
  return istNachTagesschluss(jetzt);
}
function morgenLokal(jetzt = new Date()) {
  const d = new Date(jetzt); d.setDate(d.getDate() + 1); return heuteLokal(d);
}

// Reservierbare Termine aus Admin → Termine (Haken „Reservierung möglich"). EINE Quelle für das Fenster
// „Was möchtest du buchen?", die Reiterleiste und die Karten auf der App-Startseite (Ingo 21.10.2026, Kartei #81/#187).
// Nach dem Termin fallen sie von selbst heraus (datum >= heute).
let _reservierbareTermine = null;
function ladeReservierbareTermine() {
  if (!_reservierbareTermine) {
    _reservierbareTermine = Promise.resolve(
      supabase.from('termine').select('id,titel,datum,uhrzeit,preis,beschreibung,reservierbar,aktiv')
        .eq('aktiv', true).eq('reservierbar', true).gte('datum', heuteLokal()).order('datum', { ascending: true })
    ).then(({ data }) => (data || []).filter(t => /^\d{1,2}:\d{2}$/.test(t.uhrzeit || '')))
     .catch(() => { _reservierbareTermine = null; return []; });
  }
  return _reservierbareTermine;
}
function useReservierbareTermine() {
  const [liste, setListe] = useState([]);
  useEffect(() => { let an = true; ladeReservierbareTermine().then(l => { if (an) setListe(l); }); return () => { an = false; }; }, []);
  return liste;
}
const WT_KURZ = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
function terminKurz(t) {
  const [, m, d] = String(t.datum).split('-');
  return [`${WT_KURZ[new Date(t.datum + 'T12:00:00').getDay()]} ${d}.${m}.`, `${t.uhrzeit} Uhr`, t.preis].filter(Boolean).join(' · ');
}
const terminOption = (t) => ({ key: 'termin:' + t.id, icon: '🎟', titel: t.titel, desc: terminKurz(t) });

// ===== RESERVIERUNG =====
const ZEITEN = ['09:00','09:30','10:00','10:30','11:00','11:30','12:00','12:30','13:00','13:30','14:00','14:30','15:00','15:30','16:00','16:30','17:00','17:30','18:00','18:30','19:00','19:30','20:00','20:30','21:00','21:30','22:00','22:30','23:00'];

// Ein Schritt im geführten Reservierungsformular. MUSS auf Modulebene stehen: Innerhalb von Reservierung() definiert, wäre es bei jedem
// Neuzeichnen ein "neuer" Baustein – React baut dann das ganze Formular neu auf, jedes Feld verliert beim Tippen den Fokus und der
// Datumsdialog springt am Handy weg (Fehler 20.10.2026, Kartei #199).
function Schritt({ nr, titel, offen, vorher, children }) {
  return (
    <div className={`schritt${offen ? '' : ' gesperrt'}`}>
      <div className="schritt-kopf"><span className="schritt-nr">{nr}</span><span className="schritt-titel">{titel}</span></div>
      <fieldset disabled={!offen}>{children}</fieldset>
      {!offen && <div className="schritt-hinweis">Bitte zuerst {vorher} ausfüllen.</div>}
    </div>
  );
}

function Reservierung({ startTab = 'tisch', setPage }) {
  const startTerminId = startTab.startsWith('termin:') ? startTab.slice(7)
    : startTab === 'termin' ? new URLSearchParams(window.location.search).get('termin') : null;
  const [aktTab, setAktTab] = useState(startTab.startsWith('termin') ? 'termin' : startTab);
  const [terminId, setTerminId] = useState(startTerminId);
  const alleTermine = useReservierbareTermine();
  const [form, setForm] = useState({ name: '', email: '', telefon: '', datum: '', uhrzeit: '', personen: '', sitzbereich: '', bemerkung: '' });
  const [tischId, setTischId] = useState(null);
  const [status, setStatus] = useState(null);
  const [letzteBuchung, setLetzteBuchung] = useState(null);
  const [buchungsstoppFenster, setBuchungsstoppFenster] = useState({});
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const istBrunch = aktTab === 'brunch';
  // Event aus dem Kalender (Admin → Termine, „Reservierung möglich"): ?tab=termin&termin=<id>. Datum UND Uhrzeit stehen fest, Bereichs-Grenzen gelten wie beim Brunch.
  const [eventTermin, setEventTermin] = useState(null);
  const [eventLaedt, setEventLaedt] = useState(startTab.startsWith('termin'));
  useEffect(() => {
    const id = terminId;
    if (!/^[0-9a-f-]{36}$/i.test(id || '')) { setEventLaedt(false); setEventTermin(null); setAktTab(a => (a === 'termin' ? 'tisch' : a)); return; }
    let an = true;
    setEventLaedt(true);
    supabase.from('termine').select('id,titel,datum,uhrzeit,preis,beschreibung,reservierbar,aktiv').eq('id', id).eq('aktiv', true).maybeSingle().then(({ data }) => {
      if (!an) return;
      if (data && data.reservierbar && data.datum >= heuteLokal() && /^\d{1,2}:\d{2}$/.test(data.uhrzeit || '')) setEventTermin({ ...data, uhrzeit: data.uhrzeit.padStart(5, '0') });
      else { setEventTermin(null); setAktTab('tisch'); }   // abgelaufen, versteckt oder nicht reservierbar: normale Tischreservierung
      setEventLaedt(false);
    });
    return () => { an = false; };
  }, [terminId]); // eslint-disable-line
  const istTermin = aktTab === 'termin' && !!eventTermin && eventTermin.id === terminId;
  const fest = istBrunch
    ? { datum: BRUNCH.datum, datumText: BRUNCH.datumText, zeit: null, typ: 'brunch', anlass: 'brunch', prefix: '🥂 Herbstbrunch' }
    : istTermin
      ? { datum: eventTermin.datum, datumText: datumDe(eventTermin.datum), zeit: eventTermin.uhrzeit, typ: 'event', anlass: 'event', prefix: `🎟 ${eventTermin.titel}${eventTermin.preis ? ' (' + eventTermin.preis + ')' : ''}` }
      : null;
  // Geführter Ablauf (Ingo 19.10.2026): 1 Bereich → 2 Datum → 3 Personen → 4 Uhrzeit → 5 Kontakt. Spätere Schritte bleiben grau, bis der vorherige ausgefüllt ist.
  const [personenHinweis, setPersonenHinweis] = useState('');
  const waehleBereich = (key) => {
    const info = bereichInfo(key, form.datum), max = info?.maxPersonen || 10;
    if (parseInt(form.personen, 10) > max) setPersonenHinweis(`Im Bereich „${info.label}“ sind online höchstens ${max} Personen möglich – bitte die Personenzahl neu wählen.`);
    else setPersonenHinweis('');
    const bis = BEREICHE.find(x => x.key === key)?.gesperrtBis;
    const datumZuFrueh = !!(bis && form.datum && form.datum < bis);
    if (datumZuFrueh) setPersonenHinweis(`Das Kleine Savoca ist wegen Umbau online erst ab dem ${datumDe(bis)} buchbar – bitte ein späteres Datum wählen. Für früher rufen Sie uns an: ${SAVOCA_TELEFON}`);
    setForm(f => ({ ...f, sitzbereich: key, datum: datumZuFrueh ? '' : f.datum, uhrzeit: datumZuFrueh ? '' : f.uhrzeit, personen: parseInt(f.personen, 10) > max ? '' : f.personen }));
  };
  // Brunch: Datum steht fest; beim Verlassen des Reiters wieder frei
  const festRef = useRef(null);
  useEffect(() => {
    const vorher = festRef.current;   // vor dem Überschreiben festhalten – setForm läuft verzögert (Datum blieb sonst beim Wechsel auf „Tisch" stehen, 21.10.)
    setForm(f => fest
      ? { ...f, datum: fest.datum, uhrzeit: fest.zeit || (vorher && vorher.datum === fest.datum ? f.uhrzeit : '') }
      : (vorher && f.datum === vorher.datum ? { ...f, datum: '', uhrzeit: '' } : f));
    festRef.current = fest; setStatus(null);
  }, [fest?.datum, fest?.zeit]); // eslint-disable-line
  const [jetzt, setJetzt] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setJetzt(new Date()), 60000); return () => clearInterval(t); }, []);
  const [grischiHinweis, setGrischiHinweis] = useState(false);
  useEffect(() => { if (form.datum && GRISCHI.tage.includes(form.datum)) setGrischiHinweis(true); }, [form.datum]);
  const tagesschluss = istNachTagesschluss(jetzt);              // ab 17:30: Banner oben + heute nicht mehr wählbar
  const heuteGesperrt = istTagesschluss(form.datum, jetzt);      // Sicherheitsnetz: falls doch "heute" im Feld steht
  const minDatumTisch = tagesschluss ? morgenLokal(jetzt) : heuteLokal(jetzt);
  useEffect(() => { if (heuteGesperrt) setForm(f => ({ ...f, datum: '' })); }, [heuteGesperrt]); // eslint-disable-line

  const istGesperrt = (datum, uhrzeit) => {
    if (fest) return false;   // Brunch-/Event-Reiter: der Buchungsstopp gilt nur für normale Tische
    const gesperrt = buchungsstoppFenster[datum];
    if (!gesperrt || !uhrzeit) return false;
    const fenster = findeZeitfenster(uhrzeit);
    return fenster ? gesperrt.includes(fenster.start) : false;
  };

  useEffect(() => {
    supabase.from('buchungsstopp_fenster').select('datum,start_zeit')
      .then(({ data }) => {
        const map = {};
        (data || []).forEach(r => { (map[r.datum] = map[r.datum] || []).push(r.start_zeit); });
        setBuchungsstoppFenster(map);
      });
    supabase.auth.getSession().then(async ({ data }) => {
      const u = data.session?.user;
      if (!u) return;
      const { data: profil } = await supabase.from('mitglieder').select('name,email,telefon').eq('user_id', u.id).maybeSingle();
      if (profil) setForm(f => ({ ...f, name: profil.name || '', email: profil.email || '', telefon: profil.telefon || '' }));
    });
  }, []); // eslint-disable-line

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.telefon || !form.datum || !form.uhrzeit || !form.personen || (bereichGilt(form.datum) && !form.sitzbereich)) {
      setStatus({ type: 'error', msg: 'Bitte alle Pflichtfelder ausfüllen (Name, E-Mail, Telefon, Datum, Uhrzeit, Personen, Sitzbereich).' });
      return;
    }
    if (istTagesschluss(form.datum)) {
      setStatus({ type: 'error', msg: `Für heute sind online keine Reservierungen mehr möglich. Bitte rufen Sie uns an: ${SAVOCA_TELEFON}` });
      return;
    }
    if (istGesperrt(form.datum, form.uhrzeit)) {
      setStatus({ type: 'error', msg: 'Für dieses Datum/diese Uhrzeit nehmen wir keine Reservierungen an. Bitte wählen Sie ein anderes Datum oder eine andere Uhrzeit.' });
      return;
    }
    try {
      const kap = await pruefeKapazitaet(form.datum, form.uhrzeit, form.personen, bereichGilt(form.datum) ? form.sitzbereich : null);
      if (!kap.erlaubt) {
        setStatus({ type: 'error', msg: kapazitaetsMeldung(kap) });
        return;
      }

      const gespeichert = await addReservierung({ ...form, bemerkung: fest ? [fest.prefix, form.bemerkung].filter(Boolean).join(' · ') : form.bemerkung, anfrage_typ: fest ? fest.typ : null, sitzbereich: bereichGilt(form.datum) ? (form.sitzbereich || null) : null, tisch_id: null, bereich: null, status: 'bestätigt' });
      setLetzteBuchung(gespeichert);
      setStatus({ type: 'success', msg: `🎉 Ihre Reservierung für ${form.name} am ${form.datum} um ${form.uhrzeit} Uhr ist bestätigt! Wir freuen uns auf Sie – viel Spaß bei Ihrem Besuch!` });
      fetch('/api/notify-reservation', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name: form.name, email: form.email, telefon: form.telefon, datum: form.datum, uhrzeit: form.uhrzeit, personen: form.personen, sitzbereich: bereichGilt(form.datum) ? form.sitzbereich : '', anlass: fest ? fest.anlass : '', bemerkung: form.bemerkung, notiz: '' }) }).catch(() => {});
      fetch('/api/confirm-reservation', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name: form.name, email: form.email, datum: form.datum, uhrzeit: form.uhrzeit, personen: form.personen, sitzbereich: bereichGilt(form.datum) ? form.sitzbereich : '', anlass: fest ? fest.anlass : '' }) }).catch(() => {});
      setForm(f => ({ ...f, datum: fest ? fest.datum : '', uhrzeit: fest?.zeit || '', personen: '', sitzbereich: '', bemerkung: '' }));
    } catch (err) {
      // Die Datenbank prüft die Kapazität zusätzlich (z. B. wenn zwei Gäste gleichzeitig buchen) und meldet das so:
      const voll = String(err?.message || '').includes('kapazitaet_ueberschritten');
      const zuViele = String(err?.message || '').includes('personen_ueberschritten');
      const gesperrt = String(err?.message || '').includes('bereich_gesperrt');
      setStatus({ type: 'error', msg: gesperrt ? `Das Kleine Savoca ist wegen Umbau online erst ab dem 14.11. buchbar. Bitte rufen Sie uns an: ${SAVOCA_TELEFON}` : voll ? 'Dieser Bereich ist zu dieser Uhrzeit inzwischen leider ausgebucht. Bitte wählen Sie eine andere Uhrzeit oder einen anderen Bereich.' : zuViele ? `Für diese Personenzahl bitte rufen Sie uns an: ${SAVOCA_TELEFON}` : 'Fehler beim Speichern. Bitte versuche es erneut.' });
    }
  };

  const TABS = [
    { key: 'tisch', label: '🍽 Tisch reservieren' },
    ...(brunchAktiv() ? [{ key: 'brunch', label: '🥂 Brunch 15.11.' }] : []),
    ...[...alleTermine, ...(eventTermin && !alleTermine.some(t => t.id === eventTermin.id) ? [eventTermin] : [])].map(t => (
      { key: 'termin:' + t.id, label: `🎟 ${t.titel.length > 22 ? t.titel.slice(0, 21) + '…' : t.titel}` })),
    { key: 'eisgeburtstag', label: '🍦 Eisgeburtstag' },
    { key: 'kindergeburtstag', label: '🎂 Kindergeburtstag' },
    { key: 'catering', label: '🍾 Catering' },
    { key: 'event', label: '🎉 Event' },
  ];

  return (
    <div className="shimmer-res-wrapper">
      <div className="shimmer-w1" />
      <div className="shimmer-w2" />
      <div className="shimmer-swirl-1" />
      <div className="shimmer-swirl-2" />
      <div className="res-card">
      <div className="page-title">Buchen & Anfragen</div>
      <div className="page-subtitle">Savoca · Köln</div>
      <div className="divider" />

      <div className="buchung-tabs">
        {TABS.map(t => (
          <button key={t.key} className={(t.key.startsWith('termin:') ? aktTab === 'termin' && terminId === t.key.slice(7) : aktTab === t.key) ? 'active' : ''}
            onClick={() => { if (t.key.startsWith('termin:')) { setTerminId(t.key.slice(7)); setAktTab('termin'); } else setAktTab(t.key); }}>
            {t.label}
          </button>
        ))}
      </div>

      {aktTab === 'eisgeburtstag' && <EisgeburtstagsForm />}
      {aktTab === 'kindergeburtstag' && <KindGeburtstagForm />}
      {aktTab === 'catering' && <CateringForm />}
      {aktTab === 'event' && <EventForm />}

      {aktTab === 'termin' && eventLaedt && <p style={{ color: 'var(--gray)' }}>Das Event wird geladen …</p>}
      {(aktTab === 'tisch' || istBrunch || istTermin) && <>
      {grischiHinweis && <GrischiPopup onClose={() => setGrischiHinweis(false)} knopf="Alles klar, weiter reservieren" />}
      {status?.type === 'success' ? (
        <div style={{ textAlign: 'center', padding: '2.5rem 1rem' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>✅</div>
          <div style={{ fontFamily: 'var(--font-serif)', fontSize: '1.6rem', color: 'var(--primary)', marginBottom: '0.75rem' }}>Reservierung übermittelt!</div>
          <div style={{ color: 'var(--gray)', fontSize: '0.95rem', marginBottom: '0.5rem' }}>
            Wir haben Ihre Anfrage erhalten und bestätigen per E-Mail.
          </div>
          {letzteBuchung && (
            <div style={{ margin: '1.5rem auto', maxWidth: '320px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '10px', padding: '1.2rem 1.5rem', textAlign: 'left', fontSize: '0.9rem', lineHeight: '1.8' }}>
              <div><span style={{ color: 'var(--gray)', fontSize: '0.72rem', letterSpacing: '1.5px', textTransform: 'uppercase' }}>Name</span><br /><strong>{letzteBuchung.name}</strong></div>
              <div style={{ marginTop: '0.6rem' }}><span style={{ color: 'var(--gray)', fontSize: '0.72rem', letterSpacing: '1.5px', textTransform: 'uppercase' }}>Datum & Uhrzeit</span><br /><strong>{letzteBuchung.datum} · {letzteBuchung.uhrzeit} Uhr</strong></div>
              <div style={{ marginTop: '0.6rem' }}><span style={{ color: 'var(--gray)', fontSize: '0.72rem', letterSpacing: '1.5px', textTransform: 'uppercase' }}>Personen</span><br /><strong>{letzteBuchung.personen}</strong></div>
            </div>
          )}
          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap', marginTop: '1.5rem' }}>
            <button className="btn-primary" onClick={() => { setStatus(null); setLetzteBuchung(null); setForm(f => ({ ...f, datum: fest ? fest.datum : '', uhrzeit: fest?.zeit || '', personen: '', sitzbereich: '', bemerkung: '' })); }}>
              + Neue Reservierung
            </button>
            <button onClick={() => setPage ? setPage('home') : (window.location.href = '/')} style={{ background: 'transparent', border: '2px solid var(--primary)', color: 'var(--primary)', padding: '0.85rem 2rem', borderRadius: '4px', fontSize: '0.7rem', letterSpacing: '3px', textTransform: 'uppercase', fontWeight: '600', cursor: 'pointer' }}>
              Zur Startseite
            </button>
          </div>
        </div>
      ) : (
        <>
        {istBrunch && (
          <div className="brunch-info">
            <strong>Savoca Mercato Herbstbrunch · {BRUNCH.datumText}</strong><br />
            Einmalig – wir testen den Brunch zum ersten Mal! Leckeres Buffet, warm &amp; kalt, mit Begrüßungsgetränk. Draußen Herbst, drinnen Italien.<br />
            <span style={{ fontSize: '0.82rem' }}>29 € p. P. · Kinder bis 4 Jahre frei · 4–12 Jahre 14 € · mit Mimosa-Flatrate 45 € p. P. · Kaffeespezialitäten nicht inklusive</span>
          </div>
        )}
        {istTermin && (
          <div className="brunch-info">
            <strong>{eventTermin.titel} · {fest.datumText}, {eventTermin.uhrzeit} Uhr</strong>
            {eventTermin.preis && <><br /><span style={{ fontSize: '0.9rem' }}>{eventTermin.preis} · bezahlt wird vor Ort</span></>}
            {eventTermin.beschreibung && <><br /><span style={{ fontSize: '0.85rem' }}>{eventTermin.beschreibung.length > 300 ? eventTermin.beschreibung.slice(0, 300) + '…' : eventTermin.beschreibung}</span></>}
          </div>
        )}
        {tagesschluss && !fest && (
          <div className="tagesschluss-banner" role="alert">
            <div className="tagesschluss-titel">📞 Bitte anrufen!</div>
            <div className="tagesschluss-text">Sie möchten heute noch einen Tisch? Sehr gerne! Unser Serviceteam ist gerade voll im Einsatz und kann Online-Reservierungen im Moment nicht mehr nachschauen.<br />Rufen Sie uns einfach kurz an, wir reservieren persönlich für Sie:</div>
            <a className="tagesschluss-telefon" href={`tel:${SAVOCA_TELEFON.replace(/\s/g, '')}`}>{SAVOCA_TELEFON}</a>
            <div className="tagesschluss-hinweis">Für morgen und alle anderen Tage können Sie unten weiterhin online reservieren.</div>
          </div>
        )}
        {status?.type === 'error' && !istGesperrt(form.datum, form.uhrzeit) && !heuteGesperrt && (
          <div className="msg-error">{status.msg}</div>
        )}
        <form onSubmit={handleSubmit}>
          {(() => {
            const s1 = !!form.sitzbereich, s2 = s1 && !!form.datum, s3 = s2 && !!form.personen, s4 = s3 && !!form.uhrzeit;
            const maxP = bereichInfo(form.sitzbereich, form.datum)?.maxPersonen || 10;
            return (<>
              <Schritt nr="1" titel="Wo möchten Sie sitzen? *" offen={true}>
                <BereichWahl value={form.sitzbereich} onChange={waehleBereich} ohneTitel datum={form.datum} />
              </Schritt>
              <Schritt nr="2" titel="Datum *" offen={s1} vorher="Schritt 1 (Bereich)">
                <div className="form-group" style={{ marginBottom: 0 }}>
                  {fest
                    ? <div className="brunch-datum">{fest.datumText}</div>
                    : <input type="date" value={form.datum} onChange={e => { const d = e.target.value; setForm(f => ({ ...f, datum: d, uhrzeit: '', personen: parseInt(f.personen, 10) > (bereichInfo(f.sitzbereich, d)?.maxPersonen || 10) ? '' : f.personen })); }} min={(() => { const bis = BEREICHE.find(x => x.key === form.sitzbereich)?.gesperrtBis; return bis && bis > minDatumTisch ? bis : minDatumTisch; })()} />}
                </div>
              </Schritt>
              <Schritt nr="3" titel="Wie viele Personen? *" offen={s2} vorher="Schritt 2 (Datum)">
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <select value={form.personen} onChange={e => { setPersonenHinweis(''); set('personen', e.target.value); }}>
                    <option value="">-- Bitte wählen --</option>
                    {Array.from({ length: maxP }, (_, i) => i + 1).map(n => <option key={n} value={n}>{n} {n === 1 ? 'Person' : 'Personen'}</option>)}
                  </select>
                </div>
                {personenHinweis && <div className="schritt-info warn">{personenHinweis}</div>}
                {s2 && <div className="schritt-info">Online bis zu {maxP} Personen. Für größere Gruppen rufen Sie uns bitte an: {SAVOCA_TELEFON}</div>}
              </Schritt>
              <Schritt nr="4" titel="Uhrzeit *" offen={s3} vorher="Schritt 3 (Personen)">
                <div className="form-group" style={{ marginBottom: 0 }}>
                  {fest?.zeit
                    ? <div className="brunch-datum">{fest.zeit} Uhr</div>
                    : <select value={form.uhrzeit} onChange={e => set('uhrzeit', e.target.value)}>
                        <option value="">-- Bitte wählen --</option>
                        {(istBrunch ? ZEITEN.filter(z => z >= '09:00' && z < '15:00') : ZEITEN).map(z => <option key={z} value={z}>{z} Uhr</option>)}
                      </select>}
                </div>
              </Schritt>
              <Schritt nr="5" titel="Ihre Kontaktdaten *" offen={s4} vorher="Schritt 4 (Uhrzeit)">
                <div className="form-grid">
                  <div className="form-group">
                    <label>Name *</label>
                    <input required value={form.name} onChange={e => set('name', e.target.value)} placeholder="Ihr Name" autoComplete="name" />
                  </div>
                  <div className="form-group">
                    <label>E-Mail *</label>
                    <input required type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="email@beispiel.de" autoComplete="email" />
                  </div>
                  <div className="form-group">
                    <label>Telefon *</label>
                    <input required value={form.telefon} onChange={e => set('telefon', e.target.value)} placeholder="+49 221 ..." autoComplete="tel" />
                  </div>
                </div>
              </Schritt>
            </>);
          })()}

          {istGesperrt(form.datum, form.uhrzeit) && (
            <div style={{ background: '#c0392b', borderRadius: '10px', padding: '1.25rem 1.5rem', marginBottom: '1.5rem', fontSize: '1.05rem', lineHeight: '1.5', color: '#fff', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '0.75rem', boxShadow: '0 4px 16px rgba(192,57,43,0.35)' }}>
              <span style={{ fontSize: '1.6rem' }}>🚫</span>
              <span>Für {form.datum ? `den ${form.datum.split('-').reverse().join('.')}` : 'dieses Datum'} um {form.uhrzeit} Uhr nehmen wir leider keine Reservierungen mehr an. Bitte wählen Sie ein anderes Datum oder eine andere Uhrzeit.</span>
            </div>
          )}


          <div className="form-group" style={{ marginBottom: '2rem' }}>
            <label>Bemerkungen</label>
            <textarea value={form.bemerkung} onChange={e => set('bemerkung', e.target.value)} placeholder="Sonderwünsche, Allergien, Anlass..." />
          </div>

          <button type="submit" className="btn-primary" style={{ marginTop: '2rem', opacity: heuteGesperrt ? 0.4 : 1, cursor: heuteGesperrt ? 'not-allowed' : 'pointer' }} disabled={heuteGesperrt}>Reservierung absenden</button>
        </form>
        </>
      )}
      </>}
      </div>
    </div>
  );
}


// ===== MITGLIED =====
function Mitglied({ user, setPage, startTab = 'login' }) {
  const [authTab, setAuthTab] = useState(startTab);
  const [form, setForm] = useState({ name: '', email: '', passwort: '', passwort2: '', telefon: '', geburtsdatum: '', honeypot: '', newsletter: true, datenschutz: false });
  const [pwResetMode, setPwResetMode] = useState(false);
  const [pwResetEmail, setPwResetEmail] = useState('');
  const [pwResetStatus, setPwResetStatus] = useState(null);
  const [profil, setProfil] = useState(null);
  const [profilEdit, setProfilEdit] = useState(false);
  const [profilForm, setProfilForm] = useState({});
  const [profilSaveStatus, setProfilSaveStatus] = useState(null);
  const [pizzaForm, setPizzaForm] = useState({ pizza: '', datum: '', uhrzeit: '' });
  const [pizzaStatus, setPizzaStatus] = useState(null);
  const [meineRes, setMeineRes] = useState([]);
  const [editRes, setEditRes] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editStatus, setEditStatus] = useState(null);
  const [notifErlaubt, setNotifErlaubt] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'default'
  );
  const [stempel, setStempel] = useState([]);
  const [scannerAktiv, setScannerAktiv] = useState(false);
  const [scanStatus, setScanStatus] = useState(null);
  const scannerRef = useRef(null);

  const pruefeNeueBestaetigung = (res) => {
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
    const letzterCheck = localStorage.getItem('savoca_notif_check') || '';
    const neu = res.find(r => r.status === 'bestätigt' && (!letzterCheck || r.datum >= letzterCheck));
    if (neu) {
      new Notification('Savoca — Reservierung bestätigt! 🎉', {
        body: `${neu.datum} um ${neu.uhrzeit} Uhr · ${neu.personen} Personen`,
        icon: '/favicon.ico',
      });
    }
    localStorage.setItem('savoca_notif_check', new Date().toISOString().split('T')[0]);
  };

  const pizzaTage = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(); d.setDate(d.getDate() + i);
    const iso = d.toISOString().split('T')[0];
    const label = i === 0 ? 'Heute' : i === 1 ? 'Morgen' : d.toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' });
    return { iso, label };
  });
  const [status, setStatus] = useState(null);
  const [punkte_popup, setPunktePopup] = useState(startTab === 'register');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const loadStempel = useCallback(async () => {
    if (!user) return;
    const data = await getStempel(user.id);
    setStempel(data);
  }, [user]);

  useEffect(() => {
    if (!user) return;
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    if (!token) return;
    window.history.replaceState({}, '', window.location.pathname);
    redeemToken(token, user.id)
      .then(() => { loadStempel(); setScanStatus({ ok: true, msg: '✓ Stempel gutgeschrieben!' }); })
      .catch(err => setScanStatus({ ok: false, msg: err.message || 'Fehler beim Einlösen.' }));
  }, [user]); // eslint-disable-line

  useEffect(() => { loadStempel(); }, [loadStempel]);

  useEffect(() => {
    if (!scannerAktiv || !user) return;
    let done = false;
    let scanner = null;
    const start = async () => {
      scanner = new Html5Qrcode('savoca-qr-scanner');
      scannerRef.current = scanner;
      try {
        await scanner.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: 220 },
          async (scanned) => {
            if (done) return;
            done = true;
            try { await scanner.stop(); scanner.clear(); } catch {}
            scannerRef.current = null;
            let token = scanned;
            try { const u = new URL(scanned); token = u.searchParams.get('token') || scanned; } catch {}
            try {
              await redeemToken(token, user.id);
              await loadStempel();
              setScanStatus({ ok: true, msg: '✓ Stempel gutgeschrieben!' });
            } catch (err) {
              setScanStatus({ ok: false, msg: err.message || 'Fehler beim Einlösen.' });
            }
            setScannerAktiv(false);
          },
          () => {}
        );
      } catch {
        setScanStatus({ ok: false, msg: 'Kamera nicht verfügbar.' });
        setScannerAktiv(false);
      }
    };
    start();
    return () => {
      if (scannerRef.current && !done) {
        scannerRef.current.stop().catch(() => {}).finally(() => { try { scannerRef.current?.clear(); } catch {} scannerRef.current = null; });
      }
    };
  }, [scannerAktiv]); // eslint-disable-line

  const LIMITS = { name: 50, email: 80, telefon: 20, passwort: 50 };

  useEffect(() => {
    if (!user) return;
    supabase.from('mitglieder').select('*').eq('user_id', user.id).single()
      .then(({ data }) => {
        setProfil(data);
        if (data?.email) {
          getReservierungenByEmail(data.email).then(res => {
            setMeineRes(res);
            pruefeNeueBestaetigung(res);
          });
        }
      });
  }, [user]);

  const handleRegister = async (e) => {
    e.preventDefault();
    if (form.honeypot) return; // Bot-Schutz
    if (!form.name || !form.email || !form.passwort) {
      setStatus({ type: 'error', msg: 'Name, E-Mail und Passwort sind Pflicht.' });
      return;
    }
    if (form.name.length > LIMITS.name) { setStatus({ type: 'error', msg: `Name max. ${LIMITS.name} Zeichen.` }); return; }
    if (form.email.length > LIMITS.email) { setStatus({ type: 'error', msg: `E-Mail zu lang.` }); return; }
    if (form.telefon.length > LIMITS.telefon) { setStatus({ type: 'error', msg: `Telefon max. ${LIMITS.telefon} Zeichen.` }); return; }
    if (form.passwort !== form.passwort2) {
      setStatus({ type: 'error', msg: 'Passwörter stimmen nicht überein.' });
      return;
    }
    if (form.passwort.length < 6) {
      setStatus({ type: 'error', msg: 'Passwort muss mindestens 6 Zeichen haben.' });
      return;
    }
    if (!form.datenschutz) {
      setStatus({ type: 'error', msg: 'Bitte akzeptiere die Datenschutzerklärung.' });
      return;
    }
    if (!form.newsletter) {
      setStatus({ type: 'error', msg: 'Bitte stimme dem Erhalt von Nachrichten zu.' });
      return;
    }
    const { data, error } = await supabase.auth.signUp({ email: form.email, password: form.passwort });
    if (error) { setStatus({ type: 'error', msg: error.message }); return; }
    await addMitglied({ name: form.name, email: form.email, telefon: form.telefon, geburtsdatum: form.geburtsdatum, punkte: 0, status: 'aktiv', user_id: data.user?.id, newsletter: form.newsletter });
    setStatus({ type: 'success', msg: `Willkommen, ${form.name}! Bitte bestätige deine E-Mail.` });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    const { error } = await supabase.auth.signInWithPassword({ email: form.email, password: form.passwort });
    if (error) setStatus({ type: 'error', msg: 'E-Mail oder Passwort falsch.' });
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setProfil(null);
  };

  const vorteile = [
    { icon: '⭐', text: 'Treuepunkte sammeln' },
    { icon: '🎁', text: 'Exklusive Rabatte', comingSoon: true },
    { icon: '📅', text: 'Bevorzugte Reservierung', comingSoon: true },
    { icon: '🎂', text: 'Geburtstagsüberraschung', comingSoon: true },
  ];

  // Eingeloggt → Dashboard
  if (user && profil) {
    return (
      <div className="page" style={{ maxWidth: '600px' }}>
        <div className="page-title">Willkommen, {profil.name}</div>
        <div className="page-subtitle">Mein Konto</div>
        <div className="divider" />

        <div className="admin-grid" style={{ gridTemplateColumns: '1fr 1fr', marginBottom: '2rem' }}>
          <div className="stat-card">
            <div className="stat-number">{profil.punkte || 0}</div>
            <div className="stat-label">Treuepunkte</div>
          </div>
          <div className="stat-card">
            <div className="stat-number" style={{ fontSize: '1.2rem', paddingTop: '0.5rem' }}>{profil.status}</div>
            <div className="stat-label">Status</div>
          </div>
        </div>

        {/* Stempelkarten */}
        {(() => {
          const kaffeeGesamt = stempel.filter(s => s.typ === 'kaffee').length;
          const essenGesamt = stempel.filter(s => s.typ === 'essen').length;
          const kaffeePunkte = kaffeeGesamt % 10;
          const essenPunkte = essenGesamt % 10;
          const kaffeeGratis = Math.floor(kaffeeGesamt / 10);
          const essenGratis = Math.floor(essenGesamt / 10);
          return (
            <div className="card stempelkarte-card" style={{ marginBottom: '1.5rem' }}>
              <div style={{ fontSize: '0.65rem', letterSpacing: '2px', textTransform: 'uppercase', color: 'var(--gold)', marginBottom: '1.25rem', fontWeight: 700 }}>🎟 Treuekarten</div>

              <div className="stempelkarte">
                <div className="stempelkarte-titel">☕ Kaffee <span className="stempel-count">{kaffeePunkte}/10</span></div>
                <div className="stempel-reihe">
                  {Array.from({ length: 10 }, (_, i) => (
                    <div key={i} className={`stempel-punkt${i < kaffeePunkte ? ' gefuellt' : ''}`} />
                  ))}
                </div>
                {kaffeeGratis > 0 && <div className="stempel-reward">🎉 {kaffeeGratis}× Gratis-Kaffee verfügbar!</div>}
              </div>

              <div className="stempelkarte" style={{ marginTop: '1.25rem' }}>
                <div className="stempelkarte-titel">🍽️ Essen <span className="stempel-count">{essenPunkte}/10</span></div>
                <div className="stempel-reihe">
                  {Array.from({ length: 10 }, (_, i) => (
                    <div key={i} className={`stempel-punkt${i < essenPunkte ? ' gefuellt' : ''}`} />
                  ))}
                </div>
                {essenGratis > 0 && <div className="stempel-reward">🎉 {essenGratis}× Gratis-Dessert verfügbar!</div>}
              </div>

              <div style={{ marginTop: '1.25rem' }}>
                {!scannerAktiv ? (
                  <button className="btn-ghost" style={{ width: '100%' }} onClick={() => { setScanStatus(null); setScannerAktiv(true); }}>
                    📷 QR-Code scannen
                  </button>
                ) : (
                  <div>
                    <div id="savoca-qr-scanner" style={{ borderRadius: '8px', overflow: 'hidden' }}></div>
                    <button className="btn-ghost" style={{ marginTop: '0.75rem', width: '100%' }} onClick={() => setScannerAktiv(false)}>
                      Abbrechen
                    </button>
                  </div>
                )}
                {scanStatus && (
                  <div style={{ marginTop: '0.75rem', fontSize: '0.85rem', color: scanStatus.ok ? '#2d6a4f' : '#c0392b', fontWeight: 600 }}>
                    {scanStatus.msg}
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <div style={{ fontSize: '0.65rem', letterSpacing: '2px', textTransform: 'uppercase', color: 'var(--gray)' }}>Meine Daten</div>
            <button className="btn-ghost" style={{ fontSize: '0.75rem', padding: '0.3rem 0.8rem' }} onClick={() => {
              if (!profilEdit) setProfilForm({ name: profil.name || '', telefon: profil.telefon || '', geburtsdatum: profil.geburtsdatum || '' });
              setProfilEdit(e => !e);
              setProfilSaveStatus(null);
            }}>{profilEdit ? 'Abbrechen' : '✏️ Bearbeiten'}</button>
          </div>

          {!profilEdit ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', fontSize: '0.9rem' }}>
              <div><strong>Name:</strong> {profil.name}</div>
              <div><strong>E-Mail:</strong> {profil.email}</div>
              {profil.telefon && <div><strong>Telefon:</strong> {profil.telefon}</div>}
              {profil.geburtsdatum && <div><strong>Geburtstag:</strong> {new Date(profil.geburtsdatum + 'T12:00:00').toLocaleDateString('de-DE')}</div>}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <div className="form-group">
                <label>Name</label>
                <input value={profilForm.name} onChange={e => setProfilForm(f => ({ ...f, name: e.target.value }))} maxLength={50} />
              </div>
              <div className="form-group">
                <label>E-Mail</label>
                <input value={profil.email} disabled style={{ opacity: 0.5 }} title="E-Mail kann nicht geändert werden" />
              </div>
              <div className="form-group">
                <label>Telefon</label>
                <input value={profilForm.telefon} onChange={e => setProfilForm(f => ({ ...f, telefon: e.target.value }))} maxLength={20} placeholder="+49 221 ..." />
              </div>
              <div className="form-group">
                <label>Geburtstag</label>
                <input type="date" value={profilForm.geburtsdatum} onChange={e => setProfilForm(f => ({ ...f, geburtsdatum: e.target.value }))} />
              </div>
              {profilSaveStatus && <div style={{ fontSize: '0.82rem', color: profilSaveStatus === 'ok' ? 'green' : 'red' }}>{profilSaveStatus === 'ok' ? '✓ Gespeichert!' : 'Fehler beim Speichern.'}</div>}
              <button className="btn-dark" style={{ alignSelf: 'flex-start' }} onClick={async () => {
                if (!profilForm.name.trim()) return;
                const { error } = await supabase.from('mitglieder').update({ name: profilForm.name.trim(), telefon: profilForm.telefon, geburtsdatum: profilForm.geburtsdatum || null }).eq('user_id', user.id);
                if (!error) {
                  setProfil(p => ({ ...p, name: profilForm.name.trim(), telefon: profilForm.telefon, geburtsdatum: profilForm.geburtsdatum || null }));
                  setProfilSaveStatus('ok');
                  setTimeout(() => { setProfilEdit(false); setProfilSaveStatus(null); }, 1000);
                } else { setProfilSaveStatus('error'); }
              }}>💾 Speichern</button>
            </div>
          )}
        </div>

        {((profil.name||'').toLowerCase().includes('kautz') || (profil.email||'').toLowerCase() === 'kautz@web.de') && (
          <div className="card" style={{ marginBottom: '1.5rem', borderLeft: '3px solid #ff69b4', background: '#fff' }}>
            <div style={{ fontSize: '0.65rem', letterSpacing: '2px', textTransform: 'uppercase', color: '#ff69b4', marginBottom: '1rem', fontWeight: 700 }}>⭐ Pizza bestellen</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <div className="form-group">
                <label>Pizza wählen *</label>
                <select value={pizzaForm.pizza} onChange={e => setPizzaForm(f => ({ ...f, pizza: e.target.value }))}>
                  <option value="">-- Bitte wählen --</option>
                  <option value="Margherita">🍕 Margherita</option>
                  <option value="Salami">🍕 Salami</option>
                  <option value="Schinken">🍕 Schinken</option>
                </select>
              </div>
              <div className="form-group">
                <label>Datum *</label>
                <select value={pizzaForm.datum} onChange={e => setPizzaForm(f => ({ ...f, datum: e.target.value }))}>
                  <option value="">-- Bitte wählen --</option>
                  {pizzaTage.map(t => <option key={t.iso} value={t.iso}>{t.label}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Uhrzeit *</label>
                <select value={pizzaForm.uhrzeit} onChange={e => setPizzaForm(f => ({ ...f, uhrzeit: e.target.value }))}>
                  <option value="">-- Bitte wählen --</option>
                  {ZEITEN.map(z => <option key={z} value={z}>{z} Uhr</option>)}
                </select>
              </div>
              {pizzaStatus && <div style={{ fontSize: '0.82rem', color: pizzaStatus === 'ok' ? '#ff69b4' : 'red' }}>{pizzaStatus === 'ok' ? '⭐ Pizza bestellt!' : 'Fehler. Bitte nochmal versuchen.'}</div>}
              <button className="btn-dark" style={{ background: '#ff69b4', border: 'none', alignSelf: 'flex-start' }} onClick={async () => {
                if (!pizzaForm.pizza || !pizzaForm.datum || !pizzaForm.uhrzeit) { setPizzaStatus('error'); return; }
                try {
                  await addBestellung({ name: profil.name, telefon: profil.telefon || '', typ: 'Lieferung', artikel: JSON.stringify([{ name: pizzaForm.pizza, menge: 1, preis: 0 }]), gesamt: 0, datum: pizzaForm.datum, uhrzeit: pizzaForm.uhrzeit, status: 'neu' });
                  setPizzaStatus('ok');
                  setPizzaForm({ pizza: '', datum: '', uhrzeit: '' });
                  setTimeout(() => setPizzaStatus(null), 3000);
                } catch { setPizzaStatus('error'); }
              }}>🍕 Jetzt bestellen</button>
            </div>
          </div>
        )}

        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <div style={{ fontSize: '0.65rem', letterSpacing: '2px', textTransform: 'uppercase', color: 'var(--gray)', marginBottom: '0.75rem' }}>Meine Reservierungen</div>
          {notifErlaubt !== 'granted' && typeof Notification !== 'undefined' && (
            <button className="btn-ghost" style={{ fontSize: '0.8rem', marginBottom: '1rem', width: '100%' }} onClick={async () => {
              const result = await Notification.requestPermission();
              setNotifErlaubt(result);
            }}>🔔 Benachrichtigungen aktivieren</button>
          )}
          {notifErlaubt === 'granted' && (
            <div style={{ fontSize: '0.75rem', color: '#2d6a4f', marginBottom: '0.75rem' }}>✓ Benachrichtigungen aktiv</div>
          )}
          {meineRes.length === 0 && (
            <div style={{ fontSize: '0.85rem', color: 'var(--gray)' }}>Noch keine Reservierungen.</div>
          )}
          {meineRes.map(r => {
            const istZukunft = r.datum >= new Date().toISOString().slice(0, 10);
            const kannBearbeiten = istZukunft && r.status !== 'abgesagt';
            return (
              <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem 0', borderBottom: '1px solid var(--border)', fontSize: '0.85rem' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600 }}>{r.datum} · {r.uhrzeit} Uhr</div>
                  <div style={{ color: 'var(--gray)', fontSize: '0.75rem' }}>
                    {r.personen} Personen{r.tisch_id ? ` · Tisch ${r.tisch_id}` : ''}
                    {r.bemerkung ? ` · ${r.bemerkung}` : ''}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  {kannBearbeiten && (
                    <button className="btn-ghost" style={{ fontSize: '0.75rem', padding: '0.2rem 0.6rem' }} onClick={() => {
                      setEditRes(r);
                      setEditForm({ datum: r.datum, uhrzeit: r.uhrzeit, personen: r.personen || '', sitzbereich: r.sitzbereich || '', bemerkung: r.bemerkung || '' });
                      setEditStatus(null);
                    }}>✏️</button>
                  )}
                  <span className={`badge ${r.status === 'bestätigt' ? 'badge-confirmed' : r.status === 'abgesagt' ? 'badge-cancelled' : 'badge-pending'}`}>{r.status}</span>
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '3rem' }}>
          <button className="btn-dark" onClick={() => setPage('reservierung')}>Tisch reservieren</button>
          <button className="btn-ghost" onClick={handleLogout}>Ausloggen</button>
        </div>

        <div style={{ borderTop: '1px solid var(--border)', paddingTop: '2rem' }}>
          <div style={{ fontSize: '0.65rem', letterSpacing: '2px', textTransform: 'uppercase', color: 'var(--gray-light)', marginBottom: '0.75rem' }}>Gefahrenzone</div>
          <p style={{ fontSize: '0.82rem', color: 'var(--gray)', marginBottom: '1rem', lineHeight: '1.6' }}>
            Dein Konto und alle gespeicherten Daten werden unwiderruflich gelöscht.
          </p>
          <button className="btn-delete" onClick={async () => {
            if (!window.confirm('Konto wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.')) return;
            await supabase.from('mitglieder').delete().eq('user_id', user.id);
            await supabase.auth.signOut();
          }}>Konto löschen</button>
        </div>

        {editRes && (
          <div className="res-modal-overlay" onClick={() => setEditRes(null)}>
            <div className="res-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '440px' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1.5rem' }}>
                <div style={{ fontSize:'0.65rem', letterSpacing:'2px', textTransform:'uppercase', color:'var(--gold)', fontWeight:700 }}>Reservierung ändern</div>
                <button className="btn-ghost" style={{ fontSize:'0.8rem' }} onClick={() => setEditRes(null)}>✕</button>
              </div>
              <div style={{ display:'flex', flexDirection:'column', gap:'0.75rem' }}>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.75rem' }}>
                  <div className="form-group">
                    <label>Datum *</label>
                    <input type="date" value={editForm.datum} min={new Date().toISOString().slice(0,10)} onChange={e => setEditForm(f => ({ ...f, datum: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label>Uhrzeit *</label>
                    <input type="time" value={editForm.uhrzeit} onChange={e => setEditForm(f => ({ ...f, uhrzeit: e.target.value }))} />
                  </div>
                </div>
                <div className="form-group">
                  <label>Personen</label>
                  <input type="number" min="1" max="20" value={editForm.personen} onChange={e => setEditForm(f => ({ ...f, personen: e.target.value }))} />
                </div>
                {bereichGilt(editForm.datum) && <BereichWahl value={editForm.sitzbereich} onChange={v => setEditForm(f => ({ ...f, sitzbereich: v }))} datum={editForm.datum} />}
                <div className="form-group">
                  <label>Bemerkung</label>
                  <textarea value={editForm.bemerkung} onChange={e => setEditForm(f => ({ ...f, bemerkung: e.target.value }))} rows={2} placeholder="Sonderwünsche, Allergien..." style={{ resize:'vertical' }} />
                </div>
                {editStatus && <div style={{ color:'red', fontSize:'0.82rem' }}>{editStatus}</div>}
                <div style={{ display:'flex', gap:'0.75rem', marginTop:'0.5rem' }}>
                  <button className="btn-dark" onClick={async () => {
                    if (!editForm.datum || !editForm.uhrzeit) { setEditStatus('Datum und Uhrzeit sind Pflicht.'); return; }
                    try {
                      const kap = await pruefeKapazitaet(editForm.datum, editForm.uhrzeit, editForm.personen, bereichGilt(editForm.datum) ? editForm.sitzbereich : null, editRes.id);
                      if (!kap.erlaubt) { setEditStatus(kapazitaetsMeldung(kap)); return; }
                      await updateReservierung(editRes.id, { datum: editForm.datum, uhrzeit: editForm.uhrzeit, personen: editForm.personen, sitzbereich: bereichGilt(editForm.datum) ? (editForm.sitzbereich || null) : null, bemerkung: editForm.bemerkung, status: 'offen' });
                      try {
                        await fetch('/api/notify-reservation', { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ name: profil.name, email: profil.email, telefon: profil.telefon||'', datum: editForm.datum, uhrzeit: editForm.uhrzeit, personen: editForm.personen, sitzbereich: bereichGilt(editForm.datum) ? editForm.sitzbereich : '', notiz: `GEÄNDERT von Mitglied.${editForm.bemerkung ? ' Bemerkung: '+editForm.bemerkung : ''}` }) });
                      } catch {}
                      const res = await getReservierungenByEmail(profil.email);
                      setMeineRes(res);
                      setEditRes(null);
                    } catch (err) { setEditStatus(String(err?.message || '').includes('kapazitaet_ueberschritten') ? 'Dieser Bereich ist zu dieser Uhrzeit inzwischen leider ausgebucht. Bitte wählen Sie eine andere Uhrzeit.' : 'Fehler beim Speichern. Bitte nochmal versuchen.'); }
                  }}>💾 Änderung speichern</button>
                  <button className="btn-ghost" onClick={() => setEditRes(null)}>Abbrechen</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Nicht eingeloggt → Login / Register
  return (
    <div className="page">
      {punkte_popup && (
        <div className="punkte-popup-overlay" onClick={() => setPunktePopup(false)}>
          <div className="punkte-popup" onClick={e => e.stopPropagation()}>
            <div className="punkte-popup-icon">⭐</div>
            <div className="punkte-popup-title">Profitiere von unserem Punktesystem</div>
            <div className="punkte-popup-text">
              Sammle bei jedem Besuch Punkte und löse sie gegen exklusive Vorteile ein.<br />
              Registriere dich jetzt und starte direkt mit Bonuspunkten!
            </div>
            <button className="punkte-popup-btn" onClick={() => setPunktePopup(false)}>Jetzt registrieren →</button>
          </div>
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '1.5rem', marginBottom: '3rem' }}>
        {vorteile.map(v => (
          <div key={v.text} className={`card${v.comingSoon ? ' vorteil-coming-soon' : ''}`} style={{ textAlign: 'center', padding: '1.5rem 1rem', position: 'relative' }}>
            <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>{v.icon}</div>
            <div style={{ fontSize: '0.75rem', letterSpacing: '1px', textTransform: 'uppercase' }}>{v.text}</div>
            {v.comingSoon && <span className="coming-soon-badge">Coming Soon</span>}
          </div>
        ))}
      </div>

      <div style={{ maxWidth: '460px', marginBottom: '1.5rem' }}>
        <button className="nav-btn-reservieren" style={{ width: '100%', padding: '0.85rem', fontSize: '0.8rem', letterSpacing: '2px', borderRadius: '6px', marginBottom: '1.5rem' }} onClick={() => setPage('reservierung')}>
          Tisch reservieren
        </button>
        <div className="tabs">
          <button className={authTab === 'login' ? 'active' : ''} onClick={() => { setAuthTab('login'); setStatus(null); }}>Einloggen</button>
          <button className={authTab === 'register' ? 'active' : ''} onClick={() => { setAuthTab('register'); setStatus(null); }}>Registrieren</button>
        </div>

        {status && <div className={status.type === 'success' ? 'msg-success' : 'msg-error'}>{status.msg}</div>}

        {authTab === 'login' ? (
          !pwResetMode ? (
          <form onSubmit={handleLogin}>
            <div className="page-title" style={{ fontSize: '1.5rem', marginBottom: '0.3rem' }}>Einloggen</div>
            <div className="page-subtitle" style={{ marginBottom: '0.5rem' }}>Mitglieder-Login</div>
            <div className="divider" />
            <div className="form-group" style={{ marginBottom: '1rem' }}>
              <label>E-Mail</label>
              <input type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="email@beispiel.de" />
            </div>
            <div className="form-group" style={{ marginBottom: '0.75rem' }}>
              <label>Passwort</label>
              <input type="password" value={form.passwort} onChange={e => set('passwort', e.target.value)} placeholder="••••••••" />
            </div>
            <button type="button" className="link-btn" style={{ marginBottom: '1.5rem', display: 'block', fontSize: '0.82rem' }} onClick={() => { setPwResetMode(true); setPwResetEmail(form.email); setPwResetStatus(null); }}>
              Passwort vergessen?
            </button>
            <button type="submit" className="btn-primary">Einloggen</button>
          </form>
          ) : (
          <div>
            <div className="page-title" style={{ fontSize: '1.5rem', marginBottom: '0.3rem' }}>Passwort zurücksetzen</div>
            <div className="page-subtitle" style={{ marginBottom: '0.5rem' }}>Reset-Link per E-Mail</div>
            <div className="divider" />
            <div className="form-group" style={{ marginBottom: '1rem' }}>
              <label>E-Mail</label>
              <input type="email" value={pwResetEmail} onChange={e => setPwResetEmail(e.target.value)} placeholder="email@beispiel.de" autoFocus />
            </div>
            {pwResetStatus && (
              <div className={pwResetStatus.ok ? 'msg-success' : 'msg-error'} style={{ marginBottom: '1rem' }}>{pwResetStatus.msg}</div>
            )}
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button className="btn-primary" onClick={async () => {
                if (!pwResetEmail) return;
                const { error } = await supabase.auth.resetPasswordForEmail(pwResetEmail, { redirectTo: window.location.origin });
                if (error) { setPwResetStatus({ ok: false, msg: 'Fehler: ' + error.message }); }
                else { setPwResetStatus({ ok: true, msg: '✓ Reset-Link wurde gesendet. Bitte E-Mail prüfen.' }); }
              }}>Link senden</button>
              <button className="btn-ghost" onClick={() => { setPwResetMode(false); setPwResetStatus(null); }}>Zurück</button>
            </div>
          </div>
          )
        ) : (
          <form onSubmit={handleRegister}>
            <div className="page-title" style={{ fontSize: '1.5rem', marginBottom: '0.3rem' }}>Mitglied werden</div>
            <div className="page-subtitle" style={{ marginBottom: '0.5rem' }}>Jetzt registrieren</div>
            <div className="divider" />
            <div className="form-grid">
              <div className="form-group">
                <label>Name *</label>
                <input value={form.name} onChange={e => set('name', e.target.value)} placeholder="Ihr Name" maxLength={LIMITS.name} />
              </div>
              <div className="form-group">
                <label>E-Mail *</label>
                <input type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="email@beispiel.de" maxLength={LIMITS.email} />
              </div>
              <div className="form-group">
                <label>Passwort *</label>
                <input type="password" value={form.passwort} onChange={e => set('passwort', e.target.value)} placeholder="Min. 6 Zeichen" maxLength={LIMITS.passwort} />
              </div>
              <div className="form-group">
                <label>Passwort bestätigen *</label>
                <input type="password" value={form.passwort2} onChange={e => set('passwort2', e.target.value)} placeholder="Passwort wiederholen" maxLength={LIMITS.passwort} />
              </div>
              <div className="form-group">
                <label>Telefon</label>
                <input value={form.telefon} onChange={e => set('telefon', e.target.value)} placeholder="+49 221 ..." maxLength={LIMITS.telefon} />
              </div>
              <div className="form-group">
                <label>Geburtsdatum</label>
                <input type="date" value={form.geburtsdatum} onChange={e => set('geburtsdatum', e.target.value)} />
              </div>
            </div>
            {/* Honeypot — unsichtbar für Menschen, Bots füllen es aus */}
            <input type="text" value={form.honeypot} onChange={e => set('honeypot', e.target.value)} style={{ display: 'none' }} tabIndex="-1" autoComplete="off" />
            <label className="checkbox-label" style={{ marginTop: '0.5rem', marginBottom: '0.75rem' }}>
              <input
                type="checkbox"
                checked={form.datenschutz}
                onChange={e => set('datenschutz', e.target.checked)}
              />
              <span>Ich habe die <button type="button" className="link-btn" onClick={() => setPage('datenschutz')}>Datenschutzerklärung</button> gelesen und akzeptiere sie. *</span>
            </label>
            <label className="checkbox-label" style={{ marginBottom: '1.25rem' }}>
              <input
                type="checkbox"
                checked={form.newsletter}
                onChange={e => set('newsletter', e.target.checked)}
              />
              <span>Ich möchte Nachrichten, Angebote und Neuigkeiten von Savoca Köln per E-Mail erhalten. *</span>
            </label>
            <button type="submit" className="btn-primary">Jetzt Mitglied werden</button>
          </form>
        )}
      </div>
    </div>
  );
}

// ===== PERSONEN-SLIDER =====
function PersonenSlider({ value, onChange }) {
  const val = parseInt(value) || 1;
  const markierungen = [1, 5, 10, 20, 30, 40, 50, 60, 70, 80];
  return (
    <div style={{ padding: '0.5rem 0 0.25rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.6rem' }}>
        <span style={{ fontSize: '2.8rem', fontWeight: 800, color: '#b8963e', lineHeight: 1, minWidth: '3ch', textAlign: 'center', fontFamily: 'Playfair Display, Georgia, serif' }}>{val}</span>
        <span style={{ fontSize: '0.8rem', color: '#888', letterSpacing: '1px', textTransform: 'uppercase' }}>{val === 1 ? 'Person' : 'Personen'}</span>
      </div>
      <input
        type="range"
        min="1" max="80" step="1"
        value={val}
        onChange={e => onChange(e.target.value)}
        style={{ width: '100%', accentColor: '#b8963e', height: '6px', cursor: 'pointer' }}
      />
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.4rem' }}>
        {markierungen.map(m => (
          <button key={m} onClick={() => onChange(String(m))}
            style={{ background: 'none', border: 'none', padding: 0, fontSize: m <= 10 ? '0.75rem' : '0.68rem', color: val === m ? '#b8963e' : '#bbb', fontWeight: val === m ? 800 : 400, cursor: 'pointer', fontFamily: 'Raleway, sans-serif', minWidth: m <= 10 ? '1.4ch' : 'auto' }}>
            {m}
          </button>
        ))}
      </div>
    </div>
  );
}

// ===== ADMIN: NEUE RESERVIERUNG =====
function AdminNeuReservierung({ onSaved }) {
  const [offen, setOffen] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', telefon: '', datum: '', uhrzeit: '', personen: '', tisch_id: '', sitzbereich: '', bemerkung: '' });
  const [status, setStatus] = useState(null);
  const [sendMail, setSendMail] = useState(false);
  const [sitzwunsch, setSitzwunsch] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const tische = [
    ...Array.from({length:10}, (_,i) => ({ id:`I${i+1}`, label:`Tisch ${i+1} (Innen)` })),
    ...Array.from({length:10}, (_,i) => ({ id:`A${i+1}`, label:`Tisch ${i+1} (Außen)` })),
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.datum || !form.uhrzeit || !form.personen || (bereichGilt(form.datum) && !form.sitzbereich)) {
      setStatus({ type: 'error', msg: 'Name, Datum, Uhrzeit, Personenanzahl und Sitzbereich sind Pflicht.' });
      return;
    }
    try {
      const bereich = form.tisch_id.startsWith('A') ? 'außen' : 'innen';
      const bemerkungMitSitz = [sitzwunsch ? `Sitzwunsch: ${sitzwunsch}` : '', form.bemerkung].filter(Boolean).join(' · ');
      await addReservierung({ ...form, sitzbereich: form.sitzbereich || null, bemerkung: bemerkungMitSitz, bereich, status: 'bestätigt' });
      let mailOk = false;
      if (sendMail && form.email) {
        try {
          const res = await fetch('/api/confirm-reservation', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name: form.name, email: form.email, datum: form.datum, uhrzeit: form.uhrzeit, personen: form.personen, sitzbereich: form.sitzbereich || '' }) });
          mailOk = res.ok;
        } catch { mailOk = false; }
      }
      const mailHinweis = sendMail && form.email ? (mailOk ? ' Bestätigungsmail gesendet.' : ' ⚠️ Mail-Versand fehlgeschlagen.') : '';
      setStatus({ type: 'success', msg: `Reservierung für ${form.name} gespeichert.${mailHinweis}` });
      setForm({ name: '', email: '', telefon: '', datum: '', uhrzeit: '', personen: '', tisch_id: '', sitzbereich: '', bemerkung: '' });
      setSendMail(false);
      setSitzwunsch('');
      onSaved();
      setTimeout(() => { setOffen(false); setStatus(null); }, 1500);
    } catch { setStatus({ type: 'error', msg: 'Fehler beim Speichern.' }); }
  };

  return (
    <div className="admin-neu-res">
      <button className="btn-neu-res" onClick={() => { setOffen(o => !o); setStatus(null); }}>
        {offen ? '✕ Abbrechen' : '＋ Neue Reservierung'}
      </button>
      {offen && (
        <div className="admin-neu-form">
          <div style={{ fontSize: '0.65rem', letterSpacing: '2px', textTransform: 'uppercase', color: 'var(--gray)', marginBottom: '1.25rem' }}>Neue Reservierung anlegen</div>
          {status && <div className={status.type === 'success' ? 'msg-success' : 'msg-error'} style={{ marginBottom: '1rem' }}>{status.msg}</div>}
          <div className="form-grid">
            <div className="form-group"><label>Name *</label><input value={form.name} onChange={e => set('name', e.target.value)} placeholder="Gast Name" /></div>
            <div className="form-group"><label>E-Mail <span style={{color:'var(--gray)',fontWeight:'normal',fontSize:'0.8rem'}}>(optional)</span></label><input type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="email@beispiel.de" /></div>
            <div className="form-group"><label>Telefon <span style={{color:'var(--gray)',fontWeight:'normal',fontSize:'0.8rem'}}>(optional)</span></label><input value={form.telefon} onChange={e => set('telefon', e.target.value)} placeholder="+49 221 ..." /></div>
            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label>Personen *</label>
              <PersonenSlider value={form.personen || '1'} onChange={v => set('personen', v)} />
            </div>
            <div className="form-group"><label>Datum *</label><input type="date" value={form.datum} onChange={e => set('datum', e.target.value)} /></div>
            <div className="form-group"><label>Uhrzeit *</label>
              <select value={form.uhrzeit} onChange={e => set('uhrzeit', e.target.value)}>
                <option value="">-- Bitte wählen --</option>
                {ZEITEN.map(z => <option key={z} value={z}>{z} Uhr</option>)}
              </select>
            </div>
            <div className="form-group"><label>Sitzbereich *</label>
              <BereichSelect value={form.sitzbereich} onChange={v => set('sitzbereich', v)} datum={form.datum} />
            </div>
            <div className="form-group"><label>Tisch</label>
              <select value={form.tisch_id} onChange={e => set('tisch_id', e.target.value)}>
                <option value="">-- Kein Tisch --</option>
                {tische.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display:'flex', gap:'1rem', marginBottom:'1rem', alignItems:'center', flexWrap:'wrap' }}>
            <span style={{ fontSize:'0.75rem', letterSpacing:'1px', textTransform:'uppercase', color:'var(--gray)', marginRight:'0.5rem' }}>Sitzwunsch:</span>
            <label style={{ display:'flex', alignItems:'center', gap:'0.4rem', cursor:'pointer', fontSize:'0.9rem', color:'var(--text)', fontWeight: sitzwunsch==='Drinnen' ? '700' : 'normal' }}>
              <input type="checkbox" checked={sitzwunsch === 'Drinnen'} onChange={() => setSitzwunsch(s => s === 'Drinnen' ? '' : 'Drinnen')} />
              🏠 Drinnen
            </label>
            <label style={{ display:'flex', alignItems:'center', gap:'0.4rem', cursor:'pointer', fontSize:'0.9rem', color:'var(--text)', fontWeight: sitzwunsch==='Draußen' ? '700' : 'normal' }}>
              <input type="checkbox" checked={sitzwunsch === 'Draußen'} onChange={() => setSitzwunsch(s => s === 'Draußen' ? '' : 'Draußen')} />
              ☀️ Draußen
            </label>
          </div>
          <div className="form-group" style={{ marginBottom: '1.25rem' }}><label>Bemerkung</label><textarea value={form.bemerkung} onChange={e => set('bemerkung', e.target.value)} placeholder="Anlass, Wünsche..." /></div>
          <button className="btn-primary" onClick={handleSubmit}>Reservierung speichern</button>
        </div>
      )}
    </div>
  );
}

// ===== STATUS FARBEN (global) =====
const STATUS_FARBE = { ausstehend: 'badge-pending', bestätigt: 'badge-confirmed', abgesagt: 'badge-cancelled', neu: 'badge-new', fertig: 'badge-done' };

// ===== DRUCK-HILFSFUNKTION =====
function druckeReservierung(res) {
  const w = window.open('', '_blank');
  const datumFormatiert = res.datum
    ? new Date(res.datum + 'T12:00:00').toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' })
    : '—';
  const tischInfo = res.tisch_id
    ? `Tisch ${res.tisch_id}${res.bereich ? ' (' + res.bereich + ')' : ''}`
    : 'wird vom Team zugewiesen';
  w.document.write(`<!DOCTYPE html><html lang="de"><head>
    <meta charset="utf-8">
    <title>Reservierung · ${res.name}</title>
    <style>
      @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700&family=Raleway:wght@300;400;600&display=swap');
      * { margin:0; padding:0; box-sizing:border-box; }
      body { font-family:'Raleway',sans-serif; background:#fff; display:flex; justify-content:center; align-items:center; min-height:100vh; padding:2cm; }
      .zettel { border:2px solid #5C3768; border-radius:12px; padding:2.5rem 3rem; max-width:460px; width:100%; text-align:center; }
      .logo { font-family:'Playfair Display',serif; font-size:1.6rem; color:#5C3768; margin-bottom:0.2rem; }
      .sub { font-size:0.7rem; letter-spacing:3px; text-transform:uppercase; color:#aaa; margin-bottom:2rem; }
      .titel { font-family:'Playfair Display',serif; font-size:2.6rem; font-weight:700; color:#5C3768; letter-spacing:6px; text-transform:uppercase; margin-bottom:1.2rem; }
      .divider { width:50px; height:2px; background:#b8963e; margin:0 auto 1.8rem; }
      .zeilen { text-align:left; display:inline-block; width:100%; }
      .zeile { display:flex; gap:1rem; margin-bottom:0.55rem; font-size:0.93rem; align-items:baseline; }
      .zeile-label { color:#aaa; font-size:0.7rem; letter-spacing:1.5px; text-transform:uppercase; width:75px; flex-shrink:0; }
      .zeile-wert { color:#1a1a1a; font-weight:600; }
      .footer { margin-top:2rem; font-size:0.68rem; color:#ccc; letter-spacing:1px; }
      @media print { body { padding:0.5cm; } }
    </style>
  </head><body>
    <div class="zettel">
      <div class="logo">Savoca</div>
      <div class="sub">Bar · Caffè · Mercato</div>
      <div class="titel">Reserviert</div>
      <div class="divider"></div>
      <div class="zeilen">
        <div class="zeile"><span class="zeile-label">Name</span><span class="zeile-wert">${res.name || '—'}</span></div>
        <div class="zeile"><span class="zeile-label">Datum</span><span class="zeile-wert">${datumFormatiert}</span></div>
        <div class="zeile"><span class="zeile-label">Uhrzeit</span><span class="zeile-wert">${res.uhrzeit ? res.uhrzeit + ' Uhr' : '—'}</span></div>
        <div class="zeile"><span class="zeile-label">Personen</span><span class="zeile-wert">${res.personen || '—'}</span></div>
        ${bereichInfo(res.sitzbereich) ? `<div class="zeile"><span class="zeile-label">Bereich</span><span class="zeile-wert">${bereichInfo(res.sitzbereich).label}</span></div>` : ''}
        <div class="zeile"><span class="zeile-label">Tisch</span><span class="zeile-wert">${tischInfo}</span></div>
        ${res.bemerkung ? `<div class="zeile"><span class="zeile-label">Hinweis</span><span class="zeile-wert" style="font-weight:400;font-style:italic">${res.bemerkung}</span></div>` : ''}
      </div>
      <div class="footer">Savoca Köln · Gottesweg 139 · 50939 Köln</div>
    </div>
    <style>.no-print { position:fixed; top:12px; right:12px; background:#5C3768; color:#fff; border:none; border-radius:6px; padding:0.4rem 1rem; cursor:pointer; font-size:0.9rem; } @media print { .no-print { display:none; } }</style>
    <button class="no-print" onclick="window.close()">✕ Schließen</button>
    <script>window.onload = () => { window.print(); }; window.onafterprint = () => window.close();<\/script>
  </body></html>`);
  w.document.close();
}

// ===== RESERVIERUNGSBUCH =====
function MiniTischplan({ reservierungen, datum, uhrzeit, onSelect }) {
  const zonen = [
    { label: 'Innen', ids: ['1','2','3','4','5','6','7','8','9','10','11','12','13','14','15','16','18','TOUR'] },
    { label: 'Außen', ids: ['17','20','21','22','23','24','25','26','27','28','29','30','31','32','33','34'] },
  ];
  const getStatus = (id) => {
    const buchungen = (reservierungen || []).filter(r => r.tisch_id === id && r.datum === datum && r.status !== 'abgesagt');
    if (!buchungen.length) return { status: 'frei', freiAb: null };
    if (uhrzeit) {
      const [sh, sm] = uhrzeit.split(':').map(Number);
      const selMin = sh * 60 + sm;
      const selBlock = getBlockMin(uhrzeit);
      const treffer = buchungen.find(b => {
        const [bh, bm] = (b.uhrzeit || '0:0').split(':').map(Number);
        const bMin = bh * 60 + bm;
        const bBlock = b.dauer || getBlockMin(b.uhrzeit);
        return selMin < bMin + bBlock && bMin < selMin + selBlock;
      });
      if (treffer) {
        const [bh, bm] = treffer.uhrzeit.split(':').map(Number);
        const endMin = bh * 60 + bm + (treffer.dauer || getBlockMin(treffer.uhrzeit));
        const freiAb = `${String(Math.floor(endMin / 60)).padStart(2, '0')}:${String(endMin % 60).padStart(2, '0')}`;
        return { status: 'belegt', freiAb };
      }
    }
    return { status: 'gebucht', freiAb: null };
  };
  return (
    <div className="mini-tp">
      {zonen.map(({ label, ids }) => (
        <div key={label} className="mini-tp-bereich">
          <div className="mini-tp-label">{label}</div>
          <div className="mini-tp-row">
            {ids.map(id => {
              const { status, freiAb } = getStatus(id);
              return (
                <button key={id} type="button" className={`mini-tp-cell mini-tp-${status}`} onClick={() => onSelect(id)} title={freiAb ? `Frei ab ${freiAb}` : id}>
                  <span className="mini-tp-id">{id}</span>
                  {freiAb && <span className="mini-tp-frei-ab">{freiAb}</span>}
                </button>
              );
            })}
          </div>
        </div>
      ))}
      <div className="mini-tp-legende">
        <span className="mini-tp-frei">■ Frei</span>
        <span className="mini-tp-gebucht">■ Gebucht</span>
        <span className="mini-tp-belegt">■ Belegt</span>
      </div>
    </div>
  );
}

function TischplanModal({ reservierungen, datum, uhrzeit, aktuelleAuswahl, onSelect, onClose }) {
  const zonen = [
    { label: 'Innen', ids: ['1','2','3','4','5','6','7','8','9','10','11','12','13','14','15','16','18','TOUR'] },
    { label: 'Außen', ids: ['17','20','21','22','23','24','25','26','27','28','29','30','31','32','33','34'] },
  ];
  const [ausgewaehlt, setAusgewaehlt] = useState(() => parseTische(aktuelleAuswahl));

  const getStatus = (id) => {
    const buchungen = (reservierungen || []).filter(r => {
      const rTische = parseTische(r.tisch_id);
      return rTische.includes(id) && r.datum === datum && r.status !== 'abgesagt';
    });
    if (!buchungen.length) return { status: 'frei', freiAb: null };
    if (uhrzeit) {
      const [sh, sm] = uhrzeit.split(':').map(Number);
      const selMin = sh * 60 + sm;
      const selBlock = getBlockMin(uhrzeit);
      const treffer = buchungen.find(b => {
        const [bh, bm] = (b.uhrzeit || '0:0').split(':').map(Number);
        const bMin = bh * 60 + bm;
        const bBlock = b.dauer || getBlockMin(b.uhrzeit);
        return selMin < bMin + bBlock && bMin < selMin + selBlock;
      });
      if (treffer) {
        const [bh, bm] = treffer.uhrzeit.split(':').map(Number);
        const endMin = bh * 60 + bm + (treffer.dauer || getBlockMin(treffer.uhrzeit));
        const freiAb = `${String(Math.floor(endMin / 60)).padStart(2, '0')}:${String(endMin % 60).padStart(2, '0')}`;
        return { status: 'belegt', freiAb };
      }
    }
    return { status: 'gebucht', freiAb: null };
  };

  function toggleTisch(id) {
    setAusgewaehlt(prev =>
      prev.includes(id) ? prev.filter(t => t !== id) : [...prev, id]
    );
  }

  function uebernehmen() {
    onSelect(ausgewaehlt.join(','));
    onClose();
  }

  return (
    <div className="tp-modal-overlay" onClick={onClose}>
      <div className="tp-modal-box" onClick={e => e.stopPropagation()}>
        <div className="tp-modal-header">
          <span style={{ fontWeight: 700, fontSize: '1.1rem', color: '#b8963e' }}>🪑 Tische wählen</span>
          <button className="tp-modal-close" onClick={onClose}>✕</button>
        </div>
        {zonen.map(({ label, ids }) => (
          <div key={label} className="mini-tp-bereich" style={{ marginBottom: '1rem' }}>
            <div className="mini-tp-label">{label}</div>
            <div className="mini-tp-row" style={{ flexWrap: 'wrap' }}>
              {ids.map(id => {
                const { status, freiAb } = getStatus(id);
                const gewaehlt = ausgewaehlt.includes(id);
                return (
                  <button
                    key={id}
                    type="button"
                    className={`mini-tp-cell mini-tp-${status}${gewaehlt ? ' mini-tp-gewaehlt' : ''}`}
                    onClick={() => toggleTisch(id)}
                    title={freiAb ? `Frei ab ${freiAb}` : id}
                  >
                    <span className="mini-tp-id">{gewaehlt ? '✓' : id}</span>
                    {freiAb && !gewaehlt && <span className="mini-tp-frei-ab">{freiAb}</span>}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
        <div className="mini-tp-legende">
          <span className="mini-tp-frei">■ Frei</span>
          <span className="mini-tp-gebucht">■ Gebucht</span>
          <span className="mini-tp-belegt">■ Belegt</span>
          <span style={{ color: '#b8963e' }}>■ Ausgewählt</span>
        </div>
        {ausgewaehlt.length > 0 && (
          <div style={{ marginTop: '0.8rem', borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: '0.8rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem' }}>
            <span style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.6)' }}>
              Ausgewählt: <strong style={{ color: '#b8963e' }}>{ausgewaehlt.join(', ')}</strong>
            </span>
            <button
              type="button"
              onClick={uebernehmen}
              style={{ background: 'linear-gradient(135deg,#b8963e,#d4a843)', border: 'none', color: '#fff', padding: '0.4rem 1rem', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}
            >
              ✓ Übernehmen
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function ReservierungsBuch({ reservierungen, anfragen, onSaved, onDatumWaehlen, bestellungen = [], buchungsstoppFenster = {}, onToggleBuchungsstoppFenster }) {
  const heute = new Date();
  const [buchMonat, setBuchMonat] = useState(heute.getMonth());
  const [buchJahr, setBuchJahr] = useState(heute.getFullYear());
  const [gewTag, setGewTag] = useState(heute.getDate());
  const [blatternDir, setBlatternDir] = useState(null);
  const [ansicht, setAnsicht] = useState('monat');
  const [kalenderZu, setKalenderZu] = useState(false);
  const [neuFormOffen, setNeuFormOffen] = useState(false);
  const [neuForm, setNeuForm] = useState({ name: '', email: '', telefon: '', datum: '', uhrzeit: '', personen: '2', tisch_id: '', sitzbereich: '', bemerkung: '' });
  const [neuSitzwunsch, setNeuSitzwunsch] = useState('');
  const [miniTpOffenFuer, setMiniTpOffenFuer] = useState(null);
  const [tpModal, setTpModal] = useState(null);
  const [offeneKarte, setOffeneKarte] = useState(null);
  const [neuStatus, setNeuStatus] = useState(null);
  const [buchReplyModal, setBuchReplyModal] = useState(null);
  const [buchReplySendStatus, setBuchReplySendStatus] = useState(null);
  const [adminEditRes, setAdminEditRes] = useState(null);
  const [adminEditForm, setAdminEditForm] = useState({});
  const [adminEditStatus, setAdminEditStatus] = useState(null);
  const [adminHinweis, setAdminHinweis] = useState(null);   // kurze Rückmeldung nach dem Speichern (Änderungsmail verschickt / fehlgeschlagen)
  const [buchNotizPopup, setBuchNotizPopup] = useState(null);
  const setNF = (k, v) => setNeuForm(f => ({ ...f, [k]: v }));

  const saveAdminEdit = async () => {
    if (!adminEditForm.name || !adminEditForm.datum || !adminEditForm.uhrzeit) {
      setAdminEditStatus('Name, Datum und Uhrzeit sind Pflicht.');
      return;
    }
    try {
      // Hat sich für den Gast etwas Wichtiges geändert? (Datum, Uhrzeit, Personen, Bereich) – Namen/Telefon/Bemerkung gehen den Gast nichts an
      const hhmm = v => String(v || '').slice(0, 5);
      const vorher = adminEditRes;
      const gastBetroffen = vorher.datum !== adminEditForm.datum || hhmm(vorher.uhrzeit) !== hhmm(adminEditForm.uhrzeit)
        || (parseInt(vorher.personen, 10) || 0) !== (parseInt(adminEditForm.personen, 10) || 0)
        || (vorher.sitzbereich || '') !== (adminEditForm.sitzbereich || '');
      await updateReservierung(adminEditRes.id, {
        name: adminEditForm.name,
        email: adminEditForm.email,
        telefon: adminEditForm.telefon,
        datum: adminEditForm.datum,
        uhrzeit: adminEditForm.uhrzeit,
        personen: adminEditForm.personen,
        sitzbereich: adminEditForm.sitzbereich || null,
        bemerkung: adminEditForm.bemerkung,
      });
      // Änderungsmail an den Gast: erst NACH dem Speichern; schlägt der Versand fehl, bleibt die Änderung gespeichert und der Admin wird gewarnt
      let hinweis = null;
      const mailAdresse = (adminEditForm.email || '').trim();
      if (gastBetroffen && vorher.status !== 'abgesagt') {
        if (!mailAdresse.includes('@')) {
          hinweis = { ok: false, text: 'Gespeichert – der Gast hat keine E-Mail-Adresse und wurde NICHT informiert.' };
        } else {
          try {
            const res = await fetch('/api/change-reservation', { method: 'POST', headers: { 'Content-Type': 'application/json', ...(await adminHeaders()) },
              body: JSON.stringify({ name: adminEditForm.name, email: mailAdresse, datum: adminEditForm.datum, uhrzeit: hhmm(adminEditForm.uhrzeit), personen: adminEditForm.personen || '',
                sitzbereich: adminEditForm.sitzbereich || '', alt: { datum: vorher.datum, uhrzeit: hhmm(vorher.uhrzeit), personen: vorher.personen || '', sitzbereich: vorher.sitzbereich || '' } }) });
            hinweis = res.ok ? { ok: true, text: `✉️ Änderungsmail an ${mailAdresse} verschickt.` } : { ok: false, text: '⚠️ Gespeichert, aber die Änderungsmail konnte NICHT verschickt werden – bitte den Gast anrufen.' };
          } catch { hinweis = { ok: false, text: '⚠️ Gespeichert, aber die Änderungsmail konnte NICHT verschickt werden – bitte den Gast anrufen.' }; }
        }
      }
      setAdminEditRes(null);
      if (hinweis) { setAdminHinweis(hinweis); setTimeout(() => setAdminHinweis(null), hinweis.ok ? 5000 : 12000); }
      if (onSaved) onSaved();
    } catch { setAdminEditStatus('Fehler beim Speichern.'); }
  };

  const tische = [
    ...Array.from({length:10}, (_,i) => ({ id:`I${i+1}`, label:`Tisch ${i+1} (Innen)` })),
    ...Array.from({length:10}, (_,i) => ({ id:`A${i+1}`, label:`Tisch ${i+1} (Außen)` })),
  ];

  const handleNeuSpeichern = async () => {
    const datum = neuForm.datum || datumStr || '';
    if (!neuForm.name || !neuForm.uhrzeit || (bereichGilt(datum) && !neuForm.sitzbereich)) {
      setNeuStatus({ type: 'error', msg: 'Name, Uhrzeit und Sitzbereich sind Pflicht.' });
      return;
    }
    const bereich = neuForm.tisch_id ? (neuForm.tisch_id.startsWith('A') ? 'außen' : 'innen') : 'innen';
    const bemerkungMitSitz = [neuSitzwunsch ? `Sitzwunsch: ${neuSitzwunsch}` : '', neuForm.bemerkung].filter(Boolean).join(' · ');
    try {
      await addReservierung({ ...neuForm, sitzbereich: neuForm.sitzbereich || null, bemerkung: bemerkungMitSitz, datum, bereich, status: 'bestätigt' });
      if (neuForm.email) {
        fetch('/api/confirm-reservation', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name: neuForm.name, email: neuForm.email, datum, uhrzeit: neuForm.uhrzeit, personen: neuForm.personen, sitzbereich: neuForm.sitzbereich || '' }) }).catch(() => {});
      }
      setNeuForm({ name: '', email: '', telefon: '', datum: '', uhrzeit: '', personen: '2', tisch_id: '', sitzbereich: '', bemerkung: '' });
      setNeuSitzwunsch('');
      if (onSaved) onSaved();
      setNeuFormOffen(false);
      setNeuStatus(null);
    } catch { setNeuStatus({ type: 'error', msg: 'Fehler beim Speichern.' }); }
  };

  const blattern = (dir, fn) => {
    setBlatternDir(dir);
    setTimeout(() => { fn(); setGewTag(null); setBlatternDir(null); }, 200);
  };

  const monatNamen = ['Januar','Februar','März','April','Mai','Juni','Juli','August','September','Oktober','November','Dezember'];
  const tageImMonat = new Date(buchJahr, buchMonat + 1, 0).getDate();
  const ersterWochentag = (new Date(buchJahr, buchMonat, 1).getDay() + 6) % 7;

  // Bestellungen im Reservierungsbuch: nur Status „neu“; Kautz-Bestellungen bleiben als rosa Erinnerungszeile oben
  const istKautzBestellung = (b) => (b.name||'').toLowerCase().includes('kautz') || (b.email||'').toLowerCase() === 'kautz@web.de';
  const bestellText = (b) => {
    try { const a = JSON.parse(b.artikel); if (Array.isArray(a)) return a.map(x => `${x.menge > 1 ? x.menge + '× ' : ''}${x.name}`).join(', '); } catch {}
    return b.artikel || '';
  };
  const alle = [
    ...reservierungen.map(r => ({ ...r, _typ: 'tisch' })),
    ...anfragen.map(a => ({ ...a, _typ: a.typ })),
    ...bestellungen.filter(b => b.status === 'neu' && !istKautzBestellung(b)).map(b => ({ ...b, _typ: 'bestellung', _bestellung: true, bemerkung: bestellText(b) })),
  ];

  const fuerTag = (tag) => {
    const d = `${buchJahr}-${String(buchMonat+1).padStart(2,'0')}-${String(tag).padStart(2,'0')}`;
    return alle.filter(e => e.datum === d).sort((a, b) => (a.uhrzeit || '').localeCompare(b.uhrzeit || ''));
  };

  const typFarbe = { tisch: '#5C3768', kindergeburtstag: '#2196F3', catering: '#4CAF50', event: '#FF9800', eisgeburtstag: '#00BCD4', brunch: '#d4a843', bestellung: '#2e7d32' };
  const typLabel = { tisch: 'Tisch', kindergeburtstag: '🎂', catering: '🍾', event: '🎉', eisgeburtstag: '🍦', brunch: '🥂', bestellung: '🛒 Bestellung' };
  const anfragTypVoll = { brunch: '🥂 Herbstbrunch', eisgeburtstag: '🍦 Eisgeburtstag', kindergeburtstag: '🎂 Kindergeburtstag', catering: '🍾 Catering', event: '🎉 Event', bestellung: '🛒 Bestellung' };

  const prevMonat = () => blattern('links', () => {
    if (buchMonat === 0) { setBuchMonat(11); setBuchJahr(y => y - 1); }
    else setBuchMonat(m => m - 1);
  });
  const nextMonat = () => blattern('rechts', () => {
    if (buchMonat === 11) { setBuchMonat(0); setBuchJahr(y => y + 1); }
    else setBuchMonat(m => m + 1);
  });

  const tageList = gewTag ? fuerTag(gewTag) : [];
  const datumStr = gewTag ? `${buchJahr}-${String(buchMonat+1).padStart(2,'0')}-${String(gewTag).padStart(2,'0')}` : null;

  // Auslastung je Bereich und Zeitfenster: Summe aller Buchungen, die im Fenster ANFANGEN (so zählt auch die Kundenseite)
  // Personen, die im Fenster sitzen (Buchung überschneidet das Fenster) + höchste Zahl, die dabei gleichzeitig da ist
  const auslastungBereich = (bereichKey, fenster) => {
    const vonF = minutenVon(fenster.start), bisF = minutenVon(fenster.ende);
    const zeilen = tageList.filter(e => e._typ === 'tisch' && e.status !== 'abgesagt' && e.uhrzeit && e.sitzbereich === bereichKey);
    const imFenster = zeilen.filter(e => minutenVon(e.uhrzeit) < bisF && minutenVon(e.uhrzeit) + dauerVon(e) > vonF);
    return { summe: imFenster.reduce((a, e) => a + (parseInt(e.personen, 10) || 0), 0), spitze: spitzeImBereich(zeilen, bereichKey, vonF, bisF) };
  };
  const auslastungFarbe = (anzahl, max) => {
    const pct = (anzahl / max) * 100;
    if (pct >= 90) return '#d03b3b';
    if (pct >= 70) return '#e3ab4a';
    if (anzahl <= 0) return '#ece5d5';
    return '#8fbf7a';
  };
  const alteRegel = !!datumStr && !bereichGilt(datumStr);   // Tage vor dem Stichtag: alte Balken (Personen je Uhrzeit, max. 50)
  const auslastungZeit = (uhrzeit) => {
    // alte Regel (Winter): Summe aller Buchungen, die im selben Zeitfenster anfangen
    const fenster = findeZeitfenster(uhrzeit);
    return tageList.reduce((summe, e) => {
      if (e._typ !== 'tisch' || e.status === 'abgesagt' || !e.uhrzeit) return summe;
      const f = findeZeitfenster(e.uhrzeit);
      return (fenster && f && f.start === fenster.start) ? summe + (parseInt(e.personen, 10) || 0) : summe;
    }, 0);
  };
  const ohneBereich = alteRegel ? 0 : tageList.filter(e => e._typ === 'tisch' && e.status !== 'abgesagt' && !e.sitzbereich).length;
  const gesperrteFensterHeute = datumStr ? (buchungsstoppFenster[datumStr] || []) : [];

  const druckeTagesansicht = () => {
    const w = window.open('', '_blank');
    const statusLabel = s => ({ 'bestätigt': 'Bestätigt', 'abgesagt': 'Abgesagt', 'ausstehend': 'Ausstehend' }[s] || s);
    // Personen je Bereich (ohne Absagen) – steht unter der Überschrift, damit man morgens sofort sieht, wo wie viel los ist
    const personenJeBereich = {};
    tageList.filter(e => e._typ === 'tisch' && e.status !== 'abgesagt').forEach(e => {
      const k = bereichInfo(e.sitzbereich)?.label || 'ohne Bereich';
      personenJeBereich[k] = (personenJeBereich[k] || 0) + (parseInt(e.personen, 10) || 0);
    });
    const summenJeBereich = Object.keys(personenJeBereich).length
      ? ' · ' + [...BEREICHE.map(b => b.label), 'ohne Bereich'].filter(k => personenJeBereich[k]).map(k => `${k}: ${personenJeBereich[k]} Pers.`).join(' · ')
      : '';
    const rows = tageList.map(e => `
      <tr>
        <td>${e.uhrzeit || '—'} Uhr</td>
        <td><strong>${e.name || '—'}</strong></td>
        <td style="text-align:center">${e.personen || '—'}</td>
        <td>${bereichInfo(e.sitzbereich) ? `<strong class="bereich">${bereichInfo(e.sitzbereich).label}</strong>` : '—'}</td>
        <td>${e.tisch_id ? `Tisch ${e.tisch_id}` : '—'}</td>
        <td><span class="badge badge-${e.status}">${statusLabel(e.status)}</span></td>
        <td style="color:#666;font-size:0.85em">${e.email || ''}<br>${e.telefon || ''}</td>
        <td style="font-size:0.82em;color:#555">${e.bemerkung || ''}</td>
      </tr>`).join('');
    w.document.write(`<!DOCTYPE html><html lang="de"><head>
      <meta charset="utf-8">
      <title>Savoca · ${wochentag}, ${tagStr}</title>
      <style>
        body { font-family: 'Georgia', serif; color: #1a1a1a; padding: 2cm; }
        h1 { font-size: 1.6rem; margin: 0 0 0.2rem; }
        .sub { font-size: 0.85rem; color: #666; letter-spacing: 2px; text-transform: uppercase; margin-bottom: 1.5rem; }
        hr { border: none; border-top: 2px solid #5C3768; margin-bottom: 1.5rem; }
        table { width: 100%; border-collapse: collapse; font-size: 0.92rem; }
        th { text-align: left; font-size: 0.7rem; letter-spacing: 1.5px; text-transform: uppercase; color: #888; padding: 0.4rem 0.6rem; border-bottom: 1px solid #ddd; }
        td { padding: 0.55rem 0.6rem; border-bottom: 1px solid #eee; vertical-align: top; }
        tr:last-child td { border-bottom: none; }
        .badge { padding: 0.15rem 0.5rem; border-radius: 4px; font-size: 0.75rem; font-weight: 600; }
        .bereich { display: inline-block; border: 1.5px solid #5C3768; border-radius: 4px; padding: 0.1rem 0.45rem; font-size: 0.82rem; color: #5C3768; white-space: nowrap; }
        .badge-bestätigt { background:#d4edda; color:#155724; }
        .badge-ausstehend { background:#fff3cd; color:#856404; }
        .badge-abgesagt { background:#f8d7da; color:#721c24; }
        footer { margin-top: 2rem; font-size: 0.75rem; color: #aaa; text-align: center; }
        @media print { body { padding: 1cm; } }
      </style>
    </head><body>
      <h1>Savoca Köln — ${wochentag}, ${tagStr}</h1>
      <div class="sub">${tageList.length} Reservierung${tageList.length !== 1 ? 'en' : ''}${summenJeBereich}</div>
      <hr>
      <table>
        <thead><tr>
          <th>Uhrzeit</th><th>Name</th><th>Pers.</th><th>Bereich</th><th>Tisch</th><th>Status</th><th>Kontakt</th><th>Bemerkung</th>
        </tr></thead>
        <tbody>${rows || '<tr><td colspan="8" style="color:#999;text-align:center;padding:1.5rem">Keine Einträge</td></tr>'}</tbody>
      </table>
      <footer>Savoca Köln · Gottesweg 139, 50939 Köln · gedruckt am ${new Date().toLocaleDateString('de-DE')}</footer>
      <style>.no-print { position:fixed; top:12px; right:12px; background:#5C3768; color:#fff; border:none; border-radius:6px; padding:0.4rem 1rem; cursor:pointer; font-size:0.9rem; } @media print { .no-print { display:none; } }</style>
      <button class="no-print" onclick="window.close()">✕ Schließen</button>
      <script>window.onload = () => { window.print(); }; window.onafterprint = () => window.close();<\/script>
    </body></html>`);
    w.document.close();
  };

  const druckeEinzelbelege = () => {
    const bestaetigt = tageList.filter(e => e.status !== 'abgesagt');
    const w = window.open('', '_blank');
    const belege = bestaetigt.map((e, i) => {
      const tischInfo = e.tisch_id ? `Tisch ${e.tisch_id}${e.bereich ? ' (' + e.bereich + ')' : ''}` : 'wird vom Team zugewiesen';
      return `
      <div class="seite${i < bestaetigt.length - 1 ? ' pagebreak' : ''}">
        <div class="zettel">
          <div class="logo">Savoca</div>
          <div class="sub">Bar · Caffè · Mercato</div>
          <div class="titel">Reserviert</div>
          <div class="divider"></div>
          <div class="zeilen">
            <div class="zeile"><span class="zeile-label">Name</span><span class="zeile-wert">${e.name || '—'}</span></div>
            <div class="zeile"><span class="zeile-label">Datum</span><span class="zeile-wert">${wochentag}, ${tagStr}</span></div>
            <div class="zeile"><span class="zeile-label">Uhrzeit</span><span class="zeile-wert">${e.uhrzeit ? e.uhrzeit + ' Uhr' : '—'}</span></div>
            <div class="zeile"><span class="zeile-label">Personen</span><span class="zeile-wert">${e.personen || '—'}</span></div>
            ${bereichInfo(e.sitzbereich) ? `<div class="zeile"><span class="zeile-label">Bereich</span><span class="zeile-wert">${bereichInfo(e.sitzbereich).label}</span></div>` : ''}
            <div class="zeile"><span class="zeile-label">Tisch</span><span class="zeile-wert">${tischInfo}</span></div>
            ${e.bemerkung ? `<div class="zeile"><span class="zeile-label">Hinweis</span><span class="zeile-wert" style="font-weight:400;font-style:italic">${e.bemerkung}</span></div>` : ''}
          </div>
          <div class="footer">Savoca Köln · Gottesweg 139 · 50939 Köln</div>
        </div>
      </div>`;
    }).join('');
    w.document.write(`<!DOCTYPE html><html lang="de"><head>
      <meta charset="utf-8">
      <title>Savoca · Belege · ${tagStr}</title>
      <style>
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700&family=Raleway:wght@300;400;600&display=swap');
        * { margin:0; padding:0; box-sizing:border-box; }
        body { font-family:'Raleway',sans-serif; background:#fff; }
        .seite { display:flex; justify-content:center; align-items:center; min-height:100vh; padding:2cm; }
        .pagebreak { page-break-after:always; }
        .zettel { border:2px solid #5C3768; border-radius:12px; padding:2.5rem 3rem; max-width:460px; width:100%; text-align:center; }
        .logo { font-family:'Playfair Display',serif; font-size:1.6rem; color:#5C3768; margin-bottom:0.2rem; }
        .sub { font-size:0.7rem; letter-spacing:3px; text-transform:uppercase; color:#aaa; margin-bottom:2rem; }
        .titel { font-family:'Playfair Display',serif; font-size:2.6rem; font-weight:700; color:#5C3768; letter-spacing:6px; text-transform:uppercase; margin-bottom:1.2rem; }
        .divider { width:50px; height:2px; background:#b8963e; margin:0 auto 1.8rem; }
        .zeilen { text-align:left; display:inline-block; width:100%; }
        .zeile { display:flex; gap:1rem; margin-bottom:0.55rem; font-size:0.93rem; align-items:baseline; }
        .zeile-label { color:#aaa; font-size:0.7rem; letter-spacing:1.5px; text-transform:uppercase; width:75px; flex-shrink:0; }
        .zeile-wert { color:#1a1a1a; font-weight:600; }
        .footer { margin-top:2rem; font-size:0.68rem; color:#ccc; letter-spacing:1px; }
        .no-print { position:fixed; top:12px; right:12px; background:#5C3768; color:#fff; border:none; border-radius:6px; padding:0.4rem 1rem; cursor:pointer; font-size:0.9rem; }
        @media print { .no-print { display:none; } .seite { padding:0.5cm; } }
      </style>
    </head><body>
      ${bestaetigt.length === 0 ? '<div style="padding:3cm;font-family:Raleway,sans-serif;color:#888;text-align:center;">Keine bestätigten Reservierungen für diesen Tag.</div>' : belege}
      <button class="no-print" onclick="window.close()">✕ Schließen</button>
      <script>window.onload = () => { window.print(); }; window.onafterprint = () => window.close();<\/script>
    </body></html>`);
    w.document.close();
  };

  const kautzBestellungen = datumStr ? bestellungen.filter(b => b.datum === datumStr && ((b.name||'').toLowerCase().includes('kautz') || (b.email||'').toLowerCase() === 'kautz@web.de')) : [];
  const wochentage = ['Montag','Dienstag','Mittwoch','Donnerstag','Freitag','Samstag','Sonntag'];
  const gewDatum = gewTag ? new Date(buchJahr, buchMonat, gewTag) : null;
  const wochentag = gewDatum ? wochentage[(gewDatum.getDay() + 6) % 7] : '';
  const tagStr = gewTag ? `${String(gewTag).padStart(2,'0')}.${String(buchMonat+1).padStart(2,'0')}.${buchJahr}` : '';

  return (
    <div className="buch-open-wrap">
      <div className={`buch-open${kalenderZu ? ' buch-open-zu' : ''}`}>

        {/* ── Linke Seite: Kalender ── */}
        <div className="buch-seite buch-seite-links">
          <div className="buch-ruecken">
            <div className="buch-spine-deco" />
            <div className="buch-spine-title">Savoca</div>
            <div className="buch-spine-deco" />
            <div className="buch-spine-sub">Reservierungen</div>
            <div className="buch-spine-deco" />
          </div>
          <div className="buch-mini" onClick={() => setKalenderZu(false)} title="Kalender wieder aufklappen">
            <span className="buch-mini-icon">📅</span>
            <span className="buch-mini-heute">{tageList.length} heute</span>
            <span className="buch-mini-monat">{monatNamen[buchMonat]} {buchJahr}</span>
          </div>
          <div className={`buch-seite-inhalt${blatternDir ? ` blattern-${blatternDir}` : ''}`}>

            {/* Ansicht-Umschalter */}
            <div className="buch-ansicht-tabs">
              <button className={ansicht === 'monat' ? 'aktiv' : ''} onClick={() => { setAnsicht('monat'); setGewTag(null); }}>Monat</button>
              <button className={ansicht === 'jahr' ? 'aktiv' : ''} onClick={() => { setAnsicht('jahr'); setGewTag(null); }}>Jahr</button>
            </div>

            {/* ── MONATSANSICHT ── */}
            {ansicht === 'monat' && <>
              <div className="buch-header">
                <button className="buch-nav" onClick={prevMonat}>‹</button>
                <div className="buch-monat">{monatNamen[buchMonat]} {buchJahr}</div>
                <button className="buch-nav" onClick={nextMonat}>›</button>
              </div>
              <div className="buch-wochentage">
                {['Mo','Di','Mi','Do','Fr','Sa','So'].map(w => <div key={w}>{w}</div>)}
              </div>
              <div className="buch-grid">
                {Array.from({length: ersterWochentag}).map((_,i) => <div key={`e${i}`} />)}
                {Array.from({length: tageImMonat}, (_,i) => {
                  const tag = i + 1;
                  const eintraege = fuerTag(tag);
                  const istHeute = tag === heute.getDate() && buchMonat === heute.getMonth() && buchJahr === heute.getFullYear();
                  const istGew = tag === gewTag;
                  return (
                    <div key={tag} className={`buch-tag${istHeute ? ' heute' : ''}${istGew ? ' aktiv' : ''}`} onClick={() => { const newTag = istGew ? null : tag; setGewTag(newTag); if (newTag && onDatumWaehlen) { const d = `${buchJahr}-${String(buchMonat+1).padStart(2,'0')}-${String(newTag).padStart(2,'0')}`; onDatumWaehlen(d); } }}>
                      <span className="buch-tagzahl">{tag}</span>
                      <div className="buch-dots">
                        {eintraege.slice(0,3).map((e,idx) => (
                          <span key={idx} className={`buch-dot${e.status === 'abgesagt' ? ' abgesagt' : e.status !== 'bestätigt' ? ' offen' : ''}`} style={{ background: typFarbe[e._typ] || '#999' }} title={`${typLabel[e._typ]} · ${e.name}`} />
                        ))}
                        {eintraege.length > 3 && <span className="buch-mehr">+{eintraege.length-3}</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="buch-legende">
                {Object.entries(typFarbe).map(([typ, farbe]) => (
                  <span key={typ} className="buch-leg-item">
                    <span className="buch-leg-dot" style={{ background: farbe }} />
                    {typLabel[typ]}
                  </span>
                ))}
                <span className="buch-leg-item"><span className="buch-leg-dot offen" />Ausstehend</span>
              </div>
            </>}

            {/* ── JAHRESANSICHT ── */}
            {ansicht === 'jahr' && <>
              <div className="buch-header">
                <button className="buch-nav" onClick={() => { setBuchJahr(y => y - 1); setGewTag(null); }}>‹</button>
                <div className="buch-monat">{buchJahr}</div>
                <button className="buch-nav" onClick={() => { setBuchJahr(y => y + 1); setGewTag(null); }}>›</button>
              </div>
              <div className="buch-jahr-grid">
                {monatNamen.map((name, mi) => {
                  const anzahl = alle.filter(e => {
                    const d = e.datum || '';
                    return d.startsWith(`${buchJahr}-${String(mi+1).padStart(2,'0')}`);
                  }).length;
                  const istAktiv = mi === buchMonat;
                  return (
                    <div key={mi} className={`buch-jahr-zelle${istAktiv ? ' aktiv' : ''}${anzahl > 0 ? ' hat-buchungen' : ''}`}
                      onClick={() => { setBuchMonat(mi); setAnsicht('monat'); setGewTag(null); }}>
                      <div className="buch-jahr-name">{name.slice(0,3)}</div>
                      {anzahl > 0 && <div className="buch-jahr-anzahl">{anzahl}</div>}
                    </div>
                  );
                })}
              </div>
            </>}

            <div className="buch-pg-nr">1</div>
          </div>
        </div>

        {/* ── Mittelbindung ── */}
        <div className="buch-bindung">
          <button type="button" className="buch-klapp-btn" onClick={() => setKalenderZu(z => !z)} title={kalenderZu ? 'Kalender aufklappen' : 'Kalender einklappen'} aria-label={kalenderZu ? 'Kalender aufklappen' : 'Kalender einklappen'}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="m9 5 7 7-7 7" /></svg>
          </button>
        </div>

        {/* ── Rechte Seite: Details + Verwaltung ── */}
        <div className="buch-seite buch-seite-rechts">
          <div className="buch-lesezeichen" />
          {gewTag ? (
            <div className="buch-detail-seite">
              <div className="buch-detail-wochentag">{wochentag}</div>
              <div className="buch-detail-datum-row">
                <div className="buch-detail-datum">{tagStr} · {tageList.length} Einträge</div>
                <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                  <button className="buch-plus-btn" title="Tagesübersicht drucken" onClick={druckeTagesansicht} style={{ fontSize: '1rem' }}>🖨️</button>
                  <button className="buch-plus-btn" title="Einzelbelege drucken" onClick={druckeEinzelbelege} style={{ fontSize: '1rem' }}>📄</button>
                  <button className="buch-plus-btn-haupt" title="Neue Reservierung anlegen" onClick={() => { setNeuFormOffen(true); setNeuStatus(null); setNF('datum', datumStr || ''); }}>
                    ＋
                  </button>
                </div>
              </div>

              {/* ── Auslastungs-Sparklines + Zeitfenster-Sperren ── */}
              {[
                { titel: 'Vormittag · 90-Min-Fenster', fenster: ZEITFENSTER.slice(0, 4) },
                { titel: 'Nachmittag/Abend · 120-Min-Fenster', fenster: ZEITFENSTER.slice(4) },
              ].map(({ titel, fenster }) => (
                <div className="buch-spark-block" key={titel}>
                  <div className="buch-spark-head">
                    <span className="buch-spark-title">{titel}</span>
                    <span className="buch-spark-hint">{alteRegel ? 'Balken = Personen je Zeitfenster (alte Regel, bis 11.10.)' : 'Zahl = wer im Fenster sitzt · Höhe/Farbe = höchstens gleichzeitig'}</span>
                  </div>
                  {alteRegel && (
                    <div className="buch-spark">
                      {fenster.map(f => {
                        const zeitenImFenster = ZEITEN.filter(z => minutenVon(z) >= minutenVon(f.start) && minutenVon(z) < minutenVon(f.ende));
                        const gesperrt = gesperrteFensterHeute.includes(f.start);
                        return (
                          <div className={`buch-spark-group${gesperrt ? ' zu' : ''}`} key={f.start}>
                            {zeitenImFenster.map(z => {
                              const anzahl = auslastungZeit(z);
                              const pct = Math.min(Math.max((anzahl / MAX_PERSONEN_ALT) * 100, anzahl > 0 ? 6 : 2), 100);
                              return (
                                <div key={z} className="buch-spark-bar" style={{ height: `${pct}%`, background: auslastungFarbe(anzahl, MAX_PERSONEN_ALT) }}>
                                  {anzahl > 0 && <span className="val">{anzahl}</span>}
                                  <span className="tip">Fenster ab {findeZeitfenster(z)?.start || z} Uhr · {anzahl}/{MAX_PERSONEN_ALT} Personen</span>
                                </div>
                              );
                            })}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {!alteRegel && bereicheAm(datumStr).map(ber => (
                    <div className="buch-spark-zeile" key={ber.key}>
                      <span className="buch-spark-label">{ber.label}<small>max. {ber.max}</small></span>
                      <div className="buch-spark">
                        {fenster.map(f => {
                          const gesperrt = gesperrteFensterHeute.includes(f.start);
                          const { summe: anzahl, spitze } = auslastungBereich(ber.key, f);
                          const pct = Math.min(Math.max((spitze / ber.max) * 100, anzahl > 0 ? 8 : 3), 100);
                          return (
                            <div className={`buch-spark-group${gesperrt ? ' zu' : ''}`} key={f.start}>
                              <div className="buch-spark-bar" style={{ height: `${pct}%`, background: auslastungFarbe(spitze, ber.max) }}>
                                {anzahl > 0 && <span className="val">{anzahl}</span>}
                                <span className="tip">{ber.label} · {f.start}–{f.ende} Uhr · {anzahl} Personen im Fenster · höchstens {spitze} gleichzeitig (max. {ber.max})</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                  <div className={`buch-fenster-reihe${alteRegel ? '' : ' mit-label'}`}>
                    {fenster.map(f => {
                      const gesperrt = gesperrteFensterHeute.includes(f.start);
                      return (
                        <button
                          key={f.start}
                          type="button"
                          className={`buch-fenster-btn${gesperrt ? ' zu' : ''}`}
                          title={gesperrt ? `${f.start}–${f.ende} Uhr wieder öffnen` : `${f.start}–${f.ende} Uhr sperren`}
                          onClick={() => onToggleBuchungsstoppFenster(datumStr, f.start)}
                        >
                          <span className="zeit">{f.start}–{f.ende}</span>
                          <span className="lbl">{gesperrt ? 'Gesperrt' : 'Offen'}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
              {ohneBereich > 0 && <div className="buch-ohne-bereich">⚠️ {ohneBereich} {ohneBereich === 1 ? 'Buchung hat' : 'Buchungen haben'} an diesem Tag noch keinen Sitzbereich und {ohneBereich === 1 ? 'zählt' : 'zählen'} in keinem Balken mit – bitte über ✏️ zuordnen.</div>}

              {/* Vollbild-Modal neue Reservierung */}
              {neuFormOffen && createPortal(
                <div className="res-modal-overlay" onClick={e => { if (e.target.classList.contains('res-modal-overlay')) { setNeuFormOffen(false); setNeuStatus(null); } }}>
                  <div className="res-modal">
                    <div className="res-modal-header">
                      <div>
                        <h3>Neue Reservierung</h3>
                        <div className="modal-datum-info">{tagStr}</div>
                      </div>
                      <button className="res-modal-close" onClick={() => { setNeuFormOffen(false); setNeuStatus(null); }}>✕</button>
                    </div>
                    <div className="res-modal-body">
                      {neuStatus && <div className={neuStatus.type === 'success' ? 'msg-success' : 'msg-error'} style={{ marginBottom: '1rem' }}>{neuStatus.msg}</div>}
                      <div className="res-modal-section">Gast</div>
                      <div className="res-modal-grid">
                        <div className="form-group"><label>Name *</label><input value={neuForm.name} onChange={e => setNF('name', e.target.value)} placeholder="Vor- und Nachname" /></div>
                        <div className="form-group"><label>Telefon</label><input value={neuForm.telefon} onChange={e => setNF('telefon', e.target.value)} placeholder="+49 221 ..." /></div>
                        <div className="form-group full"><label>E-Mail</label><input type="email" value={neuForm.email} onChange={e => setNF('email', e.target.value)} placeholder="email@beispiel.de" /></div>
                      </div>
                      <div className="res-modal-section">Reservierung</div>
                      <div style={{ background: '#faf7f2', border: '2px solid #b8963e', borderRadius: '12px', padding: '1rem 1.25rem', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '0.7rem', letterSpacing: '2px', textTransform: 'uppercase', color: '#b8963e', fontWeight: 700, whiteSpace: 'nowrap' }}>📅 Datum</span>
                        <input
                          type="date"
                          value={neuForm.datum}
                          onChange={e => setNF('datum', e.target.value)}
                          style={{ flex: 1, minWidth: '160px', border: 'none', background: 'transparent', fontSize: '1.25rem', fontWeight: 700, color: '#111', fontFamily: 'Raleway, sans-serif', outline: 'none', cursor: 'pointer' }}
                        />
                      </div>
                      <div className="res-modal-grid">
                        <div className="form-group"><label>Uhrzeit *</label>
                          <select value={neuForm.uhrzeit} onChange={e => setNF('uhrzeit', e.target.value)}>
                            <option value="">-- Uhrzeit wählen --</option>
                            {ZEITEN.map(z => <option key={z} value={z}>{z} Uhr</option>)}
                          </select>
                        </div>
                        <div className="form-group full">
                          <label>Personen</label>
                          <PersonenSlider value={neuForm.personen || '1'} onChange={v => setNF('personen', v)} />
                        </div>
                        <div className="form-group full"><label>Sitzbereich{bereichGilt(neuForm.datum || datumStr) ? ' *' : ' (erst ab 12.10.)'}</label>
                          <BereichSelect value={neuForm.sitzbereich} onChange={v => setNF('sitzbereich', v)} datum={neuForm.datum || datumStr} />
                        </div>
                        <div className="form-group full"><label>Tisch</label>
                          <div style={{ display:'flex', gap:'0.5rem' }}>
                            <select style={{ flex:1 }} value={neuForm.tisch_id} onChange={e => setNF('tisch_id', e.target.value)}>
                              <option value="">-- Kein Tisch --</option>
                              <optgroup label="Innen">{['17','20','21','22','23','24','25','26','27','28','29','30','31','32','33','34','TOGO','MAMA','WOW','TOLL','HASE','EVA'].map(t => <option key={t} value={t}>{t}</option>)}</optgroup>
                              <optgroup label="Außen">{['1','2','3','4','5','6','7','8','9','10','11','12','13','14','15','16','18','TOUR'].map(t => <option key={t} value={t}>{t}</option>)}</optgroup>
                            </select>
                            <button type="button" className="buch-plus-btn" style={{ width:42, height:42, borderRadius:8, fontSize:'1.1rem' }} title="Tischplan" onClick={() => setTpModal({ datum: datumStr || '', uhrzeit: neuForm.uhrzeit, onSelect: id => { setNF('tisch_id', id); setTpModal(null); } })}>🗺</button>
                          </div>
                        </div>
                      </div>
                      <div style={{ display:'flex', gap:'1.5rem', marginBottom:'1rem', alignItems:'center', flexWrap:'wrap' }}>
                        <span style={{ fontSize:'0.72rem', letterSpacing:'1px', textTransform:'uppercase', color:'var(--gray)' }}>Sitzwunsch:</span>
                        <label style={{ display:'flex', alignItems:'center', gap:'0.4rem', cursor:'pointer', fontSize:'0.9rem', fontWeight: neuSitzwunsch==='Drinnen'?'700':'normal' }}>
                          <input type="checkbox" checked={neuSitzwunsch === 'Drinnen'} onChange={() => setNeuSitzwunsch(s => s === 'Drinnen' ? '' : 'Drinnen')} />
                          🏠 Drinnen
                        </label>
                        <label style={{ display:'flex', alignItems:'center', gap:'0.4rem', cursor:'pointer', fontSize:'0.9rem', fontWeight: neuSitzwunsch==='Draußen'?'700':'normal' }}>
                          <input type="checkbox" checked={neuSitzwunsch === 'Draußen'} onChange={() => setNeuSitzwunsch(s => s === 'Draußen' ? '' : 'Draußen')} />
                          ☀️ Draußen
                        </label>
                      </div>
                      <div className="form-group"><label>Bemerkung</label><textarea value={neuForm.bemerkung} onChange={e => setNF('bemerkung', e.target.value)} placeholder="Anlass, Wünsche, Allergien..." rows={3} /></div>
                    </div>
                    <div className="res-modal-footer">
                      <button className="btn-ghost" onClick={() => { setNeuFormOffen(false); setNeuStatus(null); }}>Abbrechen</button>
                      <button className="btn-primary" onClick={handleNeuSpeichern}>Reservierung speichern</button>
                    </div>
                  </div>
                </div>
              , document.body)}

              {/* Kautz VIP Bestellungen — kompakter Reminder oben */}
              {kautzBestellungen.map((b, i) => {
                let artikel = [];
                try { artikel = JSON.parse(b.artikel); } catch {}
                const pizzaName = artikel[0]?.name || 'Bestellung';
                return (
                  <div key={`kautz-best-${i}`} style={{ background: '#fff0f7', border: '1px solid #ffb3d9', borderRadius: '6px', padding: '0.3rem 0.7rem', marginBottom: '0.4rem', fontSize: '0.78rem', color: '#cc3d7a', display: 'flex', alignItems: 'center', gap: '0.4rem', animation: 'vipPuls 2s ease-in-out infinite' }}>
                    <span>⭐</span>
                    <span><strong>Kautz</strong> · {pizzaName} · {b.typ}{b.uhrzeit ? ` · ${b.uhrzeit} Uhr` : ''}</span>
                  </div>
                );
              })}

              {tageList.length === 0 && kautzBestellungen.length === 0 && !neuFormOffen && (
                <div className="buch-leer">Keine Buchungen an diesem Tag.</div>
              )}

              {/* Accordion-Cards */}
              {tageList.map((e, i) => {
                const innenIds = ['1','2','3','4','5','6','7','8','9','10','11','12','13','14','15','16','18','TOUR'];
                const istReservierung = e._typ === 'tisch';
                const istVip = (e.name || '').toLowerCase().includes('kautz') || (e.email || '').toLowerCase() === 'kautz@web.de';
                const karteId = e.id || i;
                const offen = offeneKarte === karteId;
                const initialen = (e.name || '?').trim().split(/\s+/).slice(0, 2).map(w => w[0] || '').join('').toUpperCase();
                return (
                  <div key={i} className={`buch-mgmt-card${istVip ? ' buch-mgmt-card-vip' : ''}${offen ? ' buch-mgmt-card-offen' : ''}`} style={{ borderLeftColor: istVip ? '#ff69b4' : (typFarbe[e._typ] || '#999') }}>

                    {/* ── Kompakte Zeile (immer sichtbar) ── */}
                    <div className="buch-mgmt-row" onClick={() => setOffeneKarte(p => p === karteId ? null : karteId)}>
                      <span className="buch-mgmt-pfeil">{offen ? '▾' : '▸'}</span>
                      <span className="buch-mgmt-avatar">{initialen}</span>
                      <div className="buch-mgmt-row-info">
                        <span className="buch-mgmt-name">
                          {e._bestellung ? <>{e.name} · {e.uhrzeit ? `${e.uhrzeit} Uhr` : 'ohne Uhrzeit'}{e.typ ? ` · ${e.typ}` : ''}</> : <>{e.name} · {e.uhrzeit} Uhr · {e.personen} P.</>}
                          {istVip && <span style={{ fontSize:'0.65rem', background:'#ff69b4', color:'#fff', borderRadius:'4px', padding:'0.1rem 0.35rem', marginLeft:'0.4rem' }}>⭐ VIP</span>}
                        </span>
                        {anfragTypVoll[e.anfrage_typ || e._typ] && (
                          <span style={{ fontSize:'0.68rem', color:'var(--gray)', fontWeight:400, display:'block', marginTop:'0.1rem', letterSpacing:'0.3px' }}>{anfragTypVoll[e.anfrage_typ || e._typ]}</span>
                        )}
                        {e.tisch_id && <span className="buch-mgmt-tisch-pill">T {parseTische(e.tisch_id).join(' + ')}</span>}
                      </div>
                      <span className={`badge ${STATUS_FARBE[e.status] || 'badge-pending'}`}>{e.status}</span>
                    </div>

                    {/* ── Details (nur wenn offen) ── */}
                    {offen && (
                      <div className="buch-mgmt-details">
                        {e._typ !== 'tisch' && <div className="buch-typ-badge" style={{ background: typFarbe[e._typ], display:'inline-block', marginBottom:'0.4rem' }}>{typLabel[e._typ]}</div>}
                        {e.anfrage_typ && <div className="buch-typ-badge" style={{ background: typFarbe[e.anfrage_typ] || '#999', display:'inline-block', marginBottom:'0.4rem', marginLeft:'0.4rem' }}>{anfragTypVoll[e.anfrage_typ] || e.anfrage_typ}</div>}
                        {e.email && <div className="buch-mgmt-sub">{e.email}{e.telefon ? ` · ${e.telefon}` : ''}</div>}
                        {e.bemerkung && <div className="buch-mgmt-sub" style={{ fontStyle:'italic' }}>„{e.bemerkung}"</div>}

                        {istReservierung && (
                          <div className="buch-mgmt-tisch">
                            <label>🪑</label>
                            <select
                              value={e.tisch_id || ''}
                              onChange={async ev => {
                                const val = ev.target.value;
                                const bereich = val ? (innenIds.includes(val) ? 'innen' : 'außen') : null;
                                await updateReservierung(e.id, { tisch_id: val || null, bereich });
                                if (onSaved) onSaved();
                              }}
                            >
                              <option value="">— Kein Tisch —</option>
                              <optgroup label="Innen">{innenIds.map(t => <option key={t} value={t}>{t}</option>)}</optgroup>
                              <optgroup label="Außen">{['17','20','21','22','23','24','25','26','27','28','29','30','31','32','33','34','EVA','HASE','MAMA','TOGO','TOLL','WOW'].map(t => <option key={t} value={t}>{t}</option>)}</optgroup>
                            </select>
                            <button type="button" className="mini-tp-toggle-btn" onClick={() => setTpModal({ datum: e.datum||'', uhrzeit: e.uhrzeit||'', aktuelleAuswahl: e.tisch_id||'', onSelect: async ids => { const tischListe = parseTische(ids); const bereich = tischListe.length === 1 ? (innenIds.includes(tischListe[0]) ? 'innen' : 'außen') : (tischListe.some(t => !innenIds.includes(t)) ? 'gemischt' : 'innen'); await updateReservierung(e.id, { tisch_id: ids || null, bereich }); if (onSaved) onSaved(); setTpModal(null); } })} title="Tischplan">🗺</button>
                            {e.tisch_id && <span className="buch-mgmt-bereich">✓ {e.bereich}</span>}
                          </div>
                        )}

                        {istReservierung && e.uhrzeit && (() => {
                          const blockMin = e.dauer || getBlockMin(e.uhrzeit);
                          const [h, m] = e.uhrzeit.split(':').map(Number);
                          const endMin = h * 60 + m + blockMin;
                          const freiAb = `${String(Math.floor(endMin / 60)).padStart(2, '0')}:${String(endMin % 60).padStart(2, '0')}`;
                          return (
                            <div className="buchung-dauer-row">
                              🕐 Frei ab <strong>{freiAb} Uhr</strong> · {blockMin} Min.
                              <button type="button" onClick={async () => { await updateReservierung(e.id, { dauer: blockMin + 30 }); if (onSaved) onSaved(); }}>+30 Min.</button>
                              <button type="button" onClick={async () => { await updateReservierung(e.id, { dauer: blockMin + 60 }); if (onSaved) onSaved(); }}>+60 Min.</button>
                            </div>
                          );
                        })()}

                        <div className="buch-mgmt-actions">
                          {e._bestellung && (
                            <button className="btn-ghost" onClick={async () => { await updateBestellung(e.id, { status: 'fertig' }); if (onSaved) onSaved(); }}>✓ Fertig</button>
                          )}
                          {e._bestellung && (
                            <button className="btn-delete" onClick={async () => {
                              if (!window.confirm(`Bestellung von ${e.name} wirklich entfernen?`)) return;
                              await updateBestellung(e.id, { status: 'gelöscht' }); if (onSaved) onSaved();
                            }}>🗑 Löschen</button>
                          )}
                          {istReservierung && e.status !== 'bestätigt' && (
                            <button className="btn-ghost" onClick={async () => {
                              await updateReservierung(e.id, { status: 'bestätigt' });
                              if (e.email) {
                                fetch('/api/confirm-reservation', { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ name:e.name, email:e.email, datum:e.datum, uhrzeit:e.uhrzeit, personen:e.personen, sitzbereich:e.sitzbereich||'' }) }).catch(() => {});
                              }
                              if (onSaved) onSaved();
                            }}>✓ Bestätigen</button>
                          )}
                          {istReservierung && e.email && (
                            <button className="btn-ghost" title="E-Mail schreiben" onClick={() => {
                              setBuchReplyModal({
                                an: e.email,
                                name: e.name,
                                betreff: `Ihre Reservierung bei Savoca Köln am ${e.datum}`,
                                text: `Liebe/r ${e.name},\n\nvielen Dank für Ihre Reservierung am ${e.datum} um ${e.uhrzeit} Uhr für ${e.personen} Personen.\n\nMit freundlichen Grüßen\nIhr Savoca-Team\nGottesweg 139, 50939 Köln`,
                              });
                              setBuchReplySendStatus(null);
                            }}>📧 Mail</button>
                          )}
                          {istReservierung && e.status !== 'abgesagt' && (
                            <button className="btn-ghost" onClick={async () => {
                              await updateReservierung(e.id, { status: 'abgesagt' });
                              if (e.email) {
                                fetch('/api/cancel-reservation', { method: 'POST', headers: { 'Content-Type': 'application/json', ...(await adminHeaders()) }, body: JSON.stringify({ name: e.name, email: e.email, datum: e.datum, uhrzeit: e.uhrzeit, personen: e.personen || '' }) }).catch(() => {});
                              }
                              if (onSaved) onSaved();
                            }}>✗ Absagen</button>
                          )}
                          <button className="btn-ghost" style={{ color:'var(--gold)' }} onClick={() => setBuchNotizPopup({ name: e.name, typ: anfragTypVoll[e.anfrage_typ || e._typ] || '', notiz: e.admin_notiz || '' })}>📋 Notiz</button>
                          {istReservierung && (
                            <button className="btn-ghost" onClick={() => {
                              setAdminEditRes(e);
                              setAdminEditForm({ name: e.name||'', email: e.email||'', telefon: e.telefon||'', datum: e.datum||'', uhrzeit: e.uhrzeit||'', personen: e.personen||'', sitzbereich: e.sitzbereich||'', bemerkung: e.bemerkung||'' });
                              setAdminEditStatus(null);
                            }}>✏️ Bearbeiten</button>
                          )}
                          {istReservierung && (
                            <button className="btn-ghost" onClick={() => druckeReservierung(e)}>🖨️ Drucken</button>
                          )}
                          {istReservierung && (
                            <button className="btn-delete" onClick={async () => {
                              if (!window.confirm(`${e.name} · ${e.datum} · ${e.uhrzeit} Uhr wirklich unwiderruflich löschen?`)) return;
                              await deleteReservierung(e.id);
                              if (onSaved) onSaved();
                            }}>🗑 Löschen</button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="buch-leer-seite">
              <div className="buch-leer-ornament">✦</div>
              <div className="buch-leer-text">Tag auswählen</div>
              <div className="buch-leer-sub">Klicke auf einen Tag im Kalender um die Buchungen zu sehen</div>
            </div>
          )}
          <div className="buch-pg-nr">2</div>
        </div>

      </div>
      {buchNotizPopup && (
        <div className="res-modal-overlay" onClick={() => setBuchNotizPopup(null)}>
          <div className="res-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '480px', width: '95vw', padding: '2rem' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1.5rem' }}>
              <div style={{ fontSize:'0.75rem', letterSpacing:'2px', textTransform:'uppercase', color:'var(--gold)', fontWeight:700 }}>📋 Admin-Notiz</div>
              <button className="btn-ghost" style={{ fontSize:'1rem', padding:'0.4rem 0.9rem' }} onClick={() => setBuchNotizPopup(null)}>✕</button>
            </div>
            <div style={{ fontSize:'0.85rem', color:'var(--gray)', marginBottom:'1rem' }}>{buchNotizPopup.name}{buchNotizPopup.typ ? ` · ${buchNotizPopup.typ}` : ''}</div>
            <div style={{ background:'var(--surface)', borderRadius:'8px', padding:'1rem 1.25rem', fontSize:'0.95rem', lineHeight:'1.7', whiteSpace:'pre-wrap', minHeight:'4rem' }}>
              {buchNotizPopup.notiz || <span style={{ color:'var(--gray)', fontStyle:'italic' }}>Keine Notiz eingetragen.</span>}
            </div>
            <div style={{ marginTop:'1.5rem', textAlign:'right' }}>
              <button className="btn-ghost" onClick={() => setBuchNotizPopup(null)}>Schließen</button>
            </div>
          </div>
        </div>
      )}

      {buchReplyModal && (
        <div className="res-modal-overlay" onClick={() => setBuchReplyModal(null)}>
          <div className="res-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '680px', width: '95vw', padding: '2.5rem' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'2rem' }}>
              <div style={{ fontSize:'0.75rem', letterSpacing:'2px', textTransform:'uppercase', color:'var(--gold)', fontWeight:700 }}>📧 E-Mail senden</div>
              <button className="btn-ghost" style={{ fontSize:'1rem', padding:'0.4rem 0.9rem' }} onClick={() => setBuchReplyModal(null)}>✕</button>
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:'1.25rem' }}>
              <div className="form-group">
                <label style={{ fontSize:'0.85rem', marginBottom:'0.5rem', display:'block', fontWeight:600, letterSpacing:'1px', textTransform:'uppercase', color:'var(--gray)' }}>An</label>
                <input value={buchReplyModal.an} disabled style={{ opacity:0.6, fontSize:'1rem', padding:'0.8rem 1rem' }} />
              </div>
              <div className="form-group">
                <label style={{ fontSize:'0.85rem', marginBottom:'0.5rem', display:'block', fontWeight:600, letterSpacing:'1px', textTransform:'uppercase', color:'var(--gray)' }}>Betreff</label>
                <input value={buchReplyModal.betreff} onChange={e => setBuchReplyModal(m => ({ ...m, betreff: e.target.value }))} maxLength={120} style={{ fontSize:'1rem', padding:'0.8rem 1rem' }} />
              </div>
              <div className="form-group">
                <label style={{ fontSize:'0.85rem', marginBottom:'0.5rem', display:'block', fontWeight:600, letterSpacing:'1px', textTransform:'uppercase', color:'var(--gray)' }}>Nachricht</label>
                <textarea
                  value={buchReplyModal.text}
                  onChange={e => setBuchReplyModal(m => ({ ...m, text: e.target.value }))}
                  rows={10}
                  ref={el => { if (el && !el.dataset.positioned) { el.dataset.positioned = '1'; const pos = buchReplyModal.text.indexOf('\n\n') + 2; el.focus(); el.setSelectionRange(pos, pos); } }}
                  style={{ resize:'vertical', fontFamily:'Georgia,serif', fontSize:'1rem', lineHeight:'1.7', padding:'1rem', width:'100%', boxSizing:'border-box' }}
                />
              </div>
              {buchReplySendStatus === 'ok' && <div style={{ color:'#2d6a4f', fontSize:'1rem', fontWeight:600, padding:'0.75rem', background:'#f0fdf4', borderRadius:'8px' }}>✓ Mail erfolgreich gesendet!</div>}
              {buchReplySendStatus === 'error' && <div style={{ color:'red', fontSize:'1rem', padding:'0.75rem', background:'#fef2f2', borderRadius:'8px' }}>⚠️ Fehler beim Senden.</div>}
              <div style={{ display:'flex', gap:'1rem', marginTop:'0.5rem' }}>
                <button className="btn-dark" style={{ fontSize:'1rem', padding:'0.9rem 2rem' }} onClick={async () => {
                  setBuchReplySendStatus(null);
                  try {
                    const res = await fetch('/api/reply-anfrage', { method:'POST', headers:{ 'Content-Type':'application/json', ...(await adminHeaders()) }, body: JSON.stringify({ an: buchReplyModal.an, betreff: buchReplyModal.betreff, text: buchReplyModal.text }) });
                    if (res.ok) { setBuchReplySendStatus('ok'); setTimeout(() => setBuchReplyModal(null), 1500); }
                    else setBuchReplySendStatus('error');
                  } catch { setBuchReplySendStatus('error'); }
                }}>📨 Senden</button>
                <button className="btn-ghost" style={{ fontSize:'1rem', padding:'0.9rem 2rem' }} onClick={() => setBuchReplyModal(null)}>Abbrechen</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {tpModal && <TischplanModal reservierungen={reservierungen} datum={tpModal.datum} uhrzeit={tpModal.uhrzeit} onSelect={tpModal.onSelect} onClose={() => setTpModal(null)} />}

      {adminHinweis && createPortal(<div className={`admin-hinweis${adminHinweis.ok ? '' : ' fehler'}`} role="status" onClick={() => setAdminHinweis(null)}>{adminHinweis.text}</div>, document.body)}
      {adminEditRes && (
        <div className="res-modal-overlay" onClick={() => setAdminEditRes(null)}>
          <div className="res-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1.5rem' }}>
              <div style={{ fontSize:'0.65rem', letterSpacing:'2px', textTransform:'uppercase', color:'var(--gold)', fontWeight:700 }}>Buchung bearbeiten</div>
              <button className="btn-ghost" style={{ fontSize:'0.8rem' }} onClick={() => setAdminEditRes(null)}>✕</button>
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:'0.75rem' }}>
              <div className="form-group">
                <label>Name *</label>
                <input value={adminEditForm.name} onChange={e => setAdminEditForm(f => ({ ...f, name: e.target.value }))} maxLength={50} />
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.75rem' }}>
                <div className="form-group">
                  <label>E-Mail</label>
                  <input type="email" value={adminEditForm.email} onChange={e => setAdminEditForm(f => ({ ...f, email: e.target.value }))} maxLength={80} />
                </div>
                <div className="form-group">
                  <label>Telefon</label>
                  <input value={adminEditForm.telefon} onChange={e => setAdminEditForm(f => ({ ...f, telefon: e.target.value }))} maxLength={20} />
                </div>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'0.75rem' }}>
                <div className="form-group">
                  <label>Datum *</label>
                  <input type="date" value={adminEditForm.datum} onChange={e => setAdminEditForm(f => ({ ...f, datum: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>Uhrzeit *</label>
                  <input type="time" value={adminEditForm.uhrzeit} onChange={e => setAdminEditForm(f => ({ ...f, uhrzeit: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>Personen</label>
                  <input type="number" min="1" max="50" value={adminEditForm.personen} onChange={e => setAdminEditForm(f => ({ ...f, personen: e.target.value }))} />
                </div>
              </div>
              <div className="form-group">
                <label>Sitzbereich</label>
                <BereichSelect value={adminEditForm.sitzbereich} onChange={v => setAdminEditForm(f => ({ ...f, sitzbereich: v }))} leer="-- nicht zugeordnet --" datum={adminEditForm.datum} />
              </div>
              <div className="form-group">
                <label>Bemerkung</label>
                <textarea value={adminEditForm.bemerkung} onChange={e => setAdminEditForm(f => ({ ...f, bemerkung: e.target.value }))} rows={2} style={{ resize:'vertical' }} />
              </div>
              {adminEditStatus && <div style={{ color:'red', fontSize:'0.82rem' }}>{adminEditStatus}</div>}
              <div style={{ display:'flex', gap:'0.75rem', marginTop:'0.5rem' }}>
                <button className="btn-dark" onClick={saveAdminEdit}>💾 Speichern</button>
                <button className="btn-ghost" onClick={() => setAdminEditRes(null)}>Abbrechen</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ===== ADMIN TISCHPLAN =====
const AUSSEN_DEFAULT = [
  { id:'1',    c:1,  r:1 }, { id:'3',    c:2,  r:1 }, { id:'5',    c:3,  r:1 },
  { id:'11',   c:7,  r:1 }, { id:'12',   c:8,  r:1 }, { id:'13',   c:9,  r:1 }, { id:'14',   c:10, r:1 }, { id:'TOUR', c:13, r:1 },
  { id:'2',    c:1,  r:2 }, { id:'4',    c:2,  r:2 }, { id:'6',    c:3,  r:2 },
  { id:'8',    c:7,  r:2 }, { id:'9',    c:8,  r:2 }, { id:'10',   c:9,  r:2 }, { id:'15',   c:10, r:2 }, { id:'16',   c:11, r:2 }, { id:'18',   c:12, r:2 },
  { id:'7',    c:3,  r:3 },
];
const INNEN_DEFAULT = [
  { id:'26',   c:1,  r:1 }, { id:'27',   c:2,  r:1 }, { id:'28',   c:3,  r:1 }, { id:'29',   c:4,  r:1 }, { id:'30',   c:5,  r:1 },
  { id:'TOGO', c:7,  r:1 }, { id:'MAMA', c:8,  r:1 },
  { id:'31',   c:11, r:1 }, { id:'32',   c:12, r:1 },
  { id:'33',   c:13, r:2 },
  { id:'25',   c:1,  r:3 }, { id:'24',   c:2,  r:3 }, { id:'23',   c:4,  r:3 },
  { id:'WOW',  c:7,  r:3 }, { id:'TOLL', c:8,  r:3 },
  { id:'20',   c:1,  r:4 }, { id:'21',   c:2,  r:4 }, { id:'22',   c:3,  r:4 }, { id:'17',   c:5,  r:4 },
  { id:'HASE', c:7,  r:4 }, { id:'EVA',  c:8,  r:4 }, { id:'34',   c:13, r:4 },
];

function AdminTischplan({ reservierungen, datum, setDatum }) {
  const [uhrzeit, setUhrzeit] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [dragId, setDragId] = useState(null);
  const [dragZone, setDragZone] = useState(null);
  const [tischPopup, setTischPopup] = useState(null);

  const [aussenPos, setAussenPos] = useState(() => {
    try { return JSON.parse(localStorage.getItem('savoca_aussen') || 'null') || AUSSEN_DEFAULT; }
    catch { return AUSSEN_DEFAULT; }
  });
  const [innenPos, setInnenPos] = useState(() => {
    try { return JSON.parse(localStorage.getItem('savoca_innen') || 'null') || INNEN_DEFAULT; }
    catch { return INNEN_DEFAULT; }
  });

  function getStatus(tischId) {
    const buchungen = reservierungen.filter(r =>
      parseTische(r.tisch_id).includes(tischId) && r.datum === datum && r.status !== 'abgesagt'
    );
    if (!buchungen.length) return { status: 'frei', freiAb: null };
    if (!uhrzeit) return { status: 'gebucht', freiAb: null };
    const [rh, rm] = uhrzeit.split(':').map(Number);
    const reqMin = rh * 60 + rm;
    const reqBlock = getBlockMin(uhrzeit);
    const treffer = buchungen.find(r => {
      const [bh, bm] = r.uhrzeit.split(':').map(Number);
      const bMin = bh * 60 + bm;
      const bBlock = r.dauer || getBlockMin(r.uhrzeit);
      return reqMin < bMin + bBlock && bMin < reqMin + reqBlock;
    });
    if (treffer) {
      const [bh, bm] = treffer.uhrzeit.split(':').map(Number);
      const endMin = bh * 60 + bm + (treffer.dauer || getBlockMin(treffer.uhrzeit));
      const freiAb = `${String(Math.floor(endMin / 60)).padStart(2, '0')}:${String(endMin % 60).padStart(2, '0')}`;
      return { status: 'belegt', freiAb };
    }
    return { status: 'gebucht', freiAb: null };
  }

  function getTooltip(tischId) {
    const list = reservierungen
      .filter(r => parseTische(r.tisch_id).includes(tischId) && r.datum === datum && r.status !== 'abgesagt')
      .map(r => `${r.uhrzeit} Uhr · ${r.name} (${r.personen} P.)`);
    return list.length ? list.join('\n') : 'Frei';
  }

  function getBuchungen(tischId) {
    return reservierungen.filter(r =>
      parseTische(r.tisch_id).includes(tischId) && r.datum === datum && r.status !== 'abgesagt'
    );
  }

  function handleDrop(col, row, zone) {
    if (!dragId || dragZone !== zone) return;
    const setter = zone === 'aussen' ? setAussenPos : setInnenPos;
    const key = zone === 'aussen' ? 'savoca_aussen' : 'savoca_innen';
    setter(prev => {
      const neu = prev.map(t => t.id === dragId ? { ...t, c: col, r: row } : t);
      localStorage.setItem(key, JSON.stringify(neu));
      return neu;
    });
    setDragId(null);
    setDragZone(null);
  }

  function resetPositionen() {
    setAussenPos(AUSSEN_DEFAULT);
    setInnenPos(INNEN_DEFAULT);
    localStorage.removeItem('savoca_aussen');
    localStorage.removeItem('savoca_innen');
  }

  function renderGrid(tables, zone, cols, rows) {
    const cells = [];
    for (let r = 1; r <= rows; r++) {
      for (let c = 1; c <= cols; c++) {
        const tisch = tables.find(t => t.c === c && t.r === r);
        const { status, freiAb } = tisch ? getStatus(tisch.id) : { status: null, freiAb: null };
        cells.push(
          <div
            key={`${c}-${r}`}
            className={`tp-cell${editMode ? ' tp-cell-edit' : ''}`}
            style={{ gridColumn: c, gridRow: r }}
            onDragOver={e => { if (editMode) e.preventDefault(); }}
            onDrop={e => { e.preventDefault(); handleDrop(c, r, zone); }}
          >
            {tisch && (() => {
              const buchungen = getBuchungen(tisch.id);
              return (
                <div
                  className={`tp-t tp-t-${zone} tp-t-${status}${editMode ? ' tp-t-draggable' : ''}`}
                  draggable={editMode}
                  onDragStart={() => { setDragId(tisch.id); setDragZone(zone); }}
                  onClick={() => { if (!editMode) setTischPopup({ tischId: tisch.id, zone }); }}
                  title={getTooltip(tisch.id)}
                >
                  <span className="tp-t-nr">{tisch.id}</span>
                  {buchungen.length > 0 ? (
                    <span className="tp-t-name">
                      {buchungen[0].name.split(' ')[0]}
                      {buchungen.length > 1 && ` +${buchungen.length - 1}`}
                    </span>
                  ) : (
                    <span className="tp-t-seats">🪑</span>
                  )}
                  {freiAb && <span className="tp-t-frei-ab">ab {freiAb}</span>}
                </div>
              );
            })()}
          </div>
        );
      }
    }
    return cells;
  }

  const buchungenAmTag = reservierungen
    .filter(r => r.datum === datum && r.status !== 'abgesagt')
    .sort((a, b) => a.uhrzeit.localeCompare(b.uhrzeit));

  return (
    <div className="tp-wrap">

      <div className="tp-controls">
        <div className="form-group" style={{ margin: 0 }}>
          <label>Datum</label>
          <input type="date" value={datum} onChange={e => setDatum(e.target.value)} style={{ minWidth: '160px' }} />
        </div>
        <div className="form-group" style={{ margin: 0 }}>
          <label>Uhrzeit (optional)</label>
          <select value={uhrzeit} onChange={e => setUhrzeit(e.target.value)} style={{ minWidth: '170px' }}>
            <option value="">Alle Buchungen zeigen</option>
            {ZEITEN.map(z => <option key={z} value={z}>{z} Uhr</option>)}
          </select>
        </div>
        <div className="tp-legende">
          <span className="tp-leg tp-frei">■ Frei</span>
          <span className="tp-leg tp-gebucht">■ Gebucht</span>
          <span className="tp-leg tp-belegt">■ Jetzt belegt</span>
        </div>
        <div style={{ display:'flex', gap:'0.5rem', marginLeft:'auto' }}>
          <button
            className={editMode ? 'btn-primary' : 'btn-ghost'}
            style={{ fontSize:'0.8rem', padding:'0.4rem 1rem' }}
            onClick={() => setEditMode(e => !e)}
          >
            {editMode ? '✓ Fertig' : '✏️ Tische verschieben'}
          </button>
          {editMode && (
            <button className="btn-ghost" style={{ fontSize:'0.8rem', padding:'0.4rem 0.8rem' }} onClick={resetPositionen}>
              Zurücksetzen
            </button>
          )}
        </div>
      </div>

      {editMode && (
        <div style={{ background:'#2a1a4e', color:'#c9a84c', padding:'0.6rem 1rem', borderRadius:'6px', fontSize:'0.82rem' }}>
          ✏️ Tisch anklicken &amp; auf eine neue Position ziehen. Nummern bleiben gleich.
        </div>
      )}

      <div className="tp-scroll-wrap">
        <div className="tp-plan-wrap">

          <div className="tp-zone-header tp-innen-header">
            <span>🏠</span> Innen · Restaurant
          </div>
          <div className="tp-grid tp-grid-aussen">
            {renderGrid(aussenPos, 'aussen', 13, 3)}
          </div>

          <div className="tp-zone-header tp-aussen-header">
            <span>☀️</span> Außen · Terrasse
          </div>
          <div className="tp-grid tp-grid-innen">
            {renderGrid(innenPos, 'innen', 13, 4)}
          </div>

        </div>
      </div>

      <div className="tp-liste">
        <div className="tp-liste-title">
          Buchungen — {new Date(datum + 'T12:00:00').toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          <span className="tp-liste-count">{buchungenAmTag.length}</span>
        </div>
        {buchungenAmTag.length === 0
          ? <p style={{ color: 'var(--gray)', fontSize: '0.9rem', margin: '1rem 0' }}>Keine Buchungen an diesem Tag.</p>
          : buchungenAmTag.map(r => (
            <div key={r.id} className="tp-liste-row">
              <span className="tp-liste-time">{r.uhrzeit} Uhr</span>
              <span className="tp-liste-tisch">{r.tisch_id ? `Tisch ${r.tisch_id}` : '—'}</span>
              <span className="tp-liste-name">{r.name}</span>
              <span className="tp-liste-personen">{r.personen} P.</span>
              <span className={`badge ${STATUS_FARBE[r.status] || 'badge-pending'}`}>{r.status}</span>
            </div>
          ))
        }
      </div>

      {/* Tisch-Popup */}
      {tischPopup && (() => {
        const buchungen = getBuchungen(tischPopup.tischId);
        const datumFormatiert = new Date(datum + 'T12:00:00').toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
        return (
          <div className="tp-popup-overlay" onClick={() => setTischPopup(null)}>
            <div className="tp-popup" onClick={e => e.stopPropagation()}>
              <div className="tp-popup-header">
                <div>
                  <div className="tp-popup-title">Tisch {tischPopup.tischId}</div>
                  <div className="tp-popup-zone">{tischPopup.zone === 'aussen' ? '🏠 Innen' : '☀️ Außen'}</div>
                </div>
                <button className="tp-popup-close" onClick={() => setTischPopup(null)}>✕</button>
              </div>
              <div className="tp-popup-datum">{datumFormatiert}</div>
              <div className="tp-popup-body">
                {buchungen.length === 0
                  ? <div className="tp-popup-leer">Keine Reservierungen an diesem Tag.</div>
                  : buchungen.map((r, i) => (
                    <div key={i} className="tp-popup-buchung">
                      <div className="tp-popup-buch-top">
                        <span className="tp-popup-uhr">{r.uhrzeit} Uhr</span>
                        <span className={`badge ${STATUS_FARBE[r.status] || 'badge-pending'}`}>{r.status}</span>
                      </div>
                      <div className="tp-popup-buch-name">{r.name}</div>
                      <div className="tp-popup-buch-info">{r.personen} Personen{r.email ? ` · ${r.email}` : ''}{r.telefon ? ` · ${r.telefon}` : ''}</div>
                      {r.bemerkung && <div className="tp-popup-buch-bem">„{r.bemerkung}"</div>}
                    </div>
                  ))
                }
              </div>
            </div>
          </div>
        );
      })()}

    </div>
  );
}

// ===== ADMIN: KUNDEN BESTELLUNGEN =====
function AdminBestellungen({ bestellungen, onSaved }) {
  const [form, setForm] = useState({ name: '', datum: '', uhrzeit: '', notiz: '' });
  const [fehler, setFehler] = useState(null);
  const [ok, setOk] = useState(false);
  const [filter, setFilter] = useState('jahr');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const heute = new Date();
  const gefiltered = bestellungen.filter(b => {
    if (!b.datum) return false;
    const d = new Date(b.datum + 'T12:00:00');
    if (filter === 'tag') {
      return d.toDateString() === heute.toDateString();
    } else if (filter === 'woche') {
      const startWoche = new Date(heute); startWoche.setDate(heute.getDate() - heute.getDay() + 1);
      const endWoche = new Date(startWoche); endWoche.setDate(startWoche.getDate() + 6);
      return d >= startWoche && d <= endWoche;
    } else if (filter === 'monat') {
      return d.getMonth() === heute.getMonth() && d.getFullYear() === heute.getFullYear();
    } else {
      return d.getFullYear() === heute.getFullYear();
    }
  }).sort((a, b) => (a.datum + a.uhrzeit).localeCompare(b.datum + b.uhrzeit));

  const druckeTafel = () => {
    const filterLabel = filter === 'tag' ? `Heute · ${heute.toLocaleDateString('de-DE',{weekday:'long',day:'2-digit',month:'2-digit',year:'numeric'})}` : filter === 'woche' ? 'Diese Woche' : `${heute.toLocaleDateString('de-DE',{month:'long',year:'numeric'})}`;
    const zeilen = gefiltered.map(b => `
      <tr>
        <td>${b.datum ? new Date(b.datum+'T12:00:00').toLocaleDateString('de-DE',{weekday:'short',day:'2-digit',month:'2-digit'}) : '—'}</td>
        <td>${b.uhrzeit || '—'} Uhr</td>
        <td><strong>${b.name}</strong></td>
        <td>${b.artikel || '—'}</td>
        <td>${b.status}</td>
      </tr>`).join('');
    const w = window.open('', '_blank');
    w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>Bestellungen</title>
    <style>body{font-family:Georgia,serif;padding:2rem;color:#111}h1{font-size:1.6rem;margin-bottom:0.5rem}h2{font-size:1rem;color:#888;font-weight:normal;margin-bottom:2rem}table{width:100%;border-collapse:collapse}th{text-align:left;border-bottom:2px solid #111;padding:0.5rem 0.75rem;font-size:0.85rem;letter-spacing:1px;text-transform:uppercase}td{padding:0.6rem 0.75rem;border-bottom:1px solid #ddd;font-size:0.95rem}tr:last-child td{border-bottom:none}.no-print{position:fixed;top:12px;right:12px;background:#5C3768;color:#fff;border:none;border-radius:6px;padding:0.4rem 1rem;cursor:pointer}@media print{.no-print{display:none}}</style>
    </head><body>
    <button class="no-print" onclick="window.close()">✕ Schließen</button>
    <h1>Savoca · Bestellungen</h1><h2>${filterLabel}</h2>
    <table><thead><tr><th>Datum</th><th>Uhrzeit</th><th>Name</th><th>Bestellung</th><th>Status</th></tr></thead>
    <tbody>${zeilen || '<tr><td colspan="5" style="color:#888;text-align:center;padding:2rem">Keine Bestellungen</td></tr>'}</tbody></table>
    <script>window.onload=()=>window.print();window.onafterprint=()=>window.close();<\/script>
    </body></html>`);
    w.document.close();
  };

  const handleSubmit = async () => {
    if (!form.name || !form.datum || !form.uhrzeit || !form.notiz) {
      setFehler('Bitte alle Felder ausfüllen.'); return;
    }
    setFehler(null);
    await addBestellung({ name: form.name, datum: form.datum, uhrzeit: form.uhrzeit, artikel: form.notiz, status: 'neu', gesamt: 0 });
    setForm({ name: '', datum: '', uhrzeit: '', notiz: '' });
    setOk(true); setTimeout(() => setOk(false), 2000);
    onSaved();
  };

  return (
    <div>
      <div className="admin-section-title" style={{ marginBottom: '1.5rem' }}>Neue Bestellung erfassen</div>
      {fehler && <div className="msg-error" style={{ marginBottom:'1rem' }}>{fehler}</div>}
      {ok    && <div className="msg-success" style={{ marginBottom:'1rem' }}>✓ Bestellung gespeichert.</div>}
      <div className="form-grid" style={{ marginBottom: '1rem' }}>
        <div className="form-group"><label>Name *</label><input value={form.name} onChange={e => set('name', e.target.value)} placeholder="Kundenname" /></div>
        <div className="form-group"><label>Datum *</label><input type="date" value={form.datum} onChange={e => set('datum', e.target.value)} /></div>
        <div className="form-group"><label>Uhrzeit *</label>
          <select value={form.uhrzeit} onChange={e => set('uhrzeit', e.target.value)}>
            <option value="">-- Uhrzeit wählen --</option>
            {ZEITEN.map(z => <option key={z} value={z}>{z} Uhr</option>)}
          </select>
        </div>
      </div>
      <div className="form-group" style={{ marginBottom: '1.25rem' }}>
        <label>Bestellung *</label>
        <textarea value={form.notiz} onChange={e => set('notiz', e.target.value)} placeholder="z.B. 2× Margherita, 1× Tiramisu, Lieferung..." rows={4} />
      </div>
      <button className="btn-primary" onClick={handleSubmit}>Bestellung speichern</button>

      <div style={{ marginTop: '2.5rem' }}>
        <div style={{ display:'flex', alignItems:'center', gap:'0.75rem', marginBottom:'1.25rem', flexWrap:'wrap' }}>
          <div className="admin-section-title" style={{ margin:0 }}>Bestellungen</div>
          <div style={{ display:'flex', gap:'0.4rem' }}>
            {['tag','woche','monat','jahr'].map(f => (
              <button key={f} onClick={() => setFilter(f)}
                style={{ padding:'0.3rem 0.8rem', borderRadius:'20px', border:'1px solid var(--gold)', background: filter===f ? 'var(--gold)' : 'transparent', color: filter===f ? '#fff' : 'var(--gold)', cursor:'pointer', fontSize:'0.8rem', fontWeight:600, textTransform:'capitalize' }}>
                {f === 'tag' ? 'Tag' : f === 'woche' ? 'Woche' : f === 'monat' ? 'Monat' : 'Jahr'}
              </button>
            ))}
          </div>
          <button onClick={druckeTafel} style={{ marginLeft:'auto', background:'transparent', border:'1px solid var(--gray)', borderRadius:'8px', padding:'0.3rem 0.8rem', cursor:'pointer', fontSize:'0.85rem', color:'var(--gray)' }}>🖨️ Tafel drucken</button>
        </div>

        {gefiltered.length === 0 && <p style={{ color:'var(--gray)' }}>Keine Bestellungen für diesen Zeitraum.</p>}
        {gefiltered.map(b => (
          <div key={b.id} className="card" style={{ marginBottom: '0.75rem' }}>
            <div className="card-header">
              <div>
                <div className="card-title">{b.name}</div>
                {(b.datum || b.uhrzeit) && (
                  <div style={{ fontSize:'0.85rem', color:'var(--gold)', marginTop:'0.2rem' }}>
                    {b.datum ? new Date(b.datum+'T12:00:00').toLocaleDateString('de-DE',{weekday:'short',day:'2-digit',month:'2-digit',year:'numeric'}) : ''}
                    {b.uhrzeit ? ` · ${b.uhrzeit} Uhr` : ''}
                  </div>
                )}
                <div style={{ fontSize:'0.9rem', marginTop:'0.5rem', color:'var(--text)' }}>{b.artikel}</div>
              </div>
              <span className={`badge ${STATUS_FARBE[b.status] || 'badge-new'}`}>{b.status}</span>
            </div>
            <div style={{ display:'flex', gap:'0.5rem', marginTop:'0.5rem' }}>
              <button className="btn-ghost" onClick={async () => { await updateBestellung(b.id, { status: 'fertig' }); onSaved(); }}>✓ Fertig</button>
              <button className="btn-ghost" style={{ color:'var(--gray)' }} onClick={async () => { await updateBestellung(b.id, { status: 'gelöscht' }); onSaved(); }}>Löschen</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ===== ADMIN: TERMINE (Kalender „Aktuelles" auf der Hauptseite savoca-caffe.de) =====
const WOCHENTAGE = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];
const TERMIN_LINKS = [
  { key: '', label: 'Kein Knopf', url: '', text: '' },
  { key: 'tisch', label: 'Tisch reservieren', url: 'https://reservierung.savoca-caffe.de/?tab=tisch', text: 'Tisch reservieren' },
  { key: 'brunch', label: 'Brunch reservieren', url: 'https://reservierung.savoca-caffe.de/?tab=brunch', text: 'Brunch reservieren' },
  { key: 'eisgeburtstag', label: 'Eisgeburtstag anfragen', url: 'https://reservierung.savoca-caffe.de/?tab=eisgeburtstag', text: 'Eisgeburtstag anfragen' },
  { key: 'kindergeburtstag', label: 'Kindergeburtstag anfragen', url: 'https://reservierung.savoca-caffe.de/?tab=kindergeburtstag', text: 'Kindergeburtstag anfragen' },
  { key: 'catering', label: 'Catering anfragen', url: 'https://reservierung.savoca-caffe.de/?tab=catering', text: 'Catering anfragen' },
  { key: 'event', label: 'Event anfragen', url: 'https://reservierung.savoca-caffe.de/?tab=event', text: 'Event anfragen' },
];
const TERMIN_LEER = { id: null, titel: '', beschreibung: '', datum: '', uhrzeit: '', uhrzeitText: '', wiederholung: 'keine', wiederholung_bis: '', flyer: '', flyer_ki: false, linkZiel: '', aktiv: true };
const datumDe = (d) => { try { const [j, m, t] = String(d).split('-'); return `${t}.${m}.${j}`; } catch { return d; } };
const terminWann = (t) => t.wiederholung === 'woechentlich'
  ? `jeden ${WOCHENTAGE[new Date(t.datum + 'T12:00:00').getDay()]} ab ${datumDe(t.datum)}${t.wiederholung_bis ? ' bis ' + datumDe(t.wiederholung_bis) : ''}`
  : datumDe(t.datum);

function AdminTermine() {
  const zeitRef = useRef(null);
  const [zeitHinweis, setZeitHinweis] = useState(false);   // Hinweis „Bitte Uhrzeit auswählen“ am Zeitfeld
  const zeitZeigen = () => { setZeitHinweis(true); const el = zeitRef.current; if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); setTimeout(() => el.focus(), 350); } };
  const [liste, setListe] = useState([]);
  const [laedt, setLaedt] = useState(true);
  const [ladefehler, setLadefehler] = useState(false);
  const [form, setForm] = useState(null);          // null = Formular zu
  const [meldung, setMeldung] = useState(null);    // { ok, text }
  const [hochladen, setHochladen] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const laden = async () => {
    setLaedt(true);
    const { data, error } = await supabase.from('termine').select('*').order('datum', { ascending: true });
    if (error) { setLadefehler(true); setLaedt(false); return; }
    setLadefehler(false); setListe(data || []); setLaedt(false);
  };
  useEffect(() => { laden(); }, []);

  const bearbeiten = (t) => setForm({
    id: t.id, titel: t.titel || '', beschreibung: t.beschreibung || '', datum: t.datum || '',
    // Uhrzeit: Zeitauswahl (HH:MM). Ältere Termine mit Text wie „ab 18 Uhr“ behalten ihren Text, bis eine Zeit gewählt wird.
    uhrzeit: /^\d{1,2}:\d{2}$/.test(t.uhrzeit || '') ? t.uhrzeit.padStart(5, '0') : '', uhrzeitText: /^\d{1,2}:\d{2}$/.test(t.uhrzeit || '') ? '' : (t.uhrzeit || ''),
    wiederholung: t.wiederholung || 'keine', wiederholung_bis: t.wiederholung_bis || '', flyer: t.flyer || '', flyer_ki: !!t.flyer_ki, reservierbar: !!t.reservierbar, preis: t.preis || '',
    linkZiel: (TERMIN_LINKS.find(l => l.url && l.url === t.link) || {}).key ?? (t.link ? 'eigen' : ''), aktiv: t.aktiv !== false, linkEigen: t.link || '',
  });

  // Flyer verkleinern (max. 1200 px, JPEG) und ins Flyer-Lager hochladen
  const flyerHochladen = async (datei) => {
    if (!datei) return;
    setHochladen(true); setMeldung(null);
    try {
      const bild = await new Promise((ok, fail) => { const i = new Image(); i.onload = () => ok(i); i.onerror = fail; i.src = URL.createObjectURL(datei); });
      const faktor = Math.min(1, 1200 / Math.max(bild.width, bild.height));
      const c = document.createElement('canvas'); c.width = Math.round(bild.width * faktor); c.height = Math.round(bild.height * faktor);
      c.getContext('2d').drawImage(bild, 0, 0, c.width, c.height);
      const blob = await new Promise(ok => c.toBlob(ok, 'image/jpeg', 0.84));
      const name = `flyer-${Date.now()}-${Math.random().toString(36).slice(2, 8)}.jpg`;
      const { error } = await supabase.storage.from('termine-flyer').upload(name, blob, { contentType: 'image/jpeg' });
      if (error) throw error;
      set('flyer', supabase.storage.from('termine-flyer').getPublicUrl(name).data.publicUrl);
    } catch (e) { setMeldung({ ok: false, text: 'Das Bild konnte nicht hochgeladen werden. Bitte ein JPG oder PNG unter 5 MB versuchen.' }); }
    setHochladen(false);
  };

  const speichern = async () => {
    if (!form.titel.trim() || !form.datum) { setMeldung({ ok: false, text: 'Bitte Titel und Datum ausfüllen.' }); return; }
    if (form.reservierbar && !/^\d{1,2}:\d{2}$/.test(form.uhrzeit || '')) { setMeldung({ ok: false, text: 'Für „Reservierung möglich" bitte oben bei „Uhrzeit" die Startzeit auswählen.' }); zeitZeigen(); return; }
    if (form.reservierbar && form.wiederholung !== 'keine') { setMeldung({ ok: false, text: '„Reservierung möglich" geht nur bei einmaligen Terminen.' }); return; }
    const ziel = TERMIN_LINKS.find(l => l.key === form.linkZiel);
    const link = form.linkZiel === 'eigen' ? (form.linkEigen || '').trim() : (ziel?.url || '');
    if (link && !/^https:\/\//.test(link)) { setMeldung({ ok: false, text: 'Der Link muss mit https:// beginnen.' }); return; }
    const zeile = {
      titel: form.titel.trim(), beschreibung: form.beschreibung.trim() || null, datum: form.datum, uhrzeit: form.uhrzeit || (form.uhrzeitText || '').trim() || null,
      wiederholung: form.wiederholung, wiederholung_bis: form.wiederholung === 'woechentlich' && form.wiederholung_bis ? form.wiederholung_bis : null,
      flyer: form.flyer || null, flyer_ki: !!form.flyer && form.flyer_ki, reservierbar: !!form.reservierbar, preis: (form.preis || '').trim() || null, link: link || null, link_text: link ? (ziel?.text || 'Mehr erfahren') : null, aktiv: form.aktiv,
    };
    const { error } = form.id ? await supabase.from('termine').update(zeile).eq('id', form.id) : await supabase.from('termine').insert(zeile);
    if (error) { setMeldung({ ok: false, text: 'Speichern hat nicht geklappt – bitte noch einmal versuchen.' }); return; }
    setMeldung({ ok: true, text: form.id ? 'Termin gespeichert.' : 'Termin angelegt – er steht jetzt auf der Webseite unter „Aktuelles".' });
    setForm(null); laden();
  };

  const umschalten = async (t) => { await supabase.from('termine').update({ aktiv: !t.aktiv }).eq('id', t.id); laden(); };
  const loeschen = async (t) => {
    if (!window.confirm(`„${t.titel}" wirklich löschen? Der Termin verschwindet von der Webseite.`)) return;
    await supabase.from('termine').delete().eq('id', t.id); laden();
  };

  const heute = heuteLokal();
  const wiederkehrend = liste.filter(t => t.wiederholung === 'woechentlich' && (!t.wiederholung_bis || t.wiederholung_bis >= heute));
  const kommend = liste.filter(t => t.wiederholung !== 'woechentlich' && t.datum >= heute);
  const vorbei = liste.filter(t => !wiederkehrend.includes(t) && !kommend.includes(t));
  const karte = (t) => (
    <div key={t.id} className="card" style={{ opacity: t.aktiv ? 1 : 0.6 }}>
      <div className="card-header">
        <div>
          <div className="card-title">{t.titel}</div>
          <div style={{ color: 'var(--gray)', fontSize: '0.85rem', marginTop: '0.25rem' }}>{terminWann(t)}{t.uhrzeit ? ` · ${t.uhrzeit}` : ''}{t.preis ? ` · ${t.preis}` : ''}{t.reservierbar ? ' · 🎟 reservierbar' : ''}</div>
          {t.beschreibung && <div style={{ color: 'var(--gray)', fontSize: '0.8rem', marginTop: '0.35rem', maxWidth: '620px' }}>{t.beschreibung.length > 140 ? t.beschreibung.slice(0, 140) + '…' : t.beschreibung}</div>}
        </div>
        <span className={`badge ${t.aktiv ? 'badge-confirmed' : 'badge-cancelled'}`}>{t.aktiv ? 'Sichtbar' : 'Versteckt'}</span>
      </div>
      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
        <button className="btn-ghost" onClick={() => { bearbeiten(t); setMeldung(null); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>✏️ Bearbeiten</button>
        <button className="btn-ghost" onClick={() => umschalten(t)}>{t.aktiv ? 'Verstecken' : 'Wieder zeigen'}</button>
        <button className="btn-ghost" style={{ color: 'var(--gray)' }} onClick={() => loeschen(t)}>Löschen</button>
      </div>
    </div>
  );

  return (
    <div>
      <p style={{ color: 'var(--gray)', fontSize: '0.9rem', margin: '0 0 1rem', maxWidth: '640px' }}>
        Diese Termine erscheinen auf <strong>savoca-caffe.de</strong> unter <strong>„Aktuelles"</strong> im Kalender. Die Gäste tippen auf einen Tag und sehen den Termin.
      </p>
      {meldung && <div className={meldung.ok ? 'msg-success' : 'msg-error'} style={{ marginBottom: '1rem' }}>{meldung.text}</div>}
      {!form && <button className="btn-primary" style={{ marginBottom: '1.5rem' }} onClick={() => { setForm({ ...TERMIN_LEER }); setMeldung(null); }}>＋ Neuer Termin</button>}
      {form && (
        <div className="card" style={{ marginBottom: '2rem' }}>
          <div style={{ fontSize: '0.65rem', letterSpacing: '2px', textTransform: 'uppercase', color: 'var(--gray)', marginBottom: '1rem' }}>{form.id ? 'Termin bearbeiten' : 'Neuer Termin'}</div>
          <div className="form-grid" style={{ marginBottom: '1rem' }}>
            <div className="form-group"><label>Titel *</label><input value={form.titel} onChange={e => set('titel', e.target.value)} placeholder="z. B. Pizza Tuesday" maxLength={120} /></div>
            <div className="form-group"><label>Uhrzeit <span style={{ color: form.reservierbar ? '#a05a00' : 'var(--gray)', fontWeight: form.reservierbar ? 700 : 400 }}>{form.reservierbar ? '(Pflicht, weil „Reservierung möglich“ angehakt ist)' : '(optional, Startzeit)'}</span></label>
              <input ref={zeitRef} type="time" step="900" value={form.uhrzeit} onChange={e => { set('uhrzeit', e.target.value); if (e.target.value) setZeitHinweis(false); }}
                style={zeitHinweis && form.reservierbar && !form.uhrzeit ? { borderColor: '#c0392b', boxShadow: '0 0 0 2px rgba(192,57,43,0.25)' } : undefined} />
              {zeitHinweis && form.reservierbar && !form.uhrzeit && <div style={{ fontSize: '0.85rem', color: '#c0392b', fontWeight: 700, marginTop: '0.35rem' }}>⏰ Bitte hier die Uhrzeit auswählen – ohne Startzeit kann kein Gast das Event buchen.</div>}
              {!form.uhrzeit && form.uhrzeitText && (
                <div style={{ fontSize: '0.78rem', color: 'var(--gray)', marginTop: '0.3rem' }}>
                  Bisher eingetragen: „{form.uhrzeitText}“ – bleibt stehen, solange du keine Zeit auswählst.{' '}
                  <button type="button" className="btn-ghost" style={{ minHeight: 'auto', padding: '0.1rem 0.5rem', fontSize: '0.75rem' }} onClick={() => set('uhrzeitText', '')}>Text entfernen</button>
                </div>
              )}
            </div>
            <div className="form-group"><label>{form.wiederholung === 'woechentlich' ? 'Erster Termin *' : 'Datum *'}</label><input type="date" value={form.datum} onChange={e => set('datum', e.target.value)} /></div>
            <div className="form-group"><label>Wiederholung</label>
              <select value={form.wiederholung} onChange={e => set('wiederholung', e.target.value)}>
                <option value="keine">Einmalig</option>
                <option value="woechentlich">Jede Woche am selben Wochentag</option>
              </select>
            </div>
            {form.wiederholung === 'woechentlich' && (
              <div className="form-group"><label>Wiederholen bis <span style={{ color: 'var(--gray)', fontWeight: 400 }}>(leer = unbegrenzt)</span></label><input type="date" value={form.wiederholung_bis} onChange={e => set('wiederholung_bis', e.target.value)} /></div>
            )}
          </div>
          <div className="form-group" style={{ marginBottom: '1rem' }}>
            <label>Beschreibung</label>
            <textarea value={form.beschreibung} onChange={e => set('beschreibung', e.target.value)} placeholder="Was passiert? Preise, Hinweise …" maxLength={1500} rows={4} />
          </div>
          <div className="form-group" style={{ marginBottom: '1rem' }}>
            <label>Flyer / Bild <span style={{ color: 'var(--gray)', fontWeight: 400 }}>(optional)</span></label>
            {form.flyer && <div style={{ margin: '0.4rem 0' }}><img src={form.flyer} alt="Vorschau Flyer" style={{ maxHeight: '160px', borderRadius: '8px', border: '1px solid var(--border)' }} /></div>}
            <input type="file" accept="image/jpeg,image/png,image/webp" disabled={hochladen} onChange={e => flyerHochladen(e.target.files?.[0])} />
            {hochladen && <div style={{ fontSize: '0.8rem', color: 'var(--gray)', marginTop: '0.3rem' }}>Bild wird hochgeladen …</div>}
            {form.flyer && (
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginTop: '0.5rem', flexWrap: 'wrap' }}>
                <label style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', fontSize: '0.85rem', textTransform: 'none', letterSpacing: 0 }}><input type="checkbox" checked={form.flyer_ki} onChange={e => set('flyer_ki', e.target.checked)} style={{ width: 'auto' }} /> Bild wurde mit KI erstellt (dann steht „Bild mit KI erstellt" dabei – Pflicht)</label>
                <button type="button" className="btn-ghost" onClick={() => { set('flyer', ''); set('flyer_ki', false); }}>Bild entfernen</button>
              </div>
            )}
          </div>
          <div className="form-grid" style={{ marginBottom: '1rem' }}>
            <div className="form-group"><label>Knopf unter dem Termin</label>
              <select value={form.linkZiel} onChange={e => set('linkZiel', e.target.value)}>
                {TERMIN_LINKS.map(l => <option key={l.key} value={l.key}>{l.label}</option>)}
                {form.linkZiel === 'eigen' && <option value="eigen">Eigener Link</option>}
              </select>
            </div>
            {form.linkZiel === 'eigen' && <div className="form-group"><label>Eigener Link (https://…)</label><input value={form.linkEigen || ''} onChange={e => set('linkEigen', e.target.value)} /></div>}
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem', cursor: 'pointer', textTransform: 'none', letterSpacing: 0, fontSize: '0.92rem', minHeight: 44 }}>
                <input type="checkbox" checked={!!form.reservierbar} onChange={e => { set('reservierbar', e.target.checked); if (e.target.checked && !form.uhrzeit) zeitZeigen(); else setZeitHinweis(false); }} style={{ width: 22, height: 22, marginTop: 2, flexShrink: 0 }} />
                <span><strong>Reservierung möglich</strong> – erscheint als eigener Knopf unter „Was möchtest du buchen?", in der Reiterleiste, auf der App-Startseite, im Reservierungs-Fenster der Webseite und im Kalender</span>
              </label>
              {form.reservierbar && <div style={{ fontSize: '0.78rem', color: 'var(--gray)', marginTop: '0.3rem' }}>Oben bitte die Startzeit auswählen (z. B. 09:30), der Termin muss einmalig sein. Der Gast wählt dann nur Bereich und Personenzahl. Nach dem Termin verschwindet der Knopf von selbst.</div>}
            </div>
            <div className="form-group"><label>Preis <span style={{ color: 'var(--gray)', fontWeight: 400 }}>(Hinweis, bezahlt wird vor Ort)</span></label><input value={form.preis || ''} onChange={e => set('preis', e.target.value)} placeholder="z. B. 19 € pro Person" maxLength={60} /></div>
            <div className="form-group"><label>Sichtbar auf der Webseite</label>
              <select value={form.aktiv ? 'ja' : 'nein'} onChange={e => set('aktiv', e.target.value === 'ja')}><option value="ja">Ja</option><option value="nein">Nein (versteckt)</option></select>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            <button className="btn-primary" disabled={hochladen} onClick={speichern}>{form.id ? 'Änderung speichern' : 'Termin speichern'}</button>
            <button className="btn-ghost" onClick={() => { setForm(null); setMeldung(null); }}>Abbrechen</button>
          </div>
        </div>
      )}
      {laedt && <p style={{ color: 'var(--gray)' }}>Termine werden geladen …</p>}
      {ladefehler && <div className="msg-error">Die Termine konnten nicht geladen werden. Bitte die Seite neu laden.</div>}
      {!laedt && !ladefehler && liste.length === 0 && <p style={{ color: 'var(--gray)' }}>Noch keine Termine eingetragen.</p>}
      {wiederkehrend.length > 0 && <><div className="sk-sub-admin" style={{ fontSize: '0.65rem', letterSpacing: '2px', textTransform: 'uppercase', color: 'var(--gray)', margin: '1.5rem 0 0.75rem' }}>Regelmäßig</div>{wiederkehrend.map(karte)}</>}
      {kommend.length > 0 && <><div style={{ fontSize: '0.65rem', letterSpacing: '2px', textTransform: 'uppercase', color: 'var(--gray)', margin: '1.5rem 0 0.75rem' }}>Kommende Termine</div>{kommend.map(karte)}</>}
      {vorbei.length > 0 && <><div style={{ fontSize: '0.65rem', letterSpacing: '2px', textTransform: 'uppercase', color: 'var(--gray)', margin: '1.5rem 0 0.75rem' }}>Vergangen</div>{vorbei.map(karte)}</>}
    </div>
  );
}

// ===== ADMIN =====
function Admin() {
  const [tab, setTab] = useState('buch');
  const [mobileMenuOffen, setMobileMenuOffen] = useState(false);
  const [theme, setTheme] = useState(() => localStorage.getItem('savoca_admin_theme') || 'a');
  const [tischplanDatum, setTischplanDatum] = useState(new Date().toISOString().split('T')[0]);
  const [reservierungen, setReservierungen] = useState([]);
  const [bestellungen, setBestellungen] = useState([]);
  const [mitglieder, setMitglieder] = useState([]);
  const [ankuendigungen, setAnkuendigungen] = useState([]);
  const [anfragen, setAnfragen] = useState([]);
  const [ankForm, setAnkForm] = useState({ titel: '', text: '', farbe: 'gold', bis: '' });
  const [faqListe, setFaqListe] = useState([]);
  const [faqForm, setFaqForm] = useState({ frage: '', antwort: '' });
  const [faqEdit, setFaqEdit] = useState(null);
  const [anfrageTyp, setAnfrageTyp] = useState('eisgeburtstag');
  const [anfrageFilter, setAnfrageFilter] = useState('alle');   // „Alle“ als Standard (26.10.2026): „Jahr“ zeigte nur Termine im laufenden Kalenderjahr – Anfragen für 2027 waren unsichtbar
  const [qrTyp, setQrTyp] = useState(null);
  const [qrData, setQrData] = useState(null);
  const [qrCountdown, setQrCountdown] = useState(0);
  const [inbox, setInbox] = useState([]);
  const [inboxLaedt, setInboxLaedt] = useState(false);
  const [inboxFehler, setInboxFehler] = useState(null);
  const [inboxOffen, setInboxOffen] = useState(null);
  const [pw, setPw] = useState('');
  const [loginEmail, setLoginEmail] = useState('info@savoca-caffe.de');
  const [loginLaedt, setLoginLaedt] = useState(false);
  const [auth, setAuth] = useState(false);          // echter Login über Supabase Auth (Rolle admin), kein Passwort mehr im Code
  const [authGeprueft, setAuthGeprueft] = useState(false);
  const [mailStatus, setMailStatus] = useState(null);
  useEffect(() => {
    localStorage.removeItem('savoca_admin'); // altes Flag aus der Zeit vor dem echten Login
    supabase.auth.getSession().then(({ data }) => { setAuth(istAdminSession(data.session)); setAuthGeprueft(true); });
    const { data: l } = supabase.auth.onAuthStateChange((_e, session) => setAuth(istAdminSession(session)));
    return () => l.subscription.unsubscribe();
  }, []);
  const adminLogin = async () => {
    if (!loginEmail || !pw) return;
    setLoginLaedt(true);
    const { data, error } = await supabase.auth.signInWithPassword({ email: loginEmail.trim(), password: pw });
    setLoginLaedt(false);
    if (error) { alert('Anmeldung fehlgeschlagen: ' + (error.message === 'Invalid login credentials' ? 'E-Mail oder Passwort falsch' : error.message)); return; }
    if (!istAdminSession(data.session)) { await supabase.auth.signOut(); alert('Dieses Konto hat keine Admin-Rechte.'); return; }
    setPw(''); setAuth(true);
  };
  const adminLogout = async () => { await supabase.auth.signOut(); setAuth(false); };
  const [letzteAktualisierung, setLetzteAktualisierung] = useState(null);
  const [laedt, setLaedt] = useState(false);
  const [frisch, setFrisch] = useState(false);
  const [replyModal, setReplyModal] = useState(null);
  const [replySendStatus, setReplySendStatus] = useState(null);
  const [anfrageBestaetigenModal, setAnfrageBestaetigenModal] = useState(null);
  const [anfrageBestaetigenNotiz, setAnfrageBestaetigenNotiz] = useState('');
  const [anfrageNotizDraft, setAnfrageNotizDraft] = useState({});
  const [buchungsstoppFenster, setBuchungsstoppFenster] = useState({});

  const loadAnfragen = () =>
    supabase.from('anfragen').select('*').order('created_at', { ascending: false })
      .then(({ data }) => setAnfragen(data || []));

  const loadAnkuendigungen = () =>
    supabase.from('ankuendigungen').select('*').order('created_at', { ascending: false })
      .then(({ data }) => setAnkuendigungen(data || []));

  const loadFaqs = () => getFaqs(false).then(setFaqListe);

  const loadBuchungsstoppFenster = async () => {
    const { data } = await supabase.from('buchungsstopp_fenster').select('datum,start_zeit');
    const map = {};
    (data || []).forEach(r => { (map[r.datum] = map[r.datum] || []).push(r.start_zeit); });
    setBuchungsstoppFenster(map);
  };

  const toggleBuchungsstoppFenster = async (datum, startZeit) => {
    const aktuell = buchungsstoppFenster[datum] || [];
    const istGesperrt = aktuell.includes(startZeit);
    if (!window.confirm(`${datum.split('-').reverse().join('.')} · Fenster ab ${startZeit} Uhr wirklich ${istGesperrt ? 'wieder ÖFFNEN' : 'SPERREN'}?`)) return;
    if (istGesperrt) {
      await supabase.from('buchungsstopp_fenster').delete().eq('datum', datum).eq('start_zeit', startZeit);
    } else {
      await supabase.from('buchungsstopp_fenster').insert({ datum, start_zeit: startZeit });
    }
    setBuchungsstoppFenster(m => ({ ...m, [datum]: istGesperrt ? aktuell.filter(z => z !== startZeit) : [...aktuell, startZeit] }));
  };

  const loadAll = useCallback(async () => {
    setLaedt(true);
    await Promise.all([
      getReservierungen().then(setReservierungen),
      getBestellungen().then(setBestellungen),
      adminHeaders().then(h => h.Authorization ? fetch('/api/admin/mail-status', { headers: h }).then(r => r.ok ? r.json() : null) : null).then(setMailStatus).catch(() => setMailStatus(null)),
      getMitglieder().then(setMitglieder),
      loadAnkuendigungen(),
      loadAnfragen(),
      getFaqs(false).then(setFaqListe),
      loadBuchungsstoppFenster(),
    ]);
    setLetzteAktualisierung(new Date());
    setLaedt(false);
    setFrisch(true);
    setTimeout(() => setFrisch(false), 3000);
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!auth) return;
    loadAll();
    const interval = setInterval(loadAll, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [auth, loadAll]);

  useEffect(() => {
    if (!qrData) return;
    const timer = setInterval(() => {
      const verbleibend = Math.max(0, Math.round((new Date(qrData.ablaufAt) - new Date()) / 1000));
      setQrCountdown(verbleibend);
      if (verbleibend <= 0) { setQrData(null); setQrTyp(null); }
    }, 1000);
    return () => clearInterval(timer);
  }, [qrData]);

  const generiereQr = async (typ) => {
    const data = await generateQrToken(typ);
    setQrTyp(typ);
    setQrData({ token: data.token, ablaufAt: data.ablauf_am });
    setQrCountdown(600);
  };

  if (!authGeprueft) return null;
  if (!auth) {
    return (
      <div className="admin-login-wrap">
        <div className="admin-login-box">
          <div className="admin-login-logo">Savoca</div>
          <div className="admin-login-sub">Admin · Köln</div>
          <div className="form-group" style={{ marginBottom: '0.9rem' }}>
            <label>E-Mail</label>
            <input type="email" value={loginEmail} onChange={e => setLoginEmail(e.target.value)} autoComplete="username" />
          </div>
          <div className="form-group" style={{ marginBottom: '1.25rem' }}>
            <label>Passwort</label>
            <input type="password" value={pw} onChange={e => setPw(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') adminLogin(); }} autoComplete="current-password" autoFocus />
          </div>
          <button className="btn-primary" style={{ width: '100%' }} disabled={loginLaedt} onClick={adminLogin}>{loginLaedt ? 'Prüfe…' : 'Einloggen'}</button>
        </div>
      </div>
    );
  }

  const sidebarNav = [
    { key: 'buch',           icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><rect x="3.5" y="4.5" width="17" height="16" rx="2" /><path d="M3.5 9.5h17M8 3v3M16 3v3" /></svg>, label: 'Reservierungsbuch', badge: reservierungen.length },
    { key: 'tischplan',      icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><rect x="3.5" y="5.5" width="17" height="13" rx="2" /><path d="M3.5 11h17M12 5.5v13" /></svg>, label: 'Tischplan',         badge: null },
    { key: 'bestellungen',   icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M3 6h2l1.6 9.6a2 2 0 0 0 2 1.7h7.8a2 2 0 0 0 2-1.6L20 8H6" /><circle cx="9" cy="20" r="1.2" /><circle cx="17" cy="20" r="1.2" /></svg>, label: 'Kunden Bestellungen', badge: null, italic: true },
    { key: 'anfragen',       icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><rect x="4" y="6" width="16" height="14" rx="2" /><path d="M4 9h16M9 6V4h6v2" /></svg>, label: 'Anfragen',          badge: anfragen.filter(a => a.status === 'offen').length },
    { key: 'mitglieder',     icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><circle cx="9" cy="8" r="3" /><path d="M2.5 19c0-3 3-5 6.5-5s6.5 2 6.5 5" /><circle cx="17.5" cy="9" r="2.3" /><path d="M15.7 14.2c2.3.3 4 1.9 4 4.3" /></svg>, label: 'Mitglieder',        badge: mitglieder.length },
    { key: 'termine', icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><rect x="3.5" y="5" width="17" height="15" rx="2" /><path d="M3.5 10h17M8 3v4M16 3v4M8 14h3M13 14h3M8 17h3" /></svg>, label: 'Termine', badge: null },
    { key: 'ankuendigungen', icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M11 5 6 9H3v6h3l5 4V5Z" /><path d="M16 8.5a5 5 0 0 1 0 7" /></svg>, label: 'Ankündigungen',     badge: null },
    { key: 'faq',            icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><circle cx="12" cy="12" r="8.5" /><path d="M9.5 9.3a2.5 2.5 0 1 1 3.5 2.3c-.7.4-1 .8-1 1.6M12 17h.01" /></svg>, label: 'FAQ',               badge: null },
    { key: 'stempel',        icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><rect x="4" y="4" width="16" height="16" rx="2" /><path d="M8 4v16M4 9h4M4 14h4" /></svg>, label: 'Stempel-QR',        badge: null },
    { key: 'posteingang',    icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><rect x="3.5" y="5.5" width="17" height="13" rx="2" /><path d="m4 7 8 6 8-6" /></svg>, label: 'Posteingang',        badge: null },
  ];

  const statCards = [
    { label: 'Reservierungen',  value: reservierungen.length,                          farbe: 'lila'  },
    { label: 'Offene Anfragen', value: anfragen.filter(a => a.status === 'offen').length, farbe: 'gold'  },
    { label: 'Bestellungen',    value: bestellungen.length,                            farbe: 'gruen' },
    { label: 'Mitglieder',      value: mitglieder.length,                              farbe: 'rot'   },
  ];

  const currentTabLabel = sidebarNav.find(n => n.key === tab)?.label || '';

  const switchTheme = (t) => { setTheme(t); localStorage.setItem('savoca_admin_theme', t); };

  return (
    <div className={`admin-dashboard theme-${theme}`}>

      {/* ── Mobile Topbar ── */}
      <div className="admin-mobile-topbar">
        <button type="button" className="admin-hamburger" onClick={() => setMobileMenuOffen(true)} aria-label="Menü öffnen">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 6h16M4 12h16M4 18h16" /></svg>
        </button>
        <span className="admin-mobile-topbar-title">{currentTabLabel}</span>
      </div>

      {mobileMenuOffen && <div className="admin-sidebar-overlay" onClick={() => setMobileMenuOffen(false)} />}

      {/* ── Sidebar ── */}
      <aside className={`admin-sidebar${mobileMenuOffen ? ' offen' : ''}`}>
        <div className="admin-sidebar-logo">
          <div className="admin-sidebar-logo-title">Savoca</div>
          <div className="admin-sidebar-logo-sub">Admin · Köln</div>
          <button type="button" className="admin-sidebar-close" onClick={() => setMobileMenuOffen(false)} aria-label="Menü schließen">✕</button>
        </div>

        <div className="admin-sidebar-nav-label">Navigation</div>
        <nav className="admin-sidebar-nav">
          {sidebarNav.map(n => (
            <button
              key={n.key}
              className={`admin-nav-item${tab === n.key ? ' aktiv' : ''}`}
              onClick={() => { setTab(n.key); setMobileMenuOffen(false); }}
            >
              <span className="admin-nav-icon">{n.icon}</span>
              <span className="admin-nav-label-text" style={n.italic ? {fontStyle:'italic'} : {}}>{n.label}</span>
              {n.badge !== null && n.badge > 0 && (
                <span className="admin-nav-badge">{n.badge}</span>
              )}
            </button>
          ))}
        </nav>

        <div className="admin-theme-switcher">
          {['a','b','c'].map(t => (
            <button key={t} className={`admin-theme-btn${theme === t ? ' aktiv' : ''}`} onClick={() => switchTheme(t)}>
              {t.toUpperCase()}
            </button>
          ))}
        </div>

        <div className="admin-sidebar-bottom">
          <button className={`admin-refresh-btn${laedt ? ' laedt' : ''}`} onClick={loadAll} disabled={laedt}>
            <span className="refresh-icon">↻</span>
            {laedt ? 'Lädt...' : 'Aktualisieren'}
          </button>
          {letzteAktualisierung && (
            <div className={`admin-zuletzt${frisch ? ' admin-zuletzt-frisch' : ''}`}>
              zuletzt: {letzteAktualisierung.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} Uhr
            </div>
          )}
          {mailStatus && (
            <div style={{ fontSize: '0.68rem', margin: '0.4rem 0 0.6rem', color: mailStatus.fehler_heute > 0 ? '#e74c3c' : 'var(--gray)', textAlign: 'center' }} title={mailStatus.letzte_fehler?.map(f => `${f.zeit} ${f.an}: ${f.fehler}`).join('\n') || ''}>
              {mailStatus.fehler_heute > 0 ? `⚠️ ${mailStatus.fehler_heute} Mail-Fehler heute` : `✉️ Mails ok · ${mailStatus.absender}`}
            </div>
          )}
          <button className="admin-logout-btn" onClick={adminLogout}>Abmelden</button>
        </div>
      </aside>

      {/* ── Hauptbereich ── */}
      <main className="admin-main">

        {/* Topbar */}
        <div className="admin-topbar">
          <h1 className="admin-topbar-title">{currentTabLabel}</h1>
          <div className="admin-topbar-date">
            {new Date().toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          </div>
        </div>

        {/* Stat-Karten */}
        {tab !== 'buch' && (
          <div className="admin-stats">
            {statCards.map(s => (
              <div key={s.label} className={`admin-stat-card ${s.farbe}`}>
                <div className="admin-stat-number">{s.value}</div>
                <div className="admin-stat-label">{s.label}</div>
              </div>
            ))}
          </div>
        )}

        {/* Inhalt */}
        <div className="admin-content">

          {tab === 'buch' && (
            <ReservierungsBuch reservierungen={reservierungen} anfragen={anfragen} bestellungen={bestellungen} onSaved={() => { getReservierungen().then(setReservierungen); getBestellungen().then(setBestellungen); }} onDatumWaehlen={setTischplanDatum} buchungsstoppFenster={buchungsstoppFenster} onToggleBuchungsstoppFenster={toggleBuchungsstoppFenster} />
          )}

          {tab === 'tischplan' && (
            <AdminTischplan reservierungen={reservierungen} datum={tischplanDatum} setDatum={setTischplanDatum} />
          )}



          {tab === 'anfragen' && (() => {
            const ANFRAGE_TYPEN = [
              { key: 'eisgeburtstag',    icon: '🍦', label: 'Eisgeburtstag' },
              { key: 'kindergeburtstag', icon: '🎂', label: 'Kindergeburtstag' },
              { key: 'catering',         icon: '🍾', label: 'Catering' },
              { key: 'event',            icon: '🎉', label: 'Event' },
            ];
            const heute = new Date();
            const gefiltered = anfragen.filter(a => {
              if (a.typ !== anfrageTyp) return false;
              if (anfrageFilter === 'alle') return true;
              if (!a.datum) return anfrageFilter === 'tag' ? false : true;
              const d = new Date(a.datum + 'T12:00:00');
              if (anfrageFilter === 'tag') {
                return d.toDateString() === heute.toDateString();
              } else if (anfrageFilter === 'woche') {
                const start = new Date(heute); start.setDate(heute.getDate() - heute.getDay() + 1);
                const end = new Date(start); end.setDate(start.getDate() + 6);
                return d >= start && d <= end;
              } else if (anfrageFilter === 'monat') {
                return d.getMonth() === heute.getMonth() && d.getFullYear() === heute.getFullYear();
              } else if (/^j\d{4}$/.test(anfrageFilter)) {
                return d.getFullYear() === parseInt(anfrageFilter.slice(1), 10);   // Jahresknöpfe: laufendes + nächstes Jahr
              } else {
                return d.getFullYear() === heute.getFullYear();
              }
            });
            if (anfrageFilter === 'alle') gefiltered.sort((x, y) => (x.datum || '9999') < (y.datum || '9999') ? -1 : (x.datum || '9999') > (y.datum || '9999') ? 1 : 0);   // nach Termin, ohne Datum ans Ende
            const typLabel = (typ) => ANFRAGE_TYPEN.find(t => t.key === typ)?.icon + ' ' + ANFRAGE_TYPEN.find(t => t.key === typ)?.label || typ;
            return (
              <div>
                {/* Typ-Reiter */}
                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                  {ANFRAGE_TYPEN.map(t => {
                    const anzahl = anfragen.filter(a => a.typ === t.key && a.status === 'offen').length;
                    return (
                      <button key={t.key} onClick={() => setAnfrageTyp(t.key)}
                        style={{ padding: '0.45rem 1rem', borderRadius: '20px', border: '1px solid var(--gold)', background: anfrageTyp === t.key ? 'var(--gold)' : 'transparent', color: anfrageTyp === t.key ? '#fff' : 'var(--gold)', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                        {t.icon} {t.label}
                        {anzahl > 0 && <span style={{ background: anfrageTyp === t.key ? 'rgba(255,255,255,0.3)' : 'var(--gold)', color: '#fff', borderRadius: '10px', padding: '0.05rem 0.45rem', fontSize: '0.72rem', fontWeight: 700 }}>{anzahl}</span>}
                      </button>
                    );
                  })}
                </div>
                {/* Zeitfilter */}
                <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '1.5rem' }}>
                  {['alle', 'tag', 'woche', 'monat', `j${heute.getFullYear()}`, `j${heute.getFullYear() + 1}`].map(f => (
                    <button key={f} onClick={() => setAnfrageFilter(f)}
                      style={{ padding: '0.3rem 0.8rem', borderRadius: '20px', border: '1px solid #999', background: anfrageFilter === f ? '#555' : 'transparent', color: anfrageFilter === f ? '#fff' : '#999', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}>
                      {f === 'alle' ? 'Alle' : f === 'tag' ? 'Tag' : f === 'woche' ? 'Woche' : f === 'monat' ? 'Monat' : f.slice(1)}
                    </button>
                  ))}
                </div>
                {/* Anfragen-Cards */}
                {gefiltered.length === 0 && <p style={{ color: 'var(--gray)' }}>Keine {typLabel(anfrageTyp).replace(/^\S+\s/, '')}-Anfragen für diesen Zeitraum.</p>}
                {gefiltered.map(a => {
                  let details = {};
                  try { details = JSON.parse(a.details || '{}'); } catch {}
                  return (
                    <div key={a.id} className="card" style={{ marginBottom: '0.75rem' }}>
                      <div className="card-header">
                        <div>
                          <div className="card-title">{a.name} — {a.datum}{a.uhrzeit ? ` um ${a.uhrzeit} Uhr` : ''}</div>
                          <div style={{ color: 'var(--gray)', fontSize: '0.85rem', marginTop: '0.25rem' }}>{a.email}{a.telefon ? ` · ${a.telefon}` : ''}</div>
                          {Object.entries(details).filter(([,v]) => v).map(([k, v]) => (
                            <div key={k} style={{ fontSize: '0.82rem', marginTop: '0.2rem' }}><strong>{k.replace(/_/g, ' ')}:</strong> {v}</div>
                          ))}
                          {a.notiz && <div style={{ marginTop: '0.4rem', fontSize: '0.85rem', fontStyle: 'italic' }}>"{a.notiz}"</div>}
                        </div>
                        <span className={`badge ${a.status === 'offen' ? 'badge-pending' : (a.status === 'bestätigt' || a.status === 'beantwortet') ? 'badge-confirmed' : 'badge-cancelled'}`}>{a.status}</span>
                      </div>
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                        <button className="btn-ghost" onClick={() => { setAnfrageBestaetigenModal(a); setAnfrageBestaetigenNotiz(a.admin_notiz || ''); }}>✓ Bestätigen</button>
                        <button className="btn-ghost" onClick={async () => { await supabase.from('anfragen').update({ status: 'abgesagt' }).eq('id', a.id); loadAnfragen(); }}>✗ Absagen</button>
                        <button className="btn-ghost" style={{ color: 'var(--gray)' }} onClick={async () => { await supabase.from('anfragen').delete().eq('id', a.id); loadAnfragen(); }}>Löschen</button>
                        {a.typ === 'catering' && (
                          <button className="btn-ghost" style={{ color: '#b8963e', borderColor: '#b8963e' }} onClick={async () => {
                            const artikelText = Object.entries(details).filter(([,v]) => v).map(([k,v]) => `${k.replace(/_/g,' ')}: ${v}`).join(', ') || 'Catering';
                            await addBestellung({ name: a.name, datum: a.datum || '', uhrzeit: a.uhrzeit || '', artikel: artikelText + (a.notiz ? ` | ${a.notiz}` : ''), status: 'neu', gesamt: 0 });
                            getBestellungen().then(setBestellungen);
                          }}>🛒 → Bestellung</button>
                        )}
                        {a.email && (
                          <button className="btn-ghost" onClick={() => {
                            const tl = typLabel(a.typ).replace(/^\S+\s/, '');
                            setReplyModal({
                              anfrageId: a.id,   // nach erfolgreichem Versand springt die Anfrage auf „beantwortet“
                              an: a.email,
                              name: a.name,
                              betreff: `Ihre Anfrage bei Savoca Köln - ${tl}`,
                              text: `Liebe/r ${a.name},\n\nvielen Dank für Ihre Anfrage!\n\nIhre Angaben:\nDatum: ${a.datum}${a.uhrzeit ? `\nUhrzeit: ${a.uhrzeit} Uhr` : ''}\n\nWir melden uns in Kürze bei Ihnen.\n\nMit freundlichen Grüßen\nIhr Savoca-Team\nGottesweg 139, 50939 Köln`,
                            });
                            setReplySendStatus(null);
                          }}>📧 E-Mail</button>
                        )}
                        <button className="btn-ghost" onClick={() => {
                          const tl = typLabel(a.typ);
                          const w = window.open('', '_blank', 'width=650,height=500');
                          w.document.write(`<html><head><title>Anfrage – ${a.name}</title><style>body{font-family:Georgia,serif;padding:2rem;max-width:580px;margin:0 auto;color:#111}h2{border-bottom:2px solid #b8963e;padding-bottom:0.5rem;color:#b8963e}p{margin:0.3rem 0}strong{color:#333}hr{border:none;border-top:1px solid #ddd;margin:1rem 0}.typ{font-size:0.75rem;letter-spacing:2px;text-transform:uppercase;color:#b8963e;margin-bottom:0.3rem}.no-print{position:fixed;top:12px;right:12px;background:#5C3768;color:#fff;border:none;border-radius:6px;padding:0.4rem 1rem;cursor:pointer}@media print{.no-print{display:none}}</style></head><body><button class="no-print" onclick="window.close()">✕ Schließen</button><div class="typ">${tl}</div><h2>${a.name}</h2><p>${a.datum}${a.uhrzeit ? ` um ${a.uhrzeit} Uhr` : ''}</p><hr/>${a.email ? `<p>📧 ${a.email}</p>` : ''}${a.telefon ? `<p>📞 ${a.telefon}</p>` : ''}<hr/>${Object.entries(details).filter(([,v])=>v).map(([k,v])=>`<p><strong>${k.replace(/_/g,' ')}:</strong> ${v}</p>`).join('')}${a.notiz ? `<p><em>"${a.notiz}"</em></p>` : ''}<hr/><p>Status: <strong>${a.status}</strong></p><script>window.onload=()=>window.print();window.onafterprint=()=>window.close();<\/script></body></html>`);
                          w.document.close();
                        }}>🖨️ Drucken</button>
                      </div>
                      <div style={{ marginTop:'0.75rem', borderTop:'1px solid var(--border)', paddingTop:'0.75rem' }}>
                        <label style={{ fontSize:'0.7rem', letterSpacing:'1px', textTransform:'uppercase', color:'var(--gray)', fontWeight:600, display:'block', marginBottom:'0.4rem' }}>📋 Admin-Notiz</label>
                        <textarea
                          value={anfrageNotizDraft[a.id] !== undefined ? anfrageNotizDraft[a.id] : (a.admin_notiz || '')}
                          onChange={ev => setAnfrageNotizDraft(m => ({ ...m, [a.id]: ev.target.value }))}
                          onBlur={async ev => {
                            const val = ev.target.value;
                            await supabase.from('anfragen').update({ admin_notiz: val || null }).eq('id', a.id);
                          }}
                          placeholder="Interne Notiz (wird bei Bestätigung ins Reservierungsbuch übernommen)..."
                          rows={2}
                          style={{ width:'100%', boxSizing:'border-box', fontSize:'0.85rem', padding:'0.5rem', resize:'vertical', borderRadius:'6px' }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}

          {tab === 'bestellungen' && (
            <AdminBestellungen
              bestellungen={bestellungen.filter(b => b.status !== 'gelöscht')}
              onSaved={() => getBestellungen().then(setBestellungen)}
            />
          )}

          {tab === 'mitglieder' && (
            <div>
              {mitglieder.length === 0 && <p style={{ color: 'var(--gray)' }}>Keine Mitglieder vorhanden.</p>}
              {mitglieder.map(m => (
                <div key={m.id} className="card">
                  <div className="card-header">
                    <div>
                      <div className="card-title">{m.name}</div>
                      <div style={{ color: 'var(--gray)', fontSize: '0.85rem', marginTop: '0.25rem' }}>{m.email} · {m.telefon}</div>
                      {m.geburtsdatum && <div style={{ fontSize: '0.85rem', marginTop: '0.25rem' }}>Geb.: {m.geburtsdatum}</div>}
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: '2rem', fontFamily: 'Playfair Display, serif', fontWeight: '700', color: 'var(--gold)' }}>{m.punkte || 0}</div>
                      <div style={{ fontSize: '0.62rem', color: 'var(--gray)', letterSpacing: '2px', textTransform: 'uppercase' }}>Punkte</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button className="btn-ghost" onClick={async () => {
                      await fetch('/api/confirm-member', { method: 'POST', headers: { 'Content-Type': 'application/json', ...(await adminHeaders()) }, body: JSON.stringify({ name: m.name, email: m.email }) });
                      alert(`Bestätigung an ${m.email} gesendet!`);
                    }}>✓ Bestätigung senden</button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {tab === 'termine' && <AdminTermine />}

          {tab === 'ankuendigungen' && (
            <div>
              <div className="card" style={{ marginBottom: '2rem' }}>
                <div style={{ fontSize: '0.65rem', letterSpacing: '2px', textTransform: 'uppercase', color: 'var(--gray)', marginBottom: '1rem' }}>Neues Popup erstellen</div>
                <div className="form-grid" style={{ marginBottom: '1rem' }}>
                  <div className="form-group">
                    <label>Titel *</label>
                    <input value={ankForm.titel} onChange={e => setAnkForm(f => ({ ...f, titel: e.target.value }))} placeholder="z.B. Heute geschlossen" maxLength={80} />
                  </div>
                  <div className="form-group">
                    <label>Farbe</label>
                    <select value={ankForm.farbe} onChange={e => setAnkForm(f => ({ ...f, farbe: e.target.value }))}>
                      <option value="gold">Gold (Aktion)</option>
                      <option value="gruen">Grün (Info)</option>
                      <option value="rot">Rot (Wichtig)</option>
                    </select>
                  </div>
                </div>
                <div className="form-group" style={{ marginBottom: '1rem' }}>
                  <label>Sichtbar bis (optional – danach verschwindet das Popup von selbst)</label>
                  <input type="date" value={ankForm.bis} onChange={e => setAnkForm(f => ({ ...f, bis: e.target.value }))} style={{ width: '100%', minWidth: 0 }} />
                </div>
                <div className="form-group" style={{ marginBottom: '1rem' }}>
                  <label>Nachricht *</label>
                  <textarea value={ankForm.text} onChange={e => setAnkForm(f => ({ ...f, text: e.target.value }))} placeholder="z.B. Wir haben heute ab 15 Uhr geschlossen." maxLength={300} />
                </div>
                <button className="btn-primary" onClick={async () => {
                  if (!ankForm.titel || !ankForm.text) return alert('Titel und Nachricht ausfüllen.');
                  await supabase.from('ankuendigungen').insert({ titel: ankForm.titel, text: ankForm.text, farbe: ankForm.farbe, bis: ankForm.bis || null, aktiv: true });
                  setAnkForm({ titel: '', text: '', farbe: 'gold', bis: '' });
                  loadAnkuendigungen();
                }}>Popup veröffentlichen</button>
              </div>
              {ankuendigungen.length === 0 && <p style={{ color: 'var(--gray)' }}>Keine Ankündigungen vorhanden.</p>}
              {ankuendigungen.map(a => (
                <div key={a.id} className="card">
                  <div className="card-header">
                    <div>
                      <div className="card-title">{a.titel}</div>
                      <div style={{ color: 'var(--gray)', fontSize: '0.85rem', marginTop: '0.25rem' }}>{a.text}</div>
                      {a.bis && <div style={{ color: 'var(--gray)', fontSize: '0.75rem', marginTop: '0.25rem' }}>sichtbar bis {a.bis.split('-').reverse().join('.')}</div>}
                    </div>
                    <span className={`badge ${a.aktiv ? 'badge-confirmed' : 'badge-cancelled'}`}>{a.aktiv ? 'Aktiv' : 'Inaktiv'}</span>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button className="btn-ghost" onClick={async () => {
                      await supabase.from('ankuendigungen').update({ aktiv: !a.aktiv }).eq('id', a.id);
                      loadAnkuendigungen();
                    }}>{a.aktiv ? 'Deaktivieren' : 'Aktivieren'}</button>
                    <button className="btn-ghost" style={{ color: 'var(--gray)' }} onClick={async () => {
                      await supabase.from('ankuendigungen').delete().eq('id', a.id);
                      loadAnkuendigungen();
                    }}>Löschen</button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {tab === 'faq' && (
            <div>
              {/* Neuen FAQ anlegen / bearbeiten */}
              <div className="card" style={{ marginBottom: '2rem' }}>
                <div style={{ fontSize: '0.65rem', letterSpacing: '2px', textTransform: 'uppercase', color: 'var(--gray)', marginBottom: '1rem' }}>
                  {faqEdit ? 'FAQ bearbeiten' : 'Neuen FAQ hinzufügen'}
                </div>
                <div className="form-group" style={{ marginBottom: '0.75rem' }}>
                  <label>Frage *</label>
                  <input
                    value={faqEdit ? faqEdit.frage : faqForm.frage}
                    onChange={e => faqEdit ? setFaqEdit(f => ({ ...f, frage: e.target.value })) : setFaqForm(f => ({ ...f, frage: e.target.value }))}
                    placeholder="z.B. Kann ich spontan reservieren?"
                    maxLength={200}
                  />
                </div>
                <div className="form-group" style={{ marginBottom: '1rem' }}>
                  <label>Antwort *</label>
                  <textarea
                    value={faqEdit ? faqEdit.antwort : faqForm.antwort}
                    onChange={e => faqEdit ? setFaqEdit(f => ({ ...f, antwort: e.target.value })) : setFaqForm(f => ({ ...f, antwort: e.target.value }))}
                    placeholder="Die Antwort auf die Frage..."
                    maxLength={600}
                    rows={3}
                  />
                </div>
                <div style={{ display: 'flex', gap: '0.75rem' }}>
                  <button className="btn-primary" onClick={async () => {
                    const entry = faqEdit || faqForm;
                    if (!entry.frage.trim() || !entry.antwort.trim()) return alert('Frage und Antwort ausfüllen.');
                    await saveFaq(entry);
                    setFaqForm({ frage: '', antwort: '' });
                    setFaqEdit(null);
                    loadFaqs();
                  }}>{faqEdit ? 'Speichern' : 'Hinzufügen'}</button>
                  {faqEdit && (
                    <button className="btn-ghost" onClick={() => setFaqEdit(null)}>Abbrechen</button>
                  )}
                </div>
              </div>

              {/* Liste aller FAQs */}
              {faqListe.length === 0 && <p style={{ color: 'var(--gray)' }}>Noch keine FAQs vorhanden.</p>}
              {faqListe.map(f => (
                <div key={f.id} className="card">
                  <div className="card-header">
                    <div style={{ flex: 1 }}>
                      <div className="card-title" style={{ marginBottom: '0.3rem' }}>{f.frage}</div>
                      <div style={{ color: 'var(--gray)', fontSize: '0.85rem' }}>{f.antwort}</div>
                    </div>
                    <span className={`badge ${f.aktiv ? 'badge-confirmed' : 'badge-cancelled'}`} style={{ flexShrink: 0 }}>
                      {f.aktiv ? 'Aktiv' : 'Inaktiv'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <button className="btn-ghost" onClick={() => setFaqEdit({ ...f })}>✏️ Bearbeiten</button>
                    <button className="btn-ghost" onClick={async () => {
                      await saveFaq({ ...f, aktiv: !f.aktiv });
                      loadFaqs();
                    }}>{f.aktiv ? 'Deaktivieren' : 'Aktivieren'}</button>
                    <button className="btn-ghost" style={{ color: 'var(--gray)' }} onClick={async () => {
                      if (window.confirm('FAQ löschen?')) { await deleteFaq(f.id); loadFaqs(); }
                    }}>Löschen</button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {tab === 'posteingang' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <div style={{ fontSize: '0.65rem', letterSpacing: '2px', textTransform: 'uppercase', color: 'var(--gray)' }}>info@savoca-caffe.de — letzte 30 Mails</div>
                <button className="btn-ghost" style={{ fontSize: '0.8rem' }} onClick={async () => {
                  setInboxLaedt(true); setInboxFehler(null);
                  try {
                    const r = await fetch('/api/inbox', { headers: await adminHeaders() });
                    const d = await r.json();
                    if (d.ok) setInbox(d.emails);
                    else setInboxFehler(d.error);
                  } catch { setInboxFehler('Verbindungsfehler'); }
                  setInboxLaedt(false);
                }}>↻ Laden</button>
              </div>

              {inboxLaedt && <p style={{ color: 'var(--gray)' }}>Lade Posteingang…</p>}
              {inboxFehler && <p style={{ color: 'red' }}>Fehler: {inboxFehler}</p>}
              {!inboxLaedt && inbox.length === 0 && !inboxFehler && (
                <p style={{ color: 'var(--gray)' }}>Klick auf "↻ Laden" um Mails abzurufen.</p>
              )}

              {inbox.map(m => (
                <div key={m.id} className="card" style={{ marginBottom: '0.75rem', cursor: 'pointer' }} onClick={() => setInboxOffen(inboxOffen === m.id ? null : m.id)}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: '0.2rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.subject}</div>
                      <div style={{ fontSize: '0.78rem', color: 'var(--gray)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.sender}</div>
                    </div>
                    <div style={{ fontSize: '0.72rem', color: 'var(--gray-light)', flexShrink: 0 }}>{m.date ? new Date(m.date).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''}</div>
                  </div>
                  {inboxOffen === m.id && (
                    <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid var(--border)', fontSize: '0.85rem', whiteSpace: 'pre-wrap', color: '#444', lineHeight: '1.6' }}>
                      {m.body || '(kein Text)'}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {tab === 'stempel' && (
            <div>
              <div className="card" style={{ maxWidth: '420px' }}>
                <div style={{ fontSize: '0.65rem', letterSpacing: '2px', textTransform: 'uppercase', color: 'var(--gray)', marginBottom: '1.5rem' }}>
                  QR-Code generieren — 10 Min. gültig, einmalig einlösbar
                </div>
                <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem' }}>
                  <button
                    className={qrTyp === 'kaffee' ? 'btn-primary' : 'btn-ghost'}
                    onClick={() => generiereQr('kaffee')}
                  >☕ Kaffee-Stempel</button>
                  <button
                    className={qrTyp === 'essen' ? 'btn-primary' : 'btn-ghost'}
                    onClick={() => generiereQr('essen')}
                  >🍽️ Essen-Stempel</button>
                </div>

                {qrData && (
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ background: '#fff', display: 'inline-block', padding: '16px', borderRadius: '12px', boxShadow: '0 2px 12px rgba(0,0,0,0.1)' }}>
                      <QRCodeSVG value={`https://reservierung.savoca-caffe.de/stempel?token=${qrData.token}`} size={220} />
                    </div>
                    <div style={{ marginTop: '1rem', fontSize: '1rem', fontWeight: 700, color: qrCountdown < 60 ? '#c0392b' : 'var(--gray)' }}>
                      ⏱ {Math.floor(qrCountdown / 60)}:{String(qrCountdown % 60).padStart(2, '0')} Min.
                    </div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--gray)', marginTop: '0.4rem' }}>
                      {qrTyp === 'kaffee' ? '☕ Kaffee-Stempel' : '🍽️ Essen-Stempel'} · Einmalig einlösbar
                    </div>
                    <button className="btn-ghost" style={{ marginTop: '1rem', fontSize: '0.8rem' }} onClick={() => { setQrData(null); setQrTyp(null); }}>
                      ✕ Schließen
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

        </div>
      </main>

      {anfrageBestaetigenModal && (() => {
        const a = anfrageBestaetigenModal;
        let details = {};
        try { details = JSON.parse(a.details || '{}'); } catch {}
        const personen = details.personen || details.personen_kinder || details.gaeste || '';
        const kundenNotizTeile = [
          a.notiz ? `Kunden-Notiz: "${a.notiz}"` : '',
          Object.entries(details).filter(([,v]) => v).map(([k,v]) => `${k.replace(/_/g,' ')}: ${v}`).join(' · ')
        ].filter(Boolean).join(' | ');

        const doBestaetigen = async () => {
          await supabase.from('anfragen').update({ status: 'bestätigt' }).eq('id', a.id);
          await addReservierung({
            name: a.name, email: a.email || '', telefon: a.telefon || '',
            datum: a.datum || '', uhrzeit: a.uhrzeit || '', personen: personen || '?',
            bemerkung: kundenNotizTeile, admin_notiz: anfrageBestaetigenNotiz || null,
            status: 'bestätigt', tisch_id: null, bereich: null, anfrage_typ: a.typ,
          });
          if (a.email) {
            fetch('/api/confirm-reservation', { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ name:a.name, email:a.email, datum:a.datum, uhrzeit:a.uhrzeit||'', personen:personen||'?' }) }).catch(() => {});
          }
          setAnfrageBestaetigenModal(null);
          loadAnfragen();
          getReservierungen().then(setReservierungen);
        };

        return (
          <div className="res-modal-overlay" onClick={() => setAnfrageBestaetigenModal(null)}>
            <div className="res-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '520px', padding: '2.5rem' }}>
              <div style={{ fontSize:'0.75rem', letterSpacing:'2px', textTransform:'uppercase', color:'var(--gold)', fontWeight:700, marginBottom:'1.5rem' }}>✓ Anfrage bestätigen</div>
              <div style={{ background:'var(--surface)', borderRadius:'8px', padding:'1rem', marginBottom:'1.5rem', fontSize:'0.9rem', lineHeight:'1.7' }}>
                <div><strong>{a.name}</strong></div>
                <div style={{ color:'var(--gray)' }}>{a.datum}{a.uhrzeit ? ` · ${a.uhrzeit} Uhr` : ''}{personen ? ` · ${personen} P.` : ''}</div>
                {a.notiz && <div style={{ marginTop:'0.5rem', fontStyle:'italic', fontSize:'0.85rem' }}>„{a.notiz}"</div>}
              </div>
              <div className="form-group" style={{ marginBottom:'1.5rem' }}>
                <label style={{ fontSize:'0.85rem', fontWeight:600, letterSpacing:'1px', textTransform:'uppercase', color:'var(--gray)', display:'block', marginBottom:'0.5rem' }}>📋 Interne Notizen für Mitarbeiter</label>
                <textarea
                  value={anfrageBestaetigenNotiz}
                  onChange={e => setAnfrageBestaetigenNotiz(e.target.value)}
                  rows={4}
                  placeholder="z.B. Tisch dekorieren, Torte bereitstellen, Allergien beachten..."
                  autoFocus
                  style={{ width:'100%', boxSizing:'border-box', fontSize:'0.95rem', padding:'0.75rem', resize:'vertical' }}
                />
              </div>
              <div style={{ display:'flex', gap:'0.75rem' }}>
                <button className="btn-dark" style={{ fontSize:'1rem', padding:'0.85rem 1.5rem' }} onClick={doBestaetigen}>✓ Bestätigen & ins Reservierungsbuch</button>
                <button className="btn-ghost" onClick={() => setAnfrageBestaetigenModal(null)}>Abbrechen</button>
              </div>
            </div>
          </div>
        );
      })()}

      {replyModal && (
        <div className="res-modal-overlay" onClick={() => setReplyModal(null)}>
          <div className="res-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '680px', width: '95vw', padding: '2.5rem' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'2rem' }}>
              <div style={{ fontSize:'0.75rem', letterSpacing:'2px', textTransform:'uppercase', color:'var(--gold)', fontWeight:700 }}>📧 E-Mail senden</div>
              <button className="btn-ghost" style={{ fontSize:'1rem', padding:'0.4rem 0.9rem' }} onClick={() => setReplyModal(null)}>✕</button>
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:'1.25rem' }}>
              <div className="form-group">
                <label style={{ fontSize:'0.85rem', marginBottom:'0.5rem', display:'block', fontWeight:600, letterSpacing:'1px', textTransform:'uppercase', color:'var(--gray)' }}>An</label>
                <input value={replyModal.an} disabled style={{ opacity:0.6, fontSize:'1rem', padding:'0.8rem 1rem' }} />
              </div>
              <div className="form-group">
                <label style={{ fontSize:'0.85rem', marginBottom:'0.5rem', display:'block', fontWeight:600, letterSpacing:'1px', textTransform:'uppercase', color:'var(--gray)' }}>Betreff</label>
                <input value={replyModal.betreff} onChange={e => setReplyModal(m => ({ ...m, betreff: e.target.value }))} maxLength={120} style={{ fontSize:'1rem', padding:'0.8rem 1rem' }} />
              </div>
              <div className="form-group">
                <label style={{ fontSize:'0.85rem', marginBottom:'0.5rem', display:'block', fontWeight:600, letterSpacing:'1px', textTransform:'uppercase', color:'var(--gray)' }}>Nachricht</label>
                <textarea
                  value={replyModal.text}
                  onChange={e => setReplyModal(m => ({ ...m, text: e.target.value }))}
                  rows={10}
                  ref={el => { if (el && !el.dataset.positioned) { el.dataset.positioned = '1'; const pos = replyModal.text.indexOf('\n\n') + 2; el.focus(); el.setSelectionRange(pos, pos); } }}
                  style={{ resize:'vertical', fontFamily:'Georgia,serif', fontSize:'1rem', lineHeight:'1.7', padding:'1rem', width:'100%', boxSizing:'border-box' }}
                />
              </div>
              {replySendStatus === 'ok' && <div style={{ color:'#2d6a4f', fontSize:'1rem', fontWeight:600, padding:'0.75rem', background:'#f0fdf4', borderRadius:'8px' }}>✓ Mail erfolgreich gesendet!</div>}
              {replySendStatus === 'error' && <div style={{ color:'red', fontSize:'1rem', padding:'0.75rem', background:'#fef2f2', borderRadius:'8px' }}>⚠️ Fehler beim Senden. Mail-Server erreichbar?</div>}
              <div style={{ display:'flex', gap:'1rem', marginTop:'0.5rem' }}>
                <button className="btn-dark" style={{ fontSize:'1rem', padding:'0.9rem 2rem' }} onClick={async () => {
                  setReplySendStatus(null);
                  try {
                    const res = await fetch('/api/reply-anfrage', { method:'POST', headers:{ 'Content-Type':'application/json', ...(await adminHeaders()) }, body: JSON.stringify({ an: replyModal.an, betreff: replyModal.betreff, text: replyModal.text }) });
                    if (res.ok) {
                      setReplySendStatus('ok');
                      // Mail ist raus → Anfrage gilt als beantwortet (nur wenn sie noch „offen“ war; „bestätigt“ bleibt bestätigt)
                      if (replyModal.anfrageId) { await supabase.from('anfragen').update({ status: 'beantwortet' }).eq('id', replyModal.anfrageId).eq('status', 'offen'); loadAnfragen(); }
                      setTimeout(() => setReplyModal(null), 1500);
                    }
                    else setReplySendStatus('error');
                  } catch { setReplySendStatus('error'); }
                }}>📨 Senden</button>
                <button className="btn-ghost" style={{ fontSize:'1rem', padding:'0.9rem 2rem' }} onClick={() => setReplyModal(null)}>Abbrechen</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ===== FAQ =====
function FAQ() {
  const [faqs, setFaqs] = useState([]);
  const [offen, setOffen] = useState(null);

  useEffect(() => {
    getFaqs(true).then(setFaqs);
  }, []);

  return (
    <div className="page-wrap">
      <div className="page-header">
        <h1 className="page-title">FAQ</h1>
        <div className="page-subtitle">Häufige Fragen · Savoca Köln</div>
        <div className="divider" />
      </div>
      <div className="faq-list">
        {faqs.length === 0 && (
          <p style={{ color: 'var(--gray)', textAlign: 'center', padding: '2rem 0' }}>Noch keine Einträge vorhanden.</p>
        )}
        {faqs.map((f, i) => (
          <div key={f.id} className={`faq-item${offen === i ? ' offen' : ''}`}>
            <button className="faq-frage" onClick={() => setOffen(offen === i ? null : i)}>
              <span>{f.frage}</span>
              <span className="faq-chevron">{offen === i ? '−' : '+'}</span>
            </button>
            {offen === i && <div className="faq-antwort">{f.antwort}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

// ===== KAFFEE ASSISTENT =====
const KAFFEE_BAUM = {
  start: {
    text: 'Ciao! 👋 Wie kann ich dir helfen?',
    optionen: [
      { label: '📅 Tisch reservieren', next: 'reservieren_info' },
      { label: '⭐ Vorteile der Registrierung', next: 'registrierung_vorteile' },
      { label: '❓ Wie funktioniert die App?', next: 'app_erklaerung' },
    ]
  },
  reservieren_info: {
    text: 'So einfach geht\'s:\n\n1. Datum + Uhrzeit wählen\n2. Name, E-Mail, Telefon eingeben\n3. Absenden — fertig!\n\nDein Tisch wird vom Team zugewiesen.',
    optionen: [
      { label: '➡️ Jetzt reservieren', aktion: 'reservierung' },
      { label: '← Zurück', next: 'start' },
    ]
  },
  registrierung_vorteile: {
    text: 'Als Mitglied genießt du:\n\n✓ Reservierungen jederzeit einsehen\n✓ Status-Updates direkt in der App\n✓ Browser-Benachrichtigung bei Bestätigung\n✓ Punkte sammeln (bald verfügbar)',
    optionen: [
      { label: '🎉 Jetzt registrieren', aktion: 'mitglied-register' },
      { label: '← Zurück', next: 'start' },
    ]
  },
  app_erklaerung: {
    text: 'Savoca ist dein digitales Café-Erlebnis:\n\n• Reservierungen online buchen\n• Sonderwünsche angeben\n• Als Mitglied alles im Blick behalten',
    optionen: [
      { label: '📅 Zur Reservierung', aktion: 'reservierung' },
      { label: '← Zurück', next: 'start' },
    ]
  },
  form_start: {
    text: 'Womit kann ich dir helfen?',
    optionen: [
      { label: '📋 Was bedeuten die Tabs?', next: 'form_tabs' },
      { label: '📝 Pflichtfelder erklärt', next: 'form_felder' },
      { label: '📩 Was ist eine Anfrage?', next: 'form_anfrage' },
    ]
  },
  form_tabs: {
    text: '4 Tabs stehen zur Auswahl:\n\n🍽 Tisch — normale Reservierung\n🎂 Kindergeburtstag — ab 10 Kinder\n🍾 Catering — Events außer Haus\n🎉 Event — private Veranstaltungen',
    optionen: [{ label: '← Zurück', next: 'form_start' }]
  },
  form_felder: {
    text: 'Pflichtfelder:\n\n• Name: dein vollständiger Name\n• E-Mail: für die Bestätigung\n• Telefon: für Rückfragen\n• Personen: Anzahl der Gäste\n• Datum + Uhrzeit: gewünschter Termin',
    optionen: [{ label: '← Zurück', next: 'form_start' }]
  },
  form_anfrage: {
    text: 'Bei Kindergeburtstag, Catering und Event handelt es sich um Anfragen.\n\nDas Team meldet sich per E-Mail oder Telefon bei dir.',
    optionen: [{ label: '← Zurück', next: 'form_start' }]
  },
};

function KaffeeAssistent({ setPage, page }) {
  const [offen, setOffen] = useState(false);
  const [schritt, setSchritt] = useState('start');
  const handleOpen = () => {
    setSchritt(page === 'reservierung' ? 'form_start' : 'start');
    setOffen(true);
  };

  const node = KAFFEE_BAUM[schritt] || KAFFEE_BAUM.start;

  return (
    <div className="kaffee-assist-wrap">
      {offen && (
        <div className="kaffee-assist-bubble">
          <button className="kaffee-assist-close" onClick={() => setOffen(false)}>✕</button>
          <p className="kaffee-assist-text">{node.text}</p>
          <div className="kaffee-assist-optionen">
            {node.optionen.map((o, i) => (
              <button
                key={i}
                className="kaffee-assist-btn"
                onClick={() => {
                  if (o.aktion) { setPage(o.aktion); setOffen(false); }
                  else if (o.next) setSchritt(o.next);
                }}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>
      )}
      <button
        className={`kaffee-assist-fab${offen ? ' kaffee-assist-fab-aktiv' : ''}`}
        onClick={offen ? () => setOffen(false) : handleOpen}
        title="Hilfe"
        aria-label="Assistent öffnen"
      >
        <CupSVG className="kaffee-assist-cup" />
      </button>
    </div>
  );
}

// ===== PASSWORT RESET =====
function PasswortReset({ setPage }) {
  const [passwort, setPasswort] = useState('');
  const [passwort2, setPasswort2] = useState('');
  const [status, setStatus] = useState(null);

  return (
    <div className="page" style={{ maxWidth: '420px' }}>
      <div className="page-title">Neues Passwort</div>
      <div className="page-subtitle">Passwort festlegen</div>
      <div className="divider" />
      <div className="form-group" style={{ marginBottom: '1rem' }}>
        <label>Neues Passwort *</label>
        <input type="password" value={passwort} onChange={e => setPasswort(e.target.value)} placeholder="Min. 6 Zeichen" autoFocus />
      </div>
      <div className="form-group" style={{ marginBottom: '1.5rem' }}>
        <label>Passwort bestätigen *</label>
        <input type="password" value={passwort2} onChange={e => setPasswort2(e.target.value)} placeholder="Passwort wiederholen" />
      </div>
      {status && <div className={status.ok ? 'msg-success' : 'msg-error'} style={{ marginBottom: '1rem' }}>{status.msg}</div>}
      <button className="btn-primary" onClick={async () => {
        if (passwort.length < 6) { setStatus({ ok: false, msg: 'Passwort muss min. 6 Zeichen haben.' }); return; }
        if (passwort !== passwort2) { setStatus({ ok: false, msg: 'Passwörter stimmen nicht überein.' }); return; }
        const { error } = await supabase.auth.updateUser({ password: passwort });
        if (error) { setStatus({ ok: false, msg: 'Fehler: ' + error.message }); }
        else {
          setStatus({ ok: true, msg: '✓ Passwort geändert! Du wirst weitergeleitet...' });
          setTimeout(() => setPage('mitglied'), 2000);
        }
      }}>Passwort speichern</button>
    </div>
  );
}

// ===== LANDING PAGE =====
function LandingPage({ onReservieren }) {
  const reservierbareTermine = useReservierbareTermine();
  return (
    <div className="shimmer-landing">
      <div className="sl-glow-top" />
      <div className="shimmer-w1" />
      <div className="shimmer-w2" />

      {/* HERO */}
      <section className="sl-hero">
        <img src="/savoca-logo.png" alt="Savoca Bar Caffè" className="sl-hero-logo" />
        <span className="sl-hero-label">Bar · Caffè · Manifatura · Köln</span>
        <h1 className="sl-hero-title">
          Von cremigem Eis<br/>bis zur <em>perfekten</em><br/>Tasse Kaffee
        </h1>
        <p className="sl-hero-sub">Gottesweg 139 · 50939 Köln</p>
        <button className="sl-btn-gold" onClick={() => onReservieren('tisch')}>Tisch reservieren</button>
        <div className="sl-scroll-hint">
          <span>Entdecken</span>
          <div />
        </div>
      </section>

      {/* ÖFFNUNGSZEITEN */}
      <section className="sl-section sl-oez">
        <p className="sl-section-label">Wir sind für Sie da</p>
        <h2 className="sl-section-title">Öffnungszeiten</h2>
        <div className="sl-divider" />
        <div className="sl-oez-grid">
          <div className="sl-oez-item">
            <span className="sl-oez-tage">Täglich</span>
            <span className="sl-oez-zeit">08:00 – 23:00 Uhr</span>
          </div>
          <div className="sl-oez-item">
            <span className="sl-oez-tage">Frühstück</span>
            <span className="sl-oez-zeit">08:00 – 12:00 Uhr</span>
          </div>
          <p className="sl-oez-note">Bei schönem Wetter geöffnete Terrasse bis 22 Uhr</p>
          <p className="sl-oez-note">Die Küche hat eine Pause von 15 bis 16 Uhr – fragen Sie da nach unseren Snacks.</p>
        </div>
      </section>

      {/* GENUSS */}
      <section className="sl-section">
        <p className="sl-section-label">Handgemacht · Natürlich</p>
        <h2 className="sl-section-title">Der Kern unserer Leidenschaft</h2>
        <div className="sl-divider" />
        <div className="sl-genuss-grid">
          <div className="sl-genuss-card">
            <span className="sl-genuss-icon">🍦</span>
            <h3>Handgemachtes Eis</h3>
            <p>Jede Kugel wird mit Hingabe aus natürlichen Zutaten gefertigt – frei von Konservierungsstoffen, rein und authentisch.</p>
          </div>
          <div className="sl-genuss-card">
            <span className="sl-genuss-icon">☕</span>
            <h3>Perfekter Kaffee</h3>
            <p>Von der Röstung bis zur perfekten Crema – unser Kaffee ist eine Hommage an die italienische Barista-Kunst.</p>
          </div>
          <div className="sl-genuss-card">
            <span className="sl-genuss-icon">🍽</span>
            <h3>Savocaccias & Küche</h3>
            <p>Herzhafte Savocaccias, frische Salate und hausgemachte Pasta-Klassiker – täglich frisch zubereitet.</p>
          </div>
        </div>
      </section>

      {/* EVENTS */}
      <section className="sl-section sl-events">
        <p className="sl-section-label">Feiern & Anfragen</p>
        <h2 className="sl-section-title">Wir – für Dein Event</h2>
        <div className="sl-divider" />
        <p className="sl-events-sub">Unsere Leidenschaft auf Ihrem Fest.</p>
        <div className="sl-events-grid">
          {brunchAktiv() && (
            <button className="sl-event-card" onClick={() => onReservieren('brunch')}>
              <span className="sl-event-icon">🥂</span>
              <strong>Herbstbrunch 15.11.</strong>
              <span>Einmalig – draußen Herbst, drinnen Italien</span>
            </button>
          )}
          {reservierbareTermine.map(t => (
            <button key={t.id} className="sl-event-card" onClick={() => onReservieren('termin:' + t.id)}>
              <span className="sl-event-icon">🎟</span>
              <strong>{t.titel}</strong>
              <span>{terminKurz(t)}</span>
            </button>
          ))}
          <button className="sl-event-card" onClick={() => onReservieren('eisgeburtstag')}>
            <span className="sl-event-icon">🍦</span>
            <strong>Eisgeburtstag</strong>
            <span>Geburtstag im Eislabor</span>
          </button>
          <button className="sl-event-card" onClick={() => onReservieren('kindergeburtstag')}>
            <span className="sl-event-icon">🎂</span>
            <strong>Kindergeburtstag</strong>
            <span>Kinderparty im Savoca auf Anfrage</span>
          </button>
          <button className="sl-event-card" onClick={() => onReservieren('catering')}>
            <span className="sl-event-icon">🍾</span>
            <strong>Catering</strong>
            <span>Unsere Eiswelt kommt zu Ihnen</span>
          </button>
          <button className="sl-event-card" onClick={() => onReservieren('event')}>
            <span className="sl-event-icon">🎉</span>
            <strong>Event</strong>
            <span>Private Feiern & Firmenevents</span>
          </button>
        </div>
      </section>

      {/* CTA */}
      <section className="sl-section sl-cta">
        <h2 className="sl-cta-title">Bereit für ein<br/><em>unvergessliches</em><br/>Erlebnis?</h2>
        <button className="sl-btn-gold" onClick={() => onReservieren('tisch')}>Tisch reservieren</button>
        <p className="sl-cta-addr">Savoca Köln · Gottesweg 139 · 50939 Köln</p>
        <p className="sl-cta-tel">Tel: 0221 67811545</p>
      </section>
    </div>
  );
}

// ===== SPEISEKARTE =====
function SkZeile({ name, preis, glas, karaffe }) {
  return (
    <div className="sk-zeile">
      <span>{name}</span>
      {karaffe
        ? <span className="sk-preis sk-preis-doppel"><span>{glas}</span><span>{karaffe}</span></span>
        : <span className="sk-preis">{preis}</span>
      }
    </div>
  );
}

function Speisekarte() {
  const [aktiv, setAktiv] = useState(null);
  const karten = [
    { key: 'caffe',   titel: 'Bar · Caffè',        sub: 'Espresso · Wein · Aperitivo · Bier',          bild: 'https://static.wixstatic.com/media/11062b_32ed6e4582f64bcb991528fdc40d591c~mv2.jpg/v1/fill/w_800,h_600,al_c,q_80,enc_avif,quality_auto/Caf%C3%A9.jpg' },
    { key: 'gelati',  titel: 'Gelati Savoca',       sub: 'Handgemachtes Eis · Coppe · Spaghettieis',    bild: 'https://static.wixstatic.com/media/11062b_d3b9dbf75ae6438092310f2f6ff4b622~mv2.jpeg/v1/fill/w_800,h_600,al_c,q_80,enc_avif,quality_auto/Eis.jpeg' },
    { key: 'cucina',  titel: 'Cucina · Mercato',    sub: 'Frühstück · Pasta · Pizza · Insalate',        bild: 'https://static.wixstatic.com/media/84770f_71985fbf273740c0aac3a16a30d6455c~mv2.jpg/v1/fill/w_900,h_600,al_c,q_85,enc_avif,quality_auto/84770f_71985fbf273740c0aac3a16a30d6455c~mv2.jpg' },
  ];

  return (
    <div className="shimmer-res-wrapper">
      <div className="shimmer-w1" />
      <div className="shimmer-w2" />
      <div className="shimmer-swirl-1" />
      <div className="shimmer-swirl-2" />

      {!aktiv ? (
        <div className="sk-overview">
          <p className="sk-overview-label">Bar · Caffè · Manifatura · Mercato</p>
          <h2 className="sk-overview-title">Unsere Karte</h2>
          <div className="sk-overview-grid">
            {karten.map(k => (
              <div key={k.key} className="sk-menu-card" onClick={() => setAktiv(k.key)}>
                <div className="sk-menu-card-img" style={{ backgroundImage: `url('${k.bild}')` }} />
                <div className="sk-menu-card-body">
                  <h3 className="sk-menu-card-titel">{k.titel}</h3>
                  <p className="sk-menu-card-sub">{k.sub}</p>
                  <span className="sk-menu-card-btn">Karte ansehen →</span>
                </div>
              </div>
            ))}
          </div>
          <p className="sk-footer-note">Alle Preise in Euro · Allergene an der Theke erfragbar</p>
        </div>
      ) : (
        <div className="sk-detail">
          <button className="sk-back-btn" onClick={() => setAktiv(null)}>← Zurück zur Übersicht</button>
          <div className="sk-detail-card">

          {aktiv === 'caffe' && (<div>
            <div className="sk-detail-title">Bar · Caffè</div>
            <div className="sk-section">
            <div className="sk-kategorie">Caffè & anderes</div>
            {[
              ['Espresso', '2,20'],['Espresso Macchiato', '2,50'],['Espresso Doppio', '3,00'],
              ['Espresso Doppio Macchiato', '3,80'],['Caffè Lungo', '3,00'],['Cappuccino', '3,60'],
              ['Latte Macchiato', '4,00'],['Schoko Macchiato', '4,00'],['Caffellatte', '4,00'],
              ['Extra Shot', '0,60'],['Laktosefreie / vegane Milch', '0,00'],
            ].map(([n,p]) => <div key={n} className="sk-zeile"><span>{n}</span><span className="sk-preis">{p}</span></div>)}
            <div className="sk-kategorie" style={{marginTop:'1.5rem'}}>Warme Getränke</div>
            {[
              ['Heiße Milch', '2,50'],['Kakao', '4,00'],['Kakao mit Sahne', '4,60'],
              ['Chai Latte', '4,20'],['Tee (bio)', '3,20'],
            ].map(([n,p]) => <div key={n} className="sk-zeile"><span>{n}</span><span className="sk-preis">{p}</span></div>)}
            <div className="sk-zeile"><span>Limo (0,2 l) — Apfel · Rhabarber · Zitrone · Orange · Maracuja · Johannisbeere · Vio</span><span className="sk-preis">3,40</span></div>
            <div className="sk-zeile"><span>Cola (0,2 l) — Klassisch · Zero · Misch Masch</span><span className="sk-preis">3,50</span></div>
            <div className="sk-zeile"><span>Eistee (0,33 l)</span><span className="sk-preis">3,90</span></div>
            <div className="sk-zeile"><span>Wasser (0,25 l)</span><span className="sk-preis">2,90</span></div>
            <div className="sk-zeile"><span>Wasser (0,75 l)</span><span className="sk-preis">6,40</span></div>
            <div className="sk-kategorie" style={{marginTop:'1.5rem'}}>Bier</div>
            <div className="sk-zeile"><span>Moretti vom Fass (0,20 l)</span><span className="sk-preis">2,80</span></div>
            <div className="sk-zeile"><span>Moretti vom Fass (0,40 l)</span><span className="sk-preis">4,80</span></div>
            <div className="sk-zeile"><span>Kölsch (0,33 l)</span><span className="sk-preis">2,80</span></div>
            <div className="sk-zeile"><span>Reissdorf Radler (0,33 l)</span><span className="sk-preis">3,60</span></div>
            <div className="sk-zeile"><span>Tannenzäpfle (0,33 l)</span><span className="sk-preis">3,30</span></div>
            <div className="sk-zeile"><span>Alkoholfrei 0,0 % (0,33 l)</span><span className="sk-preis">3,20</span></div>
            <div className="sk-kategorie" style={{marginTop:'1.5rem'}}>Wein <span style={{fontWeight:400,fontSize:'0.75rem'}}>Glas · Karaffe 0,5 l</span></div>
            <div className="sk-zeile"><span>Hauswein Weiß | Rot | Rosé</span><span className="sk-preis sk-preis-doppel"><span>6,00</span><span>12,00</span></span></div>
            {[
              ['Sauvignon Blanc – Tenute Valdo','6,50','26,00'],['Pinot Grigio – reife Birne, exotisch','6,50','26,00'],
              ['Bianco – Tasca Regaleali','7,50','28,00'],['Riesling – Ripes & Stone','7,50','28,00'],
              ['Chardonnay – Heinrichshof','7,50','28,00'],['Auxerrois – Wolfsrudel','7,50','28,00'],
              ['Rosé Bumblebee – Himbeere, Erdbeere','7,50','26,00'],['Rosé Wolfsrudel – vollmundig','7,50','28,00'],
              ['Merlot – Tenute Valdo','6,50','26,00'],['Rosso – Tasca Regaleali','7,50','27,00'],
              ['Primitivo N83 – samtige Tannine','7,50','28,00'],['Nero d\'Avola – kräftig, tiefrot','8,00','30,00'],
              ['Ammasso Rosso IGT – Fruchtbombe','8,00','30,00'],
            ].map(([n,g,f]) => <div key={n} className="sk-zeile"><span>{n}</span><span className="sk-preis sk-preis-doppel"><span>{g}</span><span>{f}</span></span></div>)}
            <div className="sk-zeile"><span>Prosecco Valdo – Calice (0,20 l)</span><span className="sk-preis">6,50</span></div>
            <div className="sk-zeile"><span>Prosecco Valdo – Bottiglia (0,75 l)</span><span className="sk-preis">22,00</span></div>
            <div className="sk-zeile"><span>Prosecco Valdo Rosé (0,75 l)</span><span className="sk-preis">22,00</span></div>
            <div className="sk-kategorie" style={{marginTop:'1.5rem'}}>Aperitivo by Campari</div>
            {[['Campari Spritz','7,50'],['Aperol Spritz','7,50'],['Sarti Spritz','7,50'],['Limoncello Spritz','7,50']].map(([n,p]) => <div key={n} className="sk-zeile"><span>{n}</span><span className="sk-preis">{p}</span></div>)}
            {[['Negroni','9,20'],['Campari Orange','8,50'],['Campari Amalfi','8,50'],['Crodino (alkoholfrei)','6,20'],['Crodino Spritz','7,50']].map(([n,p]) => <div key={n} className="sk-zeile"><span>{n}</span><span className="sk-preis">{p}</span></div>)}
            <div className="sk-kategorie" style={{marginTop:'1.5rem'}}>Digestivo</div>
            {[['Grappa (2 cl)','3,50'],['Averna','3,50'],['Limoncello','3,50'],['Frangelico','3,50'],['Lorbeer Elexier','3,50']].map(([n,p]) => <div key={n} className="sk-zeile"><span>{n}</span><span className="sk-preis">{p}</span></div>)}
          </div>
          </div>)}

          {aktiv === 'gelati' && (<div>
            <div className="sk-detail-title">Gelati Savoca</div>
            <div className="sk-section">
            <div className="sk-kategorie">Gelati Savoca · Coppe</div>
            {[
              ['Spaghettieis Classico','7,50'],['Spaghettieis Carbonara','7,90'],
              ['Spaghettieis Frutti di Mare','7,90'],['Spaghettieis Pistazie','8,50'],
              ['Schoko-Becher','7,50'],['Amarena Becher','7,50'],
              ['Erdbeer-Becher','7,50'],['Bananensplit','7,50'],
            ].map(([n,p]) => <div key={n} className="sk-zeile"><span>{n}</span><span className="sk-preis">{p}</span></div>)}
            <div className="sk-kategorie" style={{marginTop:'1.5rem'}}>Gelati Savoca · im Becher</div>
            <div className="sk-zeile"><span>1 Kugel Eis</span><span className="sk-preis">1,80</span></div>
            <p className="sk-hinweis">* vegane und glutenfreie Optionen immer verfügbar</p>
            <div className="sk-kategorie" style={{marginTop:'1.5rem'}}>Eisgetränke</div>
            {[['Eiskaffee','5,90'],['Eisschokolade','5,90'],['Milchshake','4,50'],['Affogato','3,50'],['Iced Latte','4,00']].map(([n,p]) => <div key={n} className="sk-zeile"><span>{n}</span><span className="sk-preis">{p}</span></div>)}
            <div className="sk-kategorie" style={{marginTop:'1.5rem'}}>Extras</div>
            <div className="sk-zeile"><span>Sauce (Erdbeer · Schoko · Karamell)</span><span className="sk-preis">1,50</span></div>
            <div className="sk-zeile"><span>Sahne</span><span className="sk-preis">1,20</span></div>
            <div className="sk-zeile"><span>Extra Kugel</span><span className="sk-preis">1,80</span></div>
          </div>
          </div>)}

          {aktiv === 'cucina' && (<div>
            <div className="sk-detail-title">Cucina · Mercato</div>
            <div className="sk-section">
            <div className="sk-kategorie">Frühstück · 8–12 Uhr</div>
            {[
              ['Croissant','2,50'],['Gefülltes Croissant','4,50'],
              ['Süßes Frühstück – Croissant, Marmelade & Butter','5,90'],
              ['Savoca Special Benedict – Brioche, Hollandaise, Zwiebeln, Rucola, Speckcrumble','13,90'],
              ['Rote-Bete-Hummus Toast – smashed Avocado, Ei, Beilagensalat','12,90'],
              ['Mediterraner Toast/Cornetto – Brioche, Pesto, Rucola, Tomaten, Balsamicocreme, Mozzarella','8,90'],
              ['French Toast mit Apfel + Eis – Schmand-Apfelkompott, Vanilleeis, Ahornsirup','10,90'],
              ['Skyr Bowl – Skyr, Crunch-Müsli, Beerenkompott, Banane, Nussbutter, Chiasamen','8,90'],
              ['Süße Mais-Cachapa – Spiegelei & Mozzarella di Bufala (glutenfrei)','12,90'],
            ].map(([n,p]) => <div key={n} className="sk-zeile"><span>{n}</span><span className="sk-preis">{p}</span></div>)}
            <div className="sk-kategorie" style={{marginTop:'1.5rem'}}>Eier</div>
            {[
              ['Spiegeleier – 2 Bio-Eier','6,90'],
              ['Rührei – 3 Bio-Eier, Haussalat, 2 Brotscheiben & Butter','7,50'],
              ['Rührei griechischer Art – Babyspinat, Champignons, Fetakäse','12,90'],
            ].map(([n,p]) => <div key={n} className="sk-zeile"><span>{n}</span><span className="sk-preis">{p}</span></div>)}
            <div className="sk-kategorie" style={{marginTop:'1.5rem'}}>Extra</div>
            {[['Bacon','2,90'],['1 Ei','2,00'],['Käse','2,00'],['Gemüse','2,00'],['Tomate & Mozzarella','2,50'],['Marmelade / Konfitüre','2,50'],['Butter','1,50'],['Frisch gepresster Orangensaft','4,50']].map(([n,p]) => <div key={n} className="sk-zeile"><span>{n}</span><span className="sk-preis">{p}</span></div>)}
            <div className="sk-kategorie" style={{marginTop:'1.5rem'}}>Savocaccias · 8–13 Uhr</div>
            {[
              ['Pecora – Ziegenrolle, Feigensenf, Gemüse & Salat','9,50'],
              ['Siciliana – Aubergine, Zucchini, Paprika, Ricotta, rotes Pesto','8,50'],
              ['Toscana – Prosciutto crudo, Rucola, Provolone & Kräutercreme','9,00'],
              ['Vegano Verde – Hummus, Rucola, Tomaten, Gemüse & veganes Pesto','8,50'],
              ['Tuna Melt Italiana – Thunfisch, Kapern, Oliven, Zwiebeln, Provolone & Pesto','9,00'],
              ['Caprese Moderna – Mozzarella, Tomaten, Basilikumpesto & Rucola','8,50'],
              ['Panini Calabrese – Salame piccante, Provolone, Gemüse, Rucola & Nduja','9,00'],
              ['Panini alla Bolognese – Mortadella di Bologna, Pesto, Rucola, Gemüse & Provolone','9,00'],
            ].map(([n,p]) => <div key={n} className="sk-zeile"><span>{n}</span><span className="sk-preis">{p}</span></div>)}
            <div className="sk-kategorie" style={{marginTop:'1.5rem'}}>Käseplatte & Antipasti</div>
            <div className="sk-zeile"><span>Antipasti: Käse, Feigensenf & Brot · 1 / 2 / 3 Pers.</span><span className="sk-preis">14 / 25 / 35</span></div>
            <div className="sk-zeile"><span>Antipasti: Käse, Schinken & Brot · 1 / 2 / 3 Pers.</span><span className="sk-preis">14 / 25 / 35</span></div>
            <div className="sk-kategorie" style={{marginTop:'1.5rem'}}>Insalate</div>
            {[
              ['Insalata Caprese – Büffelmozzarella, wilde Tomaten, Basilikum','9,90'],
              ['Panzanella – Salat, Gurken, Tomaten, rote Zwiebeln, Croutons','7,90'],
              ['Insalata al formaggio di capra – Ziegenkäse, Walnüsse, Cherry-Tomaten','11,90'],
              ['Meeresfrüchtesalat – Meeresfrüchte mit knusprigem Brot','16,90'],
              ['Vitello Tonnato – ein Klassiker','12,90'],
            ].map(([n,p]) => <div key={n} className="sk-zeile"><span>{n}</span><span className="sk-preis">{p}</span></div>)}
            <div className="sk-kategorie" style={{marginTop:'1.5rem'}}>Pasta · Mercato</div>
            <p className="sk-hinweis">Auch als Vollkorn- oder glutenfreie Pasta erhältlich.</p>
            {[
              ['Pasta Amatriciana – Spaghetti, Tomatensauce, Guanciale, rote Zwiebeln','15,90'],
              ['Pasta con le Sarde – Rigatoni, Sardinen, Fenchelcreme, Rosinen, Pinienkerne','16,90'],
              ['Pasta alla Norma – Strozzapreti, Ricotta al forno, gebackene Aubergine','14,90'],
              ['Pasta al Ragù – Rigatoni, hausgemachtes Ragù von Oma','15,90'],
              ['Pasta Gamberi e Limone – Spaghetti, Garnelen, Amalfi Zitrone, Cherry-Tomaten','18,90'],
              ['Pasta Arrabbiata – Spaghetti, scharfe Tomatensauce, Knoblauch, Chili','12,90'],
              ['Ricotta e Limone – Penne, Ricotta, Zitrone, Parmigiano, Olivenöl','12,90'],
              ['Lasagne – klassische Bolognese, Béchamelsauce, Mozzarella überbacken','13,90'],
              ['Pasta Salsiccia – Penne, ital. Salsiccia, Tomatensauce','16,90'],
              ['Parmigiana – Auberginenauflauf, Tomatensauce, Mozzarella','11,90'],
              ['Bolognese – Spaghetti, klassische Rinderhackfleischsauce','11,90'],
              ['Frutti di Mare – Spaghetti, Meeresfrüchte, Tomatensauce, Knoblauch','16,90'],
              ['Salmone – Tagliolini, Lachs, Sahnesauce, Babyspinat','15,90'],
              ['Spaghetti Puttanesca – Oliven, Kapern, Thunfisch','14,90'],
            ].map(([n,p]) => <div key={n} className="sk-zeile"><span>{n}</span><span className="sk-preis">{p}</span></div>)}
            <div className="sk-kategorie" style={{marginTop:'1.5rem'}}>Pizza · Mercato</div>
            {[
              ['Marinara – Tomatensauce, Knoblauch, Oregano, Olivenöl, Basilikum','10,00'],
              ['Napoletana Special – Tomatensauce, Sardellen, Kapern, Artischocken, Oregano','13,90'],
              ['Margherita di Bufala – Tomatensauce, Mozzarella fior di latte, Basilikum','11,90'],
              ['Calabrese Piccante – Tomatensauce, scharfe Salami, Mozzarella, Basilikum','14,90'],
              ['Tonno e Cipolla – Tomatensauce, Thunfisch, Mozzarella, rote Zwiebeln','14,90'],
              ['Prosciutto e Funghi – Tomatensauce, Mozzarella, Schinken, Champignons','14,90'],
              ['Mortazza – Mozzarella fior di latte, Mortadella, Balsamico','16,90'],
              ['Cruda – Tomatensauce, fior di latte, Basilikum, Parmaschinken, Rucola','15,90'],
              ['Calzone – Tomaten, Ricotta, Mozzarella, Salami, Prosciutto','13,90'],
              ['Frutti di Mare – Tomatensauce, fior di latte, Meeresfrüchte','16,90'],
              ['Salsiccia e Friarielli – Salsiccia, Friarielli, Crema di friarielli con taralli','16,90'],
              ['Salami – Tomatensauce, fior di latte, milde Salami','14,90'],
              ['Bianca e Prosciutto – fior di latte, Panna, gekochter Schinken, Basilikum','14,90'],
              ['Spinaci – fior di latte, Ricotta, Babyspinat, Knoblauch, Cherrytomaten','15,90'],
              ['Salmone – fior di latte, Lachs, Sahne, Rucola','16,90'],
              ['Capricciosa – Tomatensauce, fior di latte, Schinken, Pilze, Artischocken','15,90'],
              ['Romana – Tomatensauce, fior di latte, schwarze Oliven, Sardellen, Kapern, Basilikum','15,90'],
              ['Siciliana – fior di latte, Auberginen, Basilikum','14,90'],
              ['Quattro Formaggi – fior di latte, Gorgonzola, Parmesan, Taleggio, Birne, Walnuss','16,90'],
              ['Ortolana – Kürbiscreme, fior di latte, Cherrytomaten, Gemüse des Tages, Basilikum','15,90'],
              ['Il Padrino – Tomatensauce, fior di latte, Schinken, Artischocken, Bio-Ei, Pistazie','16,90'],
            ].map(([n,p]) => <div key={n} className="sk-zeile"><span>{n}</span><span className="sk-preis">{p}</span></div>)}
            <p className="sk-hinweis" style={{marginTop:'0.8rem'}}>Alle vegetarischen Pizzen auch mit veganem Mozzarella erhältlich.</p>
            <div className="sk-kategorie" style={{marginTop:'1.5rem'}}>Dolce</div>
            <div className="sk-zeile"><span>2 Kugeln Savoca Eis – Auswahl an verschiedenen Eissorten, Sahne + 1,00, je extra Kugel + 1,80</span><span className="sk-preis">3,60</span></div>
            <div className="sk-zeile"><span>Tiramisù della casa – hausgemacht, Mascarpone, Löffelbiskuit, Espresso, Kakao</span><span className="sk-preis">7,90</span></div>
            <div className="sk-kategorie" style={{marginTop:'1.5rem'}}>Kuchen & Dolci</div>
            {[['Rührkuchen','3,20'],['Kuchen','4,80'],['Torte','4,80'],['Mandelgebäck','2,50'],['Nussecke','2,50'],['+ Sahne','1,20'],['+ Sauce (Erdbeer/Schoko)','1,50']].map(([n,p]) => <div key={n} className="sk-zeile"><span>{n}</span><span className="sk-preis">{p}</span></div>)}
          </div>
          </div>)}

          <p className="sk-footer-note" style={{marginTop:'2rem'}}>Alle Preise in Euro · Allergene an der Theke erfragbar</p>
        </div>
      </div>
      )}
    </div>
  );
}

// ===== APP =====
export default function App() {
  const [page, setPage] = useState('home');
  const [user, setUser] = useState(null);
  const [popup, setPopup] = useState(null);
  const [popupGeschlossen, setPopupGeschlossen] = useState(false);
  const [grischiOffen, setGrischiOffen] = useState(() => {
    try { return grischiAktiv() && !sessionStorage.getItem('savoca_grischi_gesehen'); } catch { return grischiAktiv(); }
  });
  const grischiSchliessen = () => { try { sessionStorage.setItem('savoca_grischi_gesehen', '1'); } catch {} setGrischiOffen(false); };
  const [reservierungTab, setReservierungTab] = useState('tisch');
  const [logoClicks, setLogoClicks] = useState(0);
  const [menueOffen, setMenueOffen] = useState(false);   // Handy-Menü (Hamburger) oben in der App
  // Selbst-Update: ein offener Tab (z. B. im Admin) bleibt sonst nach einem Deploy auf der alten Version hängen.
  // Fragt alle 2 Minuten und beim Zurückkehren auf den Tab die Startseite ab; steht dort ein anderes Programm-Paket, wird neu geladen –
  // aber nie, während jemand tippt (Eingabefeld aktiv), damit keine Eingabe verloren geht.
  useEffect(() => {
    let aktiv = true;
    const pruefe = async () => {
      try {
        const eigenes = document.querySelector('script[src*="/static/js/main."]')?.getAttribute('src');
        if (!eigenes || !aktiv) return;
        const res = await fetch('/', { cache: 'no-store' });
        if (!res.ok) return;
        const html = await res.text();
        if (html.includes(eigenes)) return;
        const el = document.activeElement;
        const tippt = el && ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName);
        if (!tippt) window.location.reload();
      } catch {}
    };
    const beiFokus = () => { if (document.visibilityState === 'visible') pruefe(); };
    document.addEventListener('visibilitychange', beiFokus);
    const timer = setInterval(pruefe, 120000);
    return () => { aktiv = false; document.removeEventListener('visibilitychange', beiFokus); clearInterval(timer); };
  }, []);
  // Herbstblätter (public/herbst/herbst.js): im Admin-Bereich abschalten, damit beim Arbeiten nichts stört
  useEffect(() => { if (window.savocaHerbst) (page === 'admin' ? window.savocaHerbst.aus() : window.savocaHerbst.an()); }, [page]);

  const handleLogoClick = () => {
    const next = logoClicks + 1;
    if (next >= 4) { setLogoClicks(0); setPage('admin'); }
    else setLogoClicks(next);
    setPage(p => p === 'admin' ? p : 'landing');
  };

  const handleReservieren = (tab = 'tisch') => {
    setReservierungTab(tab);
    setPage('reservierung');
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('token')) setPage('mitglied');
    const tab = params.get('tab');
    if (tab) { setReservierungTab(tab); setPage('reservierung'); }
    const pg = params.get('page');
    if (pg === 'impressum') setPage('impressum');
    if (pg === 'datenschutz') setPage('datenschutz');
    supabase.auth.getSession().then(({ data }) => setUser(data.session?.user ?? null));
    const { data: listener } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user ?? null);
      if (event === 'PASSWORD_RECOVERY') setPage('pw-reset');
    });
    return () => listener.subscription.unsubscribe();
  }, []); // eslint-disable-line

  useEffect(() => {
    let startY = 0;
    let pulling = false;
    let indicator = null;

    const onTouchStart = (e) => {
      if (window.scrollY === 0) {
        startY = e.touches[0].clientY;
        pulling = true;
      }
    };

    const onTouchMove = (e) => {
      if (!pulling) return;
      const diff = e.touches[0].clientY - startY;
      if (diff > 10) {
        if (!indicator) {
          indicator = document.createElement('div');
          indicator.id = 'ptr-indicator';
          indicator.style.cssText = 'position:fixed;top:0;left:0;right:0;z-index:9999;display:flex;align-items:center;justify-content:center;padding:0.6rem;background:rgba(184,150,62,0.15);backdrop-filter:blur(4px);font-size:0.85rem;color:#b8963e;font-family:Raleway,sans-serif;letter-spacing:1px;transition:opacity 0.2s;';
          indicator.textContent = '↓ Zum Aktualisieren ziehen…';
          document.body.prepend(indicator);
        }
        const progress = Math.min(diff / 80, 1);
        indicator.style.opacity = progress;
        if (diff > 80) indicator.textContent = '↑ Loslassen zum Aktualisieren';
        else indicator.textContent = '↓ Zum Aktualisieren ziehen…';
      }
    };

    const onTouchEnd = (e) => {
      if (!pulling) return;
      pulling = false;
      const diff = e.changedTouches[0].clientY - startY;
      if (indicator) { indicator.remove(); indicator = null; }
      if (diff > 80) window.location.reload();
    };

    document.addEventListener('touchstart', onTouchStart, { passive: true });
    document.addEventListener('touchmove', onTouchMove, { passive: true });
    document.addEventListener('touchend', onTouchEnd, { passive: true });
    return () => {
      document.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
    };
  }, []);

  useEffect(() => {
    // Popups mit „sichtbar bis" verschwinden danach von selbst (lokales Datum, nicht UTC)
    const jetzt = new Date(), heute = `${jetzt.getFullYear()}-${String(jetzt.getMonth() + 1).padStart(2, '0')}-${String(jetzt.getDate()).padStart(2, '0')}`;
    supabase.from('ankuendigungen').select('*').eq('aktiv', true).or(`bis.is.null,bis.gte.${heute}`).order('created_at', { ascending: false }).limit(1)
      .then(({ data }) => { if (data && data.length > 0) setPopup(data[0]); });
  }, []);

  const navItems = [
    { key: 'home', label: 'Home' },
    { key: 'reservierung', label: 'Reservieren' },
    { key: 'faq', label: 'FAQ' },
  ];

  return (
    <div className="app">
      <nav className="nav">
        <div className="nav-logo" onClick={handleLogoClick} style={{ cursor: 'pointer' }}>
          <img src="/savoca-logo.png" alt="Savoca Logo" className="nav-logo-img" />
        </div>
        <div className="nav-links">
          {navItems.map(n => (
            n.key === 'home'
              ? <button key={n.key} className={page === n.key ? 'active' : ''} onClick={() => window.location.href = 'https://savoca-caffe.de'}>{n.label}</button>
              : <button key={n.key} className={page === n.key ? 'active' : ''} onClick={() => setPage(n.key)}>{n.label}</button>
          ))}
        </div>
        {/* Nur am Handy: die normale Leiste ist dort ausgeblendet – ohne dieses Menü gäbe es keinen Weg zurück zur Hauptseite */}
        <div className="nav-mobil">
          <button className="nav-mobil-res" onClick={() => { setMenueOffen(false); handleReservieren(); }}>Reservierung</button>
          <button className="nav-mobil-burger" aria-label="Menü" aria-expanded={menueOffen} onClick={() => setMenueOffen(o => !o)}>
            <span></span><span></span><span></span>
          </button>
        </div>
      </nav>
      {menueOffen && (
        <div className="nav-mobil-menue" onClick={() => setMenueOffen(false)}>
          <div className="nav-mobil-liste" onClick={e => e.stopPropagation()}>
            <button onClick={() => { window.location.href = 'https://savoca-caffe.de'; }}>Home</button>
            <button onClick={() => { setMenueOffen(false); handleReservieren(); }}>Reservieren</button>
            <button onClick={() => { setMenueOffen(false); setPage('faq'); }}>FAQ</button>
            <button onClick={() => { setMenueOffen(false); setPage('mitglied'); }}>Mein Konto</button>
          </div>
        </div>
      )}

      {grischiOffen && <GrischiPopup onClose={grischiSchliessen} />}
      {popup && !popupGeschlossen && !grischiOffen && (
        <div className="popup-overlay" onClick={() => setPopupGeschlossen(true)}>
          <div className={`popup-box popup-${popup.farbe}`} onClick={e => e.stopPropagation()}>
            <div className="popup-titel">{popup.titel}</div>
            <div className="popup-text">{popup.text}</div>
            <button className="popup-close" onClick={() => setPopupGeschlossen(true)}>Schließen</button>
          </div>
        </div>
      )}

      {page === 'home' && <Home setPage={setPage} user={user} onReservieren={handleReservieren} />}
      {page === 'landing' && <LandingPage onReservieren={handleReservieren} />}
      {page === 'reservierung' && <Reservierung key={reservierungTab} startTab={reservierungTab} setPage={setPage} />}
      {page === 'speisekarte' && <Speisekarte />}
      {page === 'faq' && <FAQ />}
      {page === 'mitglied' && <Mitglied user={user} setPage={setPage} startTab="login" />}
      {page === 'mitglied-register' && <Mitglied user={user} setPage={setPage} startTab="register" />}
      {page === 'admin' && <Admin />}
      {page === 'impressum' && <Impressum />}
      {page === 'datenschutz' && <Datenschutz />}
      {page === 'pw-reset' && <PasswortReset setPage={setPage} />}

      <footer className="footer">
        <div className="footer-inner">
          <span>© {new Date().getFullYear()} Savoca Köln · Gottesweg 139, 50939 Köln</span>
          <div className="footer-links">
            <button onClick={() => setPage('impressum')}>Impressum</button>
            <button onClick={() => setPage('datenschutz')}>Datenschutz</button>
          </div>
        </div>
      </footer>

      {page !== 'home' && page !== 'pw-reset' && <KaffeeAssistent setPage={setPage} page={page} />}
    </div>
  );
}

// ===== IMPRESSUM =====
function Impressum() {
  return (
    <div className="page">
      <p className="page-subtitle">Rechtliches</p>
      <h2 className="page-title">Impressum</h2>
      <div className="divider" />
      <div className="legal-text">
        <h3>Angaben gemäß § 5 TMG</h3>
        <p>Savoca Manifattura GmbH<br />Gottesweg 139<br />50939 Köln</p>

        <h3>Vertreten durch</h3>
        <p>Alexander Hartmut Kautz<br />Grischa Sebastiano Savoca</p>

        <h3>Kontakt</h3>
        <p>Telefon: 0221 67811545<br />E-Mail: info@savoca-caffe.de</p>

        <h3>Registereintrag</h3>
        <p>Eingetragen im Handelsregister.<br />Registergericht: Amtsgericht Köln<br />Registernummer: HRB 103873</p>

        <h3>Umsatzsteuer-ID</h3>
        <p>Umsatzsteuer-Identifikationsnummer gemäß § 27a UStG: DE336462039</p>

        <h3>Verantwortlich für den Inhalt nach § 55 Abs. 2 RStV</h3>
        <p>Savoca Manifattura GmbH<br />Gottesweg 139<br />50939 Köln</p>

        <h3>Streitschlichtung</h3>
        <p>Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit: https://ec.europa.eu/consumers/odr/. Wir sind nicht bereit oder verpflichtet, an Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen.</p>

        <h3>Hinweis zu Bildern</h3>
        <p>Die herabfallenden Herbstblätter im Hintergrund unserer Seiten sind mit KI erstellte Bilder.</p>

        <h3>Haftungsausschluss</h3>
        <p>Die Inhalte unserer Seiten wurden mit größter Sorgfalt erstellt. Für die Richtigkeit, Vollständigkeit und Aktualität der Inhalte können wir jedoch keine Gewähr übernehmen.</p>
      </div>
    </div>
  );
}

// ===== DATENSCHUTZ =====
function Datenschutz() {
  return (
    <div className="page">
      <p className="page-subtitle">Rechtliches</p>
      <h2 className="page-title">Datenschutzerklärung</h2>
      <div className="divider" />
      <div className="legal-text">
        <h3>1. Datenschutz auf einen Blick</h3>
        <p>Die folgenden Hinweise geben einen einfachen Überblick darüber, was mit Ihren personenbezogenen Daten passiert, wenn Sie unsere Website besuchen.</p>

        <h3>2. Verantwortliche Stelle</h3>
        <p>Savoca Manifattura GmbH<br />Gottesweg 139<br />50939 Köln<br />E-Mail: info@savoca-caffe.de</p>

        <h3>3. Welche Daten wir erheben</h3>
        <p>Bei der Registrierung als Mitglied erheben wir: Name, E-Mail-Adresse, Telefonnummer und Geburtsdatum. Diese Daten werden ausschließlich zur Verwaltung Ihrer Mitgliedschaft verwendet.</p>
        <p>Bei Tischreservierungen erheben wir: Name, E-Mail-Adresse, Telefonnummer, Datum, Uhrzeit, Personenzahl und Tischnummer.</p>
        <p>Bei Anfragen (Kindergeburtstag, Catering, Events) erheben wir die im jeweiligen Formular angegebenen Daten zur Bearbeitung Ihrer Anfrage.</p>

        <h3>4. Speicherung & Löschung</h3>
        <p>Ihre Daten werden in einer gesicherten Datenbank (Supabase) gespeichert. Sie können jederzeit die Löschung Ihrer Daten beantragen: info@savoca-caffe.de</p>

        <h3>5. Ihre Rechte</h3>
        <p>Sie haben jederzeit das Recht auf Auskunft, Berichtigung, Löschung und Einschränkung der Verarbeitung Ihrer gespeicherten Daten sowie ein Widerspruchsrecht gegen die Verarbeitung.</p>

        <h3>6. Hosting</h3>
        <p>Diese Website wird auf einem Server in Deutschland gehostet. Anbieter: Hetzner Online GmbH, Industriestr. 25, 91710 Gunzenhausen.</p>

        <h3>7. Cookies</h3>
        <p>Wir verwenden ausschließlich technisch notwendige Cookies für die Authentifizierung (Login-Session). Es werden keine Tracking- oder Marketing-Cookies eingesetzt.</p>
      </div>
    </div>
  );
}
